import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, ScatterChart, Scatter, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, Users, GraduationCap, MapPin, Calendar, RefreshCw, Award, Clock, Download, LogOut, Settings, GripVertical, Eye, EyeOff } from 'lucide-react';
import { ButterflyLoader } from './ButterflyLoader';
import { jsPDF } from 'jspdf';
import { checkDomainAllowed, upsertUser, logActivity, getUserPreferences, saveUserPreferences, checkIsAdmin, getUserDashboardAccess } from './supabaseClient';
import AdminPanel from './AdminPanel';
import HiringForecast from './HiringForecast';
import MarketingMetrics from './MarketingMetrics';
import SFMarketingAttribution from './SFMarketingAttribution';
import SFQuery from './SFQuery';
import EmailGenerator from './EmailGenerator';
import PipelineHealth from './PipelineHealth';
import Partnerships from './Partnerships';
import TestEntryAudit from './TestEntryAudit';
import './Dashboard.css';

// Every dashboard a super-admin can see. Kept in one place deliberately: this
// list used to be written out three times — on login, on session restore, and
// into localStorage — and adding a tab to only some of them meant the tab
// silently never appeared.
const SUPER_ADMIN_DASHBOARDS = ['funnel', 'hiring', 'marketing', 'duration', 'partnerships', 'email', 'sfquery', 'testaudit'];

// Mini sparkline component for MTD trends
const Sparkline = ({ data, width = 120, height = 28, color = '#4ADE80' }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const padding = 2;
  const w = width - padding * 2;
  const h = height - padding * 2;
  
  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * w;
    const y = padding + h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  
  // Gradient fill under the line
  const fillPoints = `${padding},${padding + h} ${points} ${padding + w},${padding + h}`;
  
  // Determine trend color: compare last 3 days avg vs first 3 days avg
  const recentAvg = data.slice(-3).reduce((s, v) => s + v, 0) / Math.min(3, data.length);
  const earlyAvg = data.slice(0, 3).reduce((s, v) => s + v, 0) / Math.min(3, data.length);
  const trendUp = recentAvg > earlyAvg;
  const lineColor = color;
  
  return (
    <svg width={width} height={height} style={{ display: 'block', margin: '0.25rem auto 0' }}>
      <defs>
        <linearGradient id={`spark-fill-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.25" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={fillPoints} fill={`url(#spark-fill-${color.replace('#','')})`} />
      <polyline points={points} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* End dot */}
      {data.length > 0 && (() => {
        const lastX = padding + ((data.length - 1) / (data.length - 1)) * w;
        const lastY = padding + h - ((data[data.length - 1] - min) / range) * h;
        return <circle cx={lastX} cy={lastY} r="2" fill={lineColor} />;
      })()}
    </svg>
  );
};

// Overlay Field Experience hire cohorts (from the fe-hires endpoint — point-in-time on
// Date_Entered__c, per certification state) onto a computed metrics object. Falls back to
// the metrics object's existing (CSV-derived) values when FE data isn't loaded yet.
const applyFeHires = (m, state, fe) => {
  if (!m || !fe) return m;
  const slice = state === 'All States' ? (fe.totals || {}) : (fe.states?.[state] || {});
  const g = (cur, prev) => (prev > 0 ? parseFloat((((cur - prev) / prev) * 100).toFixed(1)) : 0);
  m.fall2026Hires = slice.fall2026 || 0;
  m.fall2025Hires = slice.fall2025 || 0;
  m.fallHiresGrowth = g(m.fall2026Hires, m.fall2025Hires);
  m.spring2026Hires = slice.spring2026 || 0;
  m.spring2025Hires = slice.spring2025 || 0;
  m.springHiresGrowth = g(m.spring2026Hires, m.spring2025Hires);
  m.currentlyPlacedTeachers = m.spring2026Hires + m.fall2026Hires;
  m.priorYearPlacedTeachers = m.spring2025Hires + m.fall2025Hires;
  m.placedTeachersGrowth = g(m.currentlyPlacedTeachers, m.priorYearPlacedTeachers);
  m.currentHired = m.currentlyPlacedTeachers;
  return m;
};

// Fold the Louisiana Add-On track (from the la-addon endpoint) into a computed metrics
// object. Most Add-On candidates are net-new contacts and so already ride in on the main
// CSV feed; the ones who are returning teachers have no new Contact.Applied__c date and
// are missing from it entirely. Add only the remainder the feed doesn't already carry, so
// the enrolled totals pick up the whole Add-On population without double-counting.
const applyAddOns = (m, state, addOn, rawData) => {
  if (!m || !addOn?.candidates) return m;
  if (state !== 'All States' && state !== addOn.state) return m;

  // The feed formats dates as M/D/YYYY; the Add-On feed uses ISO days.
  const isoDay = (mdy) => {
    const [mo, d, y] = (mdy || '').split('/');
    return y ? `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}` : null;
  };

  // An Add-On candidate is already represented if a feed row for their email carries the
  // same enrolled date — matching on the date, not just the email, so a candidate who is
  // in the feed for some earlier application still gets their Add-On enrollment counted.
  const feedEnrolledDays = new Set();
  const feedCurrentlyEnrolled = new Set();
  for (const r of rawData) {
    if (r['Certification state'] !== addOn.state || !r.Email) continue;
    const email = r.Email.trim().toLowerCase();
    if (r.Enrolled) feedEnrolledDays.add(`${email}|${isoDay(r.Enrolled)}`);
    if ((r['Admission Status'] || '').trim() === 'Enrolled') feedCurrentlyEnrolled.add(email);
  }
  const countedEnrolled = (c) => c.email && feedEnrolledDays.has(`${c.email}|${c.enrolled}`);
  const uncounted = addOn.candidates.filter(c => c.enrolled && !countedEnrolled(c));

  const now = new Date();
  const monthKey = now.toISOString().slice(0, 7);
  const yearKey = String(now.getFullYear());
  const addedEnrolled = uncounted.length;
  const addedCurrentEnrolled = uncounted.filter(
    c => c.status === 'enrolled' && (!c.email || !feedCurrentlyEnrolled.has(c.email))
  ).length;
  const addedMTD = uncounted.filter(c => c.enrolled.slice(0, 7) === monthKey).length;
  const addedYTD = uncounted.filter(c => c.enrolled.slice(0, 4) === yearKey).length;

  m.enrolled += addedEnrolled;
  m.currentEnrolled += addedCurrentEnrolled;
  m.enrollmentsMTD += addedMTD;
  m.enrollmentsYTD += addedYTD;

  const g = (cur, prev) => (prev > 0 ? parseFloat((((cur - prev) / prev) * 100).toFixed(1)) : 0);
  m.enrollmentsYoYGrowth = g(m.enrollmentsMTD, m.enrollmentsMTDLastYear);
  m.enrollmentsYTDGrowth = g(m.enrollmentsYTD, m.enrollmentsYTDLastYear);
  m.forecastEnrollmentsVariance = g(m.enrollmentsMTD, m.forecastEnrollmentsMTD);
  m.forecastEnrollmentsYTDVariance = g(m.enrollmentsYTD, m.forecastEnrollmentsYTD);

  m.addOn = { ...addOn, addedEnrolled, addedCurrentEnrolled, addedMTD, addedYTD };
  return m;
};

const STATE_ABBREV = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC'
};

const getStateAbbrev = (name) => STATE_ABBREV[name] || (name.length <= 2 ? name : name.substring(0, 2).toUpperCase());

// Card registry — defines all customizable dashboard sections
const CARD_REGISTRY = [
  { id: 'ai-summary', label: 'AI Executive Summary', icon: '🤖' },
  { id: 'kpi-cards', label: 'KPI Performance Cards', icon: '📊' },
  { id: 'la-addon', label: 'Louisiana Add-On Program', icon: '➕' },
  { id: 'highlights', label: 'Pacing & Highlights', icon: '⚡' },
  { id: 'eval-health', label: 'Evaluation Health', icon: '🩺' },
  { id: 'forecast-readiness', label: 'Forecast Readiness', icon: '🎯' },
  { id: 'app-age', label: 'Applications Age Distribution', icon: '📋' },
  { id: 'cohort', label: 'Cohort Conversion Analysis', icon: '🔄' },
  { id: 'app-trend', label: 'Application Trend (3-Year)', icon: '📈' },
  { id: 'enroll-trend', label: 'Enrollment Trend (3-Year)', icon: '📈' },
  { id: 'fall-fe-state', label: 'Fall FE by State (2026 vs 2025)', icon: '🍂' },
  { id: 'state-matrix', label: 'State Performance Matrix', icon: '🗺️' },
  { id: 'state-mom', label: 'State Enrollment Trends (MoM)', icon: '🏆' },
  { id: 'hired-comparison', label: 'Hired Candidate Comparison', icon: '👩‍🏫' },
  { id: 'pipeline-health', label: 'Pipeline Health', icon: '🏥' },
];

const DEFAULT_CARD_CONFIG = CARD_REGISTRY.map((card, idx) => ({
  id: card.id,
  visible: true,
  order: idx,
}));

const EnrollmentDashboard = () => {
  const [rawData, setRawData] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [feHires, setFeHires] = useState(null);
  const [addOnData, setAddOnData] = useState(null);
  const [evaluationData, setEvaluationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [selectedState, setSelectedState] = useState('All States');
  const [accessCode, setAccessCode] = useState('');
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessError, setAccessError] = useState(false);
  const [authUser, setAuthUser] = useState(null); // { email, name, picture }
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [cardConfig, setCardConfig] = useState(DEFAULT_CARD_CONFIG);
  const [showSettings, setShowSettings] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dashboardAccess, setDashboardAccess] = useState(['funnel']);
  const [showAdmin, setShowAdmin] = useState(false);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(true);
  const [matrixHover, setMatrixHover] = useState(null); // { data, x, y } for state matrix tooltip
  const [activeTab, setActiveTab] = useState('funnel'); // 'funnel' or 'hiring'
  const [durationData, setDurationData] = useState(null);
  const [durationLoading, setDurationLoading] = useState(false);
  const [durationError, setDurationError] = useState(null);
  const [durationTimeRange, setDurationTimeRange] = useState('all');

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Track activity helper
  const trackActivity = useCallback((action, details = {}) => {
    const email = authUser?.email;
    if (email) {
      logActivity(email, action, details);
    }
  }, [authUser]);

  // Google Sign-In callback
  const handleGoogleCallback = useCallback(async (response) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      // Decode the JWT credential to get user info
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const { email, name, picture } = payload;

      // Check if domain is allowed
      const allowed = await checkDomainAllowed(email);
      if (!allowed) {
        setAuthError(`Access denied. The domain "${email.split('@')[1]}" is not authorized.`);
        setAuthLoading(false);
        return;
      }

      // Upsert user record
      await upsertUser(email, name, picture);

      // Set auth state
      const user = { email, name, picture };
      setAuthUser(user);
      setAccessGranted(true);
      setAuthError('');

      // Check admin status and dashboard access
      const adminStatus = await checkIsAdmin(email);
      setIsAdmin(adminStatus);
      const isSuperAdmin = email === 'andrew.rozell@iteach.net';
      const access = await getUserDashboardAccess(email);
      setDashboardAccess(isSuperAdmin ? SUPER_ADMIN_DASHBOARDS : access);

      // Persist session
      localStorage.setItem('iteach_auth', JSON.stringify({ ...user, isAdmin: adminStatus, isSuperAdmin, dashboardAccess: isSuperAdmin ? SUPER_ADMIN_DASHBOARDS : access }));

      // Log login
      logActivity(email, 'login', { method: 'google' });
    } catch (err) {
      console.error('Google auth error:', err);
      setAuthError('Authentication failed. Please try again.');
    }
    setAuthLoading(false);
  }, []);

  // Initialize Google Sign-In
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    // Check for existing session
    const stored = localStorage.getItem('iteach_auth');
    if (stored) {
      try {
        const user = JSON.parse(stored);
        if (user.email) {
          setAuthUser(user);
          setAccessGranted(true);
          if (user.isAdmin) setIsAdmin(true);
          if (user.isSuperAdmin) {
            setDashboardAccess(SUPER_ADMIN_DASHBOARDS);
          } else if (user.dashboardAccess) {
            setDashboardAccess(user.dashboardAccess);
          }
        }
      } catch (e) {
        localStorage.removeItem('iteach_auth');
      }
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        });
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existing) existing.remove();
    };
  }, [GOOGLE_CLIENT_ID, handleGoogleCallback]);

  // Render Google button when available
  const renderGoogleButton = useCallback((elementId) => {
    if (window.google && GOOGLE_CLIENT_ID) {
      setTimeout(() => {
        const container = document.getElementById(elementId);
        if (container && container.childElementCount === 0) {
          window.google.accounts.id.renderButton(container, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'signin_with',
            width: 280,
          });
        }
      }, 100);
    }
  }, [GOOGLE_CLIENT_ID]);

  // Handle logout
  const handleLogout = () => {
    trackActivity('logout');
    setAuthUser(null);
    setAccessGranted(false);
    setAccessCode('');
    localStorage.removeItem('iteach_auth');
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  // Load user preferences when authenticated
  useEffect(() => {
    const loadPrefs = async () => {
      if (!authUser?.email || authUser.email === 'guest@accesscode') return;
      const prefs = await getUserPreferences(authUser.email);
      if (prefs?.card_config && Array.isArray(prefs.card_config) && prefs.card_config.length > 0) {
        // Merge with registry in case new cards were added. Slot each new card in after
        // the registry neighbour that precedes it, so it lands where it was designed to
        // sit rather than at the bottom of everyone's saved layout.
        const merged = [...prefs.card_config].sort((a, b) => a.order - b.order);
        CARD_REGISTRY.forEach((card, idx) => {
          if (merged.some(c => c.id === card.id)) return;
          const prevId = CARD_REGISTRY.slice(0, idx).reverse().find(r => merged.some(c => c.id === r.id))?.id;
          const at = prevId ? merged.findIndex(c => c.id === prevId) + 1 : merged.length;
          merged.splice(at, 0, { id: card.id, visible: true, order: at });
        });
        setCardConfig(merged.map((c, i) => ({ ...c, order: i })));
      }
      if (prefs?.default_state) {
        setSelectedState(prefs.default_state);
      }
    };
    loadPrefs();
  }, [authUser?.email]);

  // Save preferences when card config changes (debounced)
  useEffect(() => {
    if (!authUser?.email || authUser.email === 'guest@accesscode') return;
    const timer = setTimeout(() => {
      saveUserPreferences(authUser.email, cardConfig, selectedState);
    }, 1000);
    return () => clearTimeout(timer);
  }, [cardConfig, authUser?.email]);

  // Card config helpers
  const isCardVisible = (cardId) => {
    const card = cardConfig.find(c => c.id === cardId);
    return card ? card.visible : true;
  };

  const toggleCard = (cardId) => {
    setCardConfig(prev => prev.map(c => 
      c.id === cardId ? { ...c, visible: !c.visible } : c
    ));
    trackActivity('card_toggle', { card: cardId });
  };

  const handleDragStart = (idx) => {
    setDragItem(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragItem === null || dragItem === idx) return;
    setCardConfig(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(dragItem, 1);
      updated.splice(idx, 0, moved);
      return updated.map((c, i) => ({ ...c, order: i }));
    });
    setDragItem(idx);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    trackActivity('card_reorder');
  };

  const resetCardConfig = () => {
    setCardConfig(DEFAULT_CARD_CONFIG);
    trackActivity('card_reset');
  };

  // Get ordered visible cards for rendering
  const getOrderedCards = () => {
    return [...cardConfig]
      .sort((a, b) => a.order - b.order)
      .filter(c => CARD_REGISTRY.find(r => r.id === c.id));
  };

 const SALESFORCE_URL = '/.netlify/functions/salesforce-data';
 const FE_HIRES_URL = '/.netlify/functions/fe-hires';
 const LA_ADDON_URL = '/.netlify/functions/la-addon';
 const SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTX4HLDANd_0wzMqs3FN_Xs1IfPWtqmhd2ID7fWMubK8sE22cQaNlDKW73O-q9aB4rg41toPCKYjemg/pub?gid=0&single=true&output=csv';
  const FORECAST_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTvDX8QKsNlVvn9LhXdaUqiUeEuuXYe1-DA_BsJS-d2wHYnC0UpC1EqEDpeOX6-Ai_r62Nir9YS3Jnb/pub?gid=0&single=true&output=csv';
  const EVALUATION_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTuv04q6yNn603Zr-S5LNClenX14sO8XKVyRHOCAor9idaZUlRl_UdRoIT8pFoi5K3X5ZcrpD396h5D/pub?gid=0&single=true&output=csv';

  const parseCSV = (csv) => {
    const lines = csv.trim().split('\n');
    
    // Parse a single CSV line respecting quoted fields
    const parseLine = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++; // skip escaped quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    };
    
    const headers = parseLine(lines[0]).map(h => h.replace(/^["']|["']$/g, ''));
    
    return lines.slice(1).map(line => {
      const values = parseLine(line);
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      return obj;
    });
  };

  const calculateMetrics = (rawData, stateFilter = 'All States', forecastData = []) => {
    const now = new Date(); // Declare now at the top so all sections can use it
    
    // Filter data by selected state
    const filteredData = stateFilter === 'All States' 
      ? rawData 
      : rawData.filter(r => r['Certification state'] === stateFilter);
    
    const totalApplications = filteredData.length;
    const enrolled = filteredData.filter(r => r.Enrolled).length;
    const dateEntered = filteredData.filter(r => r['Date Entered']).length;
    const dropped = filteredData.filter(r => r['Dropped Date']).length;

    // Current funnel snapshot — count of candidates CURRENTLY in each admission status
    // (mutually exclusive, respects state filter). For New Jersey, "Pre Enrolled" is
    // folded into Enrolled per program rules.
    const admStatus = (r) => (r['Admission Status'] || '').trim();
    const isNJ = (r) => r['Certification state'] === 'New Jersey';
    const isPreEnrolled = (s) => s.toLowerCase().replace(/[-\s]+/g, ' ') === 'pre enrolled';
    const currentApps = filteredData.filter(r => admStatus(r) === 'Applied').length;
    const currentAccepted = filteredData.filter(r => admStatus(r) === 'Accepted').length;
    const currentEnrolled = filteredData.filter(r => {
      const s = admStatus(r);
      return s === 'Enrolled' || (isNJ(r) && isPreEnrolled(s));
    }).length;
    
    // Enrollment Rate - LAST 6 MONTHS ONLY
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const last6MonthsApps = filteredData.filter(r => r.Applied && new Date(r.Applied) >= sixMonthsAgo);
    const last6MonthsEnrolled = last6MonthsApps.filter(r => r.Enrolled).length;
    const enrollmentRate = last6MonthsApps.length > 0 ? ((last6MonthsEnrolled / last6MonthsApps.length) * 100).toFixed(1) : '0.0';
    
    // Completion Rate - LAST 2 YEARS ONLY
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    const last2YearsEnrolled = filteredData.filter(r => r.Enrolled && new Date(r.Enrolled) >= twoYearsAgo);
    const last2YearsCompleted = last2YearsEnrolled.filter(r => r['Date Entered']).length;
    const completionRate = last2YearsEnrolled.length > 0 ? ((last2YearsCompleted / last2YearsEnrolled.length) * 100).toFixed(1) : '0.0';
    
    const retentionRate = (((dateEntered - dropped) / dateEntered) * 100).toFixed(1);

    const stateBreakdown = filteredData.reduce((acc, row) => {
      const state = row['Certification state'] || 'Unknown';
      acc[state] = (acc[state] || 0) + 1;
      return acc;
    }, {});

    const semesterBreakdown = filteredData.reduce((acc, row) => {
      const semester = row['FE Semester'] || 'Not Started';
      if (semester) {
        acc[semester] = (acc[semester] || 0) + 1;
      }
      return acc;
    }, {});

    const enrollmentTimes = rawData
      .filter(r => r.Applied && r.Enrolled)
      .map(r => {
        const applied = new Date(r.Applied);
        const enrolled = new Date(r.Enrolled);
        return Math.floor((enrolled - applied) / (1000 * 60 * 60 * 24));
      });
    
    const avgEnrollmentTime = enrollmentTimes.length > 0
      ? Math.round(enrollmentTimes.reduce((a, b) => a + b, 0) / enrollmentTimes.length)
      : 0;

    // Most Popular Application Time (last 6 months) - reuses sixMonthsAgo from line 51
    
    // Try different possible column names for the time field
    const timeColumnNames = ['Created Time', 'Application Time', 'Time Applied', 'Applied Time', 'Time', 'Application Timestamp'];
    const timeColumn = timeColumnNames.find(col => filteredData.some(r => r[col]));
    
    const timeOfDayBreakdown = { 'Morning (6am-12pm)': 0, 'Afternoon (12pm-6pm)': 0, 'Evening (6pm-12am)': 0, 'Night (12am-6am)': 0 };
    
    if (timeColumn) {
      console.log(`Found time column: ${timeColumn}`);
      
      const recentApps = filteredData
        .filter(r => {
          if (!r.Applied || !r[timeColumn]) return false;
          const appliedDate = new Date(r.Applied);
          return appliedDate >= sixMonthsAgo;
        });
      
      console.log(`Processing ${recentApps.length} applications from last 6 months`);
      
      recentApps.forEach((r, index) => {
          const timeStr = r[timeColumn];
          
          // Log first few entries to see format
          if (index < 3) {
            console.log(`Sample time data: "${timeStr}"`);
          }
          
          // Parse time - handle formats like "14:30", "2:30 PM", "14:30:00"
          let hour = 0;
          
          if (timeStr && timeStr.includes(':')) {
            const parts = timeStr.split(':');
            hour = parseInt(parts[0]);
            
            // Handle 12-hour format with AM/PM
            if (timeStr.toLowerCase().includes('pm') && hour !== 12) {
              hour += 12;
            } else if (timeStr.toLowerCase().includes('am') && hour === 12) {
              hour = 0;
            }
          }
          
          // Categorize by time of day
          if (hour >= 6 && hour < 12) {
            timeOfDayBreakdown['Morning (6am-12pm)']++;
          } else if (hour >= 12 && hour < 18) {
            timeOfDayBreakdown['Afternoon (12pm-6pm)']++;
          } else if (hour >= 18 && hour < 24) {
            timeOfDayBreakdown['Evening (6pm-12am)']++;
          } else {
            timeOfDayBreakdown['Night (12am-6am)']++;
          }
        });
      
      console.log('Time breakdown:', timeOfDayBreakdown);
    } else {
      console.log('No time column found in data');
    }
    
    // Find the most popular time slot
    const mostPopularTime = Object.entries(timeOfDayBreakdown)
      .sort((a, b) => b[1] - a[1])[0];
    
    const popularTimeSlot = mostPopularTime ? mostPopularTime[0] : 'N/A';
    const popularTimeCount = mostPopularTime ? mostPopularTime[1] : 0;

    const semesterTrends = Object.entries(semesterBreakdown)
      .filter(([sem]) => sem !== 'Not Started')
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([semester, count]) => ({
        semester: semester.replace(' (Transitional)', '*'),
        students: count,
        enrolled: filteredData.filter(r => r['FE Semester'] === semester && r.Enrolled).length
      }));

    // Semester Year-over-Year Growth (Fall to Fall, Spring to Spring)
    const semesterYoY = {};
    filteredData.forEach(r => {
      const semester = r['FE Semester'];
      if (!semester || semester === 'Not Started') return;
      
      // Extract term and year (e.g., "Fall 2024" -> "Fall", "2024")
      const match = semester.match(/(Fall|Spring)\s*(\d{4})/i);
      if (match) {
        const term = match[1];
        const year = parseInt(match[2]);
        
        // Only include Fall and Spring from 2018 onwards
        if (year < 2018) return;
        
        const key = term;
        
        if (!semesterYoY[key]) semesterYoY[key] = {};
        if (!semesterYoY[key][year]) semesterYoY[key][year] = 0;
        
        if (r.Enrolled) {
          semesterYoY[key][year]++;
        }
      }
    });
    
    // Convert to chart format: Spring 2018, Fall 2018, Spring 2019, Fall 2019...
    const semesterComparison = [];
    const startYear = 2018;
    const endYear = now.getFullYear();
    
    for (let year = startYear; year <= endYear; year++) {
      // Spring first, then Fall for each year
      if (semesterYoY['Spring'] && semesterYoY['Spring'][year]) {
        semesterComparison.push({
          term: `Spring ${year}`,
          enrolled: semesterYoY['Spring'][year],
          termOnly: 'Spring'
        });
      }
      
      if (semesterYoY['Fall'] && semesterYoY['Fall'][year]) {
        semesterComparison.push({
          term: `Fall ${year}`,
          enrolled: semesterYoY['Fall'][year],
          termOnly: 'Fall'
        });
      }
    }

    const monthlyApps = filteredData.reduce((acc, row) => {
      if (row.Applied) {
        const date = new Date(row.Applied);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        acc[monthYear] = (acc[monthYear] || 0) + 1;
      }
      return acc;
    }, {});

    // Get all 12 calendar months with 3-year comparison
    const currentYearForTrend = now.getFullYear();
    const year1Label = (currentYearForTrend - 2).toString();
    const year2Label = (currentYearForTrend - 1).toString();
    const year3Label = currentYearForTrend.toString();
    
    // Build chart for all 12 calendar months (Jan-Dec)
    const monthlyTrend = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const currentMonthIndex = now.getMonth(); // 0-based (0 = Jan)
    
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthName = monthNames[monthIndex];
      
      // Get applications for this month in each of the 3 years
      const year1Key = `${currentYearForTrend - 2}-${String(monthIndex + 1).padStart(2, '0')}`;
      const year2Key = `${currentYearForTrend - 1}-${String(monthIndex + 1).padStart(2, '0')}`;
      const year3Key = `${currentYearForTrend}-${String(monthIndex + 1).padStart(2, '0')}`;
      
      // For the current year, set future months to null so the line stops
      const isFutureMonth = monthIndex > currentMonthIndex;
      
      const dataPoint = {
        month: monthName,
        [year1Label]: monthlyApps[year1Key] || 0,
        [year2Label]: monthlyApps[year2Key] || 0,
        [year3Label]: isFutureMonth ? null : (monthlyApps[year3Key] || 0)
      };
      
      monthlyTrend.push(dataPoint);
    }
    

    // Build enrollment trend for all 12 calendar months (Jan-Dec)
    const monthlyEnrollments = filteredData.reduce((acc, row) => {
      if (row.Enrolled) {
        const date = new Date(row.Enrolled);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        acc[monthYear] = (acc[monthYear] || 0) + 1;
      }
      return acc;
    }, {});

    const monthlyEnrollmentTrend = [];
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthName = monthNames[monthIndex];
      const year1Key = `${currentYearForTrend - 2}-${String(monthIndex + 1).padStart(2, '0')}`;
      const year2Key = `${currentYearForTrend - 1}-${String(monthIndex + 1).padStart(2, '0')}`;
      const year3Key = `${currentYearForTrend}-${String(monthIndex + 1).padStart(2, '0')}`;
      const isFutureMonth = monthIndex > currentMonthIndex;
      
      monthlyEnrollmentTrend.push({
        month: monthName,
        [year1Label]: monthlyEnrollments[year1Key] || 0,
        [year2Label]: monthlyEnrollments[year2Key] || 0,
        [year3Label]: isFutureMonth ? null : (monthlyEnrollments[year3Key] || 0)
      });
    }

    // Build monthly forecast from forecast sheet for 2026 overlay
    const monthlyForecastApps = {};
    const monthlyForecastEnroll = {};
    
    if (forecastData && forecastData.length > 0) {
      const headerKeys = Object.keys(forecastData[0]);
      
      // Map date columns by month for current year
      const dateColsByMonth = {};
      headerKeys.forEach(colKey => {
        if (typeof colKey === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(colKey.trim())) {
          const parts = colKey.trim().split('/');
          const month = parseInt(parts[0]);
          const year = parseInt(parts[2]);
          if (year === currentYearForTrend) {
            if (!dateColsByMonth[month]) dateColsByMonth[month] = [];
            dateColsByMonth[month].push(colKey);
          }
        }
      });
      
      let fCategory = null;
      for (let i = 0; i < forecastData.length; i++) {
        const row = forecastData[i];
        const categoryCol = row['Category'] || '';
        const stateCol = row['State'] || '';
        
        if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('application')) {
          fCategory = 'Applications';
        } else if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('enrollment')) {
          fCategory = 'Enrollments';
        } else if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('hire')) {
          fCategory = 'Hires';
        }
        
        const stateName = typeof stateCol === 'string' ? stateCol.trim() : '';
        const stateNameLower = stateName.toLowerCase();
        if (!stateName || stateName === 'State' || 
            stateNameLower === 'total' || stateNameLower === 'totals' ||
            stateNameLower.includes('grand total') || stateNameLower.includes('sum') ||
            stateNameLower === 'all' || stateNameLower === 'all states' ||
            stateNameLower.includes('application') || stateNameLower.includes('enrollment') ||
            stateNameLower.includes('hire') || stateNameLower === 'category') {
          continue;
        }
        
        const includeState = stateFilter === 'All States' || 
                             stateName === stateFilter ||
                             stateName.toLowerCase() === stateFilter.toLowerCase();
        if (!includeState) continue;
        
        if (fCategory === 'Applications' || fCategory === 'Enrollments') {
          const target = fCategory === 'Applications' ? monthlyForecastApps : monthlyForecastEnroll;
          Object.entries(dateColsByMonth).forEach(([month, cols]) => {
            const monthNum = parseInt(month);
            cols.forEach(colKey => {
              const val = parseFloat(row[colKey]) || 0;
              target[monthNum] = (target[monthNum] || 0) + val;
            });
          });
        }
      }
    }
    
    // Add forecast line to both trends (bridges from current month actual into future forecast)
    const forecastLabel = `${year3Label} Forecast`;
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthNum = monthIndex + 1;
      const isFutureMonth = monthIndex > currentMonthIndex;
      const isCurrentMonth = monthIndex === currentMonthIndex;
      
      if (isCurrentMonth) {
        // Bridge point: forecast starts at current month for line continuity
        monthlyTrend[monthIndex][forecastLabel] = Math.round(monthlyForecastApps[monthNum] || 0);
        monthlyEnrollmentTrend[monthIndex][forecastLabel] = Math.round(monthlyForecastEnroll[monthNum] || 0);
      } else if (isFutureMonth) {
        monthlyTrend[monthIndex][forecastLabel] = Math.round(monthlyForecastApps[monthNum] || 0);
        monthlyEnrollmentTrend[monthIndex][forecastLabel] = Math.round(monthlyForecastEnroll[monthNum] || 0);
      } else {
        monthlyTrend[monthIndex][forecastLabel] = null;
        monthlyEnrollmentTrend[monthIndex][forecastLabel] = null;
      }
    }
    
    const monthlyTrendMeta = { year1Label, year2Label, year3Label, forecastLabel };

    const currentSemester = semesterTrends[semesterTrends.length - 1];
    const allSemesterCounts = semesterTrends.map(s => s.students).sort((a, b) => b - a);
    const currentRank = currentSemester 
      ? allSemesterCounts.indexOf(currentSemester.students) + 1
      : null;

    // MTD Calculations (comparing full days only - up to yesterday)
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    const currentYear = yesterday.getFullYear();
    const currentMonth = yesterday.getMonth();
    const lastYear = currentYear - 1;
    
    // Get the day of month for yesterday (e.g., if today is Mar 2, dayOfMonth = 1)
    const dayOfMonth = yesterday.getDate();

    // Applications MTD (current month this year, up to same day)
    const applicationsMTD = filteredData.filter(r => {
      if (!r.Applied) return false;
      const date = new Date(r.Applied);
      return date.getFullYear() === currentYear && 
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).length;

    // Applications same period last year (same month, same day range)
    const applicationsLastYearSameMonth = filteredData.filter(r => {
      if (!r.Applied) return false;
      const date = new Date(r.Applied);
      return date.getFullYear() === lastYear && 
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).length;

    // Enrollments MTD (current month this year, up to same day)
    const enrollmentsMTD = filteredData.filter(r => {
      if (!r.Enrolled) return false;
      const date = new Date(r.Enrolled);
      return date.getFullYear() === currentYear && 
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).length;

    // Enrollments same period last year (same month, same day range)
    const enrollmentsLastYearSameMonth = filteredData.filter(r => {
      if (!r.Enrolled) return false;
      const date = new Date(r.Enrolled);
      return date.getFullYear() === lastYear &&
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).length;

    // Acceptances MTD (current month this year, up to same day) — uses Accepted Date
    const acceptancesMTD = filteredData.filter(r => {
      if (!r.Accepted) return false;
      const date = new Date(r.Accepted);
      return date.getFullYear() === currentYear &&
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).length;

    // Acceptances same period last year (same month, same day range)
    const acceptancesLastYearSameMonth = filteredData.filter(r => {
      if (!r.Accepted) return false;
      const date = new Date(r.Accepted);
      return date.getFullYear() === lastYear &&
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).length;

    // Build daily cumulative sparkline data for current month
    const appDailyCount = {};
    const enrollDailyCount = {};
    const acceptDailyCount = {};
    filteredData.forEach(r => {
      if (r.Applied) {
        const d = new Date(r.Applied);
        if (d.getFullYear() === currentYear && d.getMonth() === currentMonth && d.getDate() <= dayOfMonth) {
          const day = d.getDate();
          appDailyCount[day] = (appDailyCount[day] || 0) + 1;
        }
      }
      if (r.Enrolled) {
        const d = new Date(r.Enrolled);
        if (d.getFullYear() === currentYear && d.getMonth() === currentMonth && d.getDate() <= dayOfMonth) {
          const day = d.getDate();
          enrollDailyCount[day] = (enrollDailyCount[day] || 0) + 1;
        }
      }
      if (r.Accepted) {
        const d = new Date(r.Accepted);
        if (d.getFullYear() === currentYear && d.getMonth() === currentMonth && d.getDate() <= dayOfMonth) {
          const day = d.getDate();
          acceptDailyCount[day] = (acceptDailyCount[day] || 0) + 1;
        }
      }
    });

    // Convert to cumulative arrays [day1Total, day1+day2Total, ...]
    const appSparkline = [];
    const enrollSparkline = [];
    const acceptSparkline = [];
    let appCum = 0, enrollCum = 0, acceptCum = 0;
    for (let d = 1; d <= dayOfMonth; d++) {
      appCum += (appDailyCount[d] || 0);
      enrollCum += (enrollDailyCount[d] || 0);
      acceptCum += (acceptDailyCount[d] || 0);
      appSparkline.push(appCum);
      enrollSparkline.push(enrollCum);
      acceptSparkline.push(acceptCum);
    }

    // Build daily cumulative sparkline for Hired (Date Entered) since Dec 1
    const dec1 = new Date(currentYear, 11, 1); // Dec 1 of current school year start
    // If we're before Dec, use last year's Dec 1
    const hireStartDate = currentMonth < 11 ? new Date(currentYear - 1, 11, 1) : dec1;
    const hireToday = new Date();
    const hireDailyCount = {};
    filteredData.forEach(r => {
      if (!r['Date Entered']) return;
      const d = new Date(r['Date Entered']);
      if (d >= hireStartDate && d <= hireToday) {
        // Key by days since start
        const dayIndex = Math.floor((d - hireStartDate) / (1000 * 60 * 60 * 24));
        hireDailyCount[dayIndex] = (hireDailyCount[dayIndex] || 0) + 1;
      }
    });
    const totalDaysSinceStart = Math.floor((hireToday - hireStartDate) / (1000 * 60 * 60 * 24)) + 1;
    const hireSparkline = [];
    let hireCum = 0;
    for (let d = 0; d < totalDaysSinceStart; d++) {
      hireCum += (hireDailyCount[d] || 0);
      hireSparkline.push(hireCum);
    }
    // Downsample if too many days (show weekly points for cleaner sparkline)
    const hireSparklineDownsampled = hireSparkline.length > 60
      ? hireSparkline.filter((_, i) => i % 7 === 0 || i === hireSparkline.length - 1)
      : hireSparkline;

    // Calculate YoY growth
    const applicationsYoYGrowth = applicationsLastYearSameMonth > 0
      ? (((applicationsMTD - applicationsLastYearSameMonth) / applicationsLastYearSameMonth) * 100).toFixed(1)
      : 0;

    const enrollmentsYoYGrowth = enrollmentsLastYearSameMonth > 0
      ? (((enrollmentsMTD - enrollmentsLastYearSameMonth) / enrollmentsLastYearSameMonth) * 100).toFixed(1)
      : 0;

    const acceptancesYoYGrowth = acceptancesLastYearSameMonth > 0
      ? (((acceptancesMTD - acceptancesLastYearSameMonth) / acceptancesLastYearSameMonth) * 100).toFixed(1)
      : 0;

    // FORECAST MTD AND YTD CALCULATIONS
    // New simplified structure: Row 1 has "Category" + "State" + dates, Row 2+ has category label + states + data
    let forecastApplicationsMTD = 0;
    let forecastApplicationsMTDFiltered = 0;
    let forecastEnrollmentsMTD = 0;
    let forecastEnrollmentsMTDFiltered = 0;
    
    let forecastApplicationsYTD = 0;
    let forecastApplicationsYTDFiltered = 0;
    let forecastEnrollmentsYTD = 0;
    let forecastEnrollmentsYTDFiltered = 0;
    
    if (forecastData && forecastData.length > 0) {
      console.log('Processing forecast data...');
      console.log('Total forecast rows:', forecastData.length);
      console.log('First 5 rows:', forecastData.slice(0, 5));
      
      // Row 1 should have "Category" in column A, "State" in column B, then dates in columns C, D, E...
      const headerRow = forecastData[0];
      const headerValues = Object.values(headerRow);
      const headerKeys = Object.keys(headerRow);
      
      console.log('Header row values:', headerValues);
      console.log('Header row keys:', headerKeys);
      console.log('Total header columns:', headerKeys.length);
      console.log('First header row object:', headerRow);
      
      // Find MTD date columns for current month
      const mtdColumns = [];
      // Find YTD date columns (Jan 1 through yesterday)
      const ytdColumns = [];
      
      // Log some sample dates from different parts of the header
      console.log('Sample dates from header:');
      console.log('  Columns 0-5:', headerValues.slice(0, 5));
      console.log('  Columns 30-35:', headerValues.slice(30, 35));
      console.log('  Columns 60-65:', headerValues.slice(60, 65));
      
      const startOfYear = new Date(currentYear, 0, 1); // Jan 1 of current year
      
      headerKeys.forEach((colKey, idx) => {
        // colKey IS the date string (e.g., "1/1/2026", "2/1/2026")
        if (typeof colKey === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(colKey.trim())) {
          const parts = colKey.trim().split('/');
          const month = parseInt(parts[0]);
          const day = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          
          // Log first few dates found
          if (mtdColumns.length < 5) {
            console.log(`Column ${idx}: Found date ${colKey} (month=${month}, looking for month=${currentMonth + 1})`);
          }
          
          const dateObj = new Date(year, month - 1, day);
          
          // Check if this is current month and within MTD
          if (year === currentYear && month === currentMonth + 1 && day <= dayOfMonth) {
            mtdColumns.push({ colKey, dateStr: colKey, month, day });
          }
          
          // Check if this is YTD (Jan 1 through yesterday)
          if (year === currentYear && dateObj >= startOfYear && dateObj <= yesterday) {
            ytdColumns.push({ colKey, dateStr: colKey, month, day });
          }
        }
      });
      
      console.log(`Found ${mtdColumns.length} MTD columns:`, mtdColumns.map(c => c.dateStr));
      console.log(`Found ${ytdColumns.length} YTD columns (Jan 1 - yesterday)`);
      
      // Process data rows - look for "Applications" and "Enrollments" sections
      let currentCategory = null;
      let rowsProcessed = { Applications: [], Enrollments: [], Unknown: [] };
      
      for (let i = 0; i < forecastData.length; i++) {
        const row = forecastData[i];
        
        // Get Category from column A and State from column B
        const categoryCol = row['Category'] || '';
        const stateCol = row['State'] || '';
        
        // Log ALL rows to see full structure
        if (i <= 50 || categoryCol) {
          console.log(`Row ${i}: Category="${categoryCol}", State="${stateCol}"`);
        }
        
        // Check if this row is a category header (but still process the row if it has a state)
        if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('application')) {
          currentCategory = 'Applications';
          console.log(`>>> SWITCHED TO Applications at row ${i}`);
        } else if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('enrollment')) {
          currentCategory = 'Enrollments';
          console.log(`>>> SWITCHED TO Enrollments at row ${i}`);
        } else if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('hire')) {
          currentCategory = 'Hires';
          console.log(`>>> SWITCHED TO Hires at row ${i} (not summed into Apps/Enrollments)`);
        }
        
        // Get state name from column B
        const stateName = typeof stateCol === 'string' ? stateCol.trim() : '';
        const stateNameLower = stateName.toLowerCase();
        
        // Skip empty rows, header rows, total/summary rows, and blank-state rows
        // Also skip if the "state" is actually a category label or repeated header
        if (!stateName || stateName === '' || stateName === 'State' || 
            stateNameLower === 'total' || stateNameLower === 'totals' ||
            stateNameLower.includes('grand total') || stateNameLower.includes('sum') ||
            stateNameLower === 'all' || stateNameLower === 'all states' ||
            stateNameLower.includes('application') || stateNameLower.includes('enrollment') ||
            stateNameLower.includes('hire') || stateNameLower === 'category') {
          if (stateName) {
            console.log(`   SKIPPED: State="${stateName}"`);
          }
          continue;
        }
        
        // Check if this state matches the filter
        const includeThisState = stateFilter === 'All States' || 
                                  stateName === stateFilter ||
                                  stateName.toLowerCase() === stateFilter.toLowerCase();
        
        // Sum values from MTD columns and track per-row contribution
        let rowMTDSum = 0;
        mtdColumns.forEach(({ colKey }) => {
          const value = parseFloat(row[colKey]) || 0;
          rowMTDSum += value;
          
          if (currentCategory === 'Applications') {
            forecastApplicationsMTD += value;
            if (includeThisState) {
              forecastApplicationsMTDFiltered += value;
            }
          } else if (currentCategory === 'Enrollments') {
            forecastEnrollmentsMTD += value;
            if (includeThisState) {
              forecastEnrollmentsMTDFiltered += value;
            }
          }
        });
        
        // Log every row's contribution
        const bucket = currentCategory || 'Unknown';
        rowsProcessed[bucket] = rowsProcessed[bucket] || [];
        rowsProcessed[bucket].push({ row: i, state: stateName, mtd: rowMTDSum.toFixed(1) });
        console.log(`   SUMMED row ${i}: ${bucket} / ${stateName} => MTD +${rowMTDSum.toFixed(1)} (running: Apps=${forecastApplicationsMTD.toFixed(1)}, Enroll=${forecastEnrollmentsMTD.toFixed(1)})`);
        
        // Sum values from YTD columns
        ytdColumns.forEach(({ colKey }) => {
          const value = parseFloat(row[colKey]) || 0;
          
          if (currentCategory === 'Applications') {
            forecastApplicationsYTD += value;
            if (includeThisState) {
              forecastApplicationsYTDFiltered += value;
            }
          } else if (currentCategory === 'Enrollments') {
            forecastEnrollmentsYTD += value;
            if (includeThisState) {
              forecastEnrollmentsYTDFiltered += value;
            }
          }
        });
      }
      
      console.log('=== FORECAST SUMMARY ===');
      console.log(`Applications rows processed: ${(rowsProcessed.Applications || []).length}`);
      console.log(`Enrollments rows processed: ${(rowsProcessed.Enrollments || []).length}`);
      console.log(`Unknown rows processed: ${(rowsProcessed.Unknown || []).length}`);
      if ((rowsProcessed.Unknown || []).length > 0) {
        console.log('WARNING: Unknown category rows:', rowsProcessed.Unknown);
      }
      
      console.log(`Forecast Applications MTD (All): ${forecastApplicationsMTD}`);
      console.log(`Forecast Applications MTD (${stateFilter}): ${forecastApplicationsMTDFiltered}`);
      console.log(`Forecast Enrollments MTD (All): ${forecastEnrollmentsMTD}`);
      console.log(`Forecast Enrollments MTD (${stateFilter}): ${forecastEnrollmentsMTDFiltered}`);
      console.log(`Forecast Applications YTD (All): ${forecastApplicationsYTD}`);
      console.log(`Forecast Applications YTD (${stateFilter}): ${forecastApplicationsYTDFiltered}`);
      console.log(`Forecast Enrollments YTD (All): ${forecastEnrollmentsYTD}`);
      console.log(`Forecast Enrollments YTD (${stateFilter}): ${forecastEnrollmentsYTDFiltered}`);
    } else {
      console.log('No forecast data available');
    }

    // YTD Calculations (Jan 1 through yesterday of current year vs same period last year)
    const startOfYear = new Date(currentYear, 0, 1); // January 1 of current year
    
    // Applications YTD (Jan 1 through yesterday this year)
    const applicationsYTD = filteredData.filter(r => {
      if (!r.Applied) return false;
      const date = new Date(r.Applied);
      return date >= startOfYear && date <= yesterday;
    }).length;

    // Applications same period last year (Jan 1 through same day last year)
    const lastYearYesterday = new Date(lastYear, yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
    const startOfLastYear = new Date(lastYear, 0, 1);
    
    const applicationsLastYearYTD = filteredData.filter(r => {
      if (!r.Applied) return false;
      const date = new Date(r.Applied);
      return date >= startOfLastYear && date <= lastYearYesterday;
    }).length;

    // Enrollments YTD (Jan 1 through yesterday this year)
    const enrollmentsYTD = filteredData.filter(r => {
      if (!r.Enrolled) return false;
      const date = new Date(r.Enrolled);
      return date >= startOfYear && date <= yesterday;
    }).length;

    // Enrollments same period last year (Jan 1 through same day last year)
    const enrollmentsLastYearYTD = filteredData.filter(r => {
      if (!r.Enrolled) return false;
      const date = new Date(r.Enrolled);
      return date >= startOfLastYear && date <= lastYearYesterday;
    }).length;

    // Acceptances YTD (Jan 1 through yesterday this year) — uses Accepted Date
    const acceptancesYTD = filteredData.filter(r => {
      if (!r.Accepted) return false;
      const date = new Date(r.Accepted);
      return date >= startOfYear && date <= yesterday;
    }).length;

    // Acceptances same period last year (Jan 1 through same day last year)
    const acceptancesLastYearYTD = filteredData.filter(r => {
      if (!r.Accepted) return false;
      const date = new Date(r.Accepted);
      return date >= startOfLastYear && date <= lastYearYesterday;
    }).length;

    // Calculate YTD YoY growth
    const applicationsYTDGrowth = applicationsLastYearYTD > 0
      ? (((applicationsYTD - applicationsLastYearYTD) / applicationsLastYearYTD) * 100).toFixed(1)
      : 0;

    const enrollmentsYTDGrowth = enrollmentsLastYearYTD > 0
      ? (((enrollmentsYTD - enrollmentsLastYearYTD) / enrollmentsLastYearYTD) * 100).toFixed(1)
      : 0;

    const acceptancesYTDGrowth = acceptancesLastYearYTD > 0
      ? (((acceptancesYTD - acceptancesLastYearYTD) / acceptancesLastYearYTD) * 100).toFixed(1)
      : 0;

    // Calculate forecast variances (now that MTD and YTD are defined)
    const forecastAppsToUse = stateFilter === 'All States' ? forecastApplicationsMTD : forecastApplicationsMTDFiltered;
    const forecastAppsVariance = forecastAppsToUse > 0
      ? (((applicationsMTD - forecastAppsToUse) / forecastAppsToUse) * 100).toFixed(1)
      : 0;
      
    const forecastEnrollToUse = stateFilter === 'All States' ? forecastEnrollmentsMTD : forecastEnrollmentsMTDFiltered;
    const forecastEnrollVariance = forecastEnrollToUse > 0
      ? (((enrollmentsMTD - forecastEnrollToUse) / forecastEnrollToUse) * 100).toFixed(1)
      : 0;
      
    const forecastAppsYTDToUse = stateFilter === 'All States' ? forecastApplicationsYTD : forecastApplicationsYTDFiltered;
    const forecastAppsYTDVariance = forecastAppsYTDToUse > 0
      ? (((applicationsYTD - forecastAppsYTDToUse) / forecastAppsYTDToUse) * 100).toFixed(1)
      : 0;
      
    const forecastEnrollYTDToUse = stateFilter === 'All States' ? forecastEnrollmentsYTD : forecastEnrollmentsYTDFiltered;
    const forecastEnrollYTDVariance = forecastEnrollYTDToUse > 0
      ? (((enrollmentsYTD - forecastEnrollYTDToUse) / forecastEnrollYTDToUse) * 100).toFixed(1)
      : 0;

    // Cohort counts by FE Semester membership (all assigned candidates, hired or not) —
    // matches Salesforce reports filtered on FE Semester (e.g. FE Semester = Fall 2026).
    const feCount = (label) => filteredData.filter(r => (r['FE Semester'] || '').includes(label)).length;

    const spring2026Hires = feCount('Spring 2026');
    const spring2025Hires = feCount('Spring 2025');
    const springHiresGrowth = spring2025Hires > 0
      ? (((spring2026Hires - spring2025Hires) / spring2025Hires) * 100).toFixed(1)
      : 0;

    const fall2026Hires = feCount('Fall 2026');
    const fall2025Hires = feCount('Fall 2025');
    const fallHiresGrowth = fall2025Hires > 0
      ? (((fall2026Hires - fall2025Hires) / fall2025Hires) * 100).toFixed(1)
      : 0;

    // Currently Placed Teachers: Spring 2026 + Fall 2026 (drops Fall 2025)
    const currentlyPlacedTeachers = spring2026Hires + fall2026Hires;

    // Prior Year Placed Teachers: Spring 2025 + Fall 2025 (analogous prior period)
    const priorYearPlacedTeachers = spring2025Hires + fall2025Hires;
    const placedTeachersGrowth = priorYearPlacedTeachers > 0
      ? (((currentlyPlacedTeachers - priorYearPlacedTeachers) / priorYearPlacedTeachers) * 100).toFixed(1)
      : 0;
    
    if (stateFilter !== 'All States') {
      console.log(`State Filter: ${stateFilter}`);
      console.log(`Fall 2025 Hires: ${fall2025Hires}`);
      console.log(`Spring 2026 Hires: ${spring2026Hires}`);
      console.log(`Currently Placed Teachers: ${currentlyPlacedTeachers}`);
    }

    // Neat Facts Calculations
    
    // 1. Application Pacing Rank (compare MTD this year vs same period all previous years)
    const allYears = [...new Set(filteredData.map(r => {
      if (!r.Applied) return null;
      const y = new Date(r.Applied).getFullYear();
      return (y >= 2000 && y <= currentYear) ? y : null;
    }).filter(y => y !== null))].sort((a, b) => b - a);
    
    const applicationPacings = allYears.map(year => {
      const yearApps = filteredData.filter(r => {
        if (!r.Applied) return false;
        const date = new Date(r.Applied);
        return date.getFullYear() === year && 
               date.getMonth() === currentMonth &&
               date.getDate() <= dayOfMonth;
      }).length;
      return { year, count: yearApps };
    });
    
    const currentYearPacing = applicationPacings.find(p => p.year === currentYear)?.count || 0;
    const sortedAppPacings = [...applicationPacings].sort((a, b) => b.count - a.count);
    const appPacingRank = sortedAppPacings.findIndex(p => p.year === currentYear) + 1;
    
    // 2. Enrollment Pacing Rank (compare MTD this year vs same period all previous years)
    const enrollmentPacings = allYears.map(year => {
      const yearEnrollments = filteredData.filter(r => {
        if (!r.Enrolled) return false;
        const date = new Date(r.Enrolled);
        return date.getFullYear() === year && 
               date.getMonth() === currentMonth &&
               date.getDate() <= dayOfMonth;
      }).length;
      return { year, count: yearEnrollments };
    });
    
    const currentYearEnrollmentPacing = enrollmentPacings.find(p => p.year === currentYear)?.count || 0;
    const sortedEnrollPacings = [...enrollmentPacings].sort((a, b) => b.count - a.count);
    const enrollmentPacingRank = sortedEnrollPacings.findIndex(p => p.year === currentYear) + 1;
    
    // 3. Top Application State MTD
    const mtdApplicationsByState = filteredData.filter(r => {
      if (!r.Applied) return false;
      const date = new Date(r.Applied);
      return date.getFullYear() === currentYear && 
             date.getMonth() === currentMonth &&
             date.getDate() <= dayOfMonth;
    }).reduce((acc, row) => {
      const state = row['Certification state'] || 'Unknown';
      acc[state] = (acc[state] || 0) + 1;
      return acc;
    }, {});
    
    const topStateMTD = Object.entries(mtdApplicationsByState)
      .sort((a, b) => b[1] - a[1])[0];
    
    const topState = topStateMTD ? {
      name: topStateMTD[0],
      count: topStateMTD[1],
      percentage: ((topStateMTD[1] / applicationsMTD) * 100).toFixed(1)
    } : null;

    // NEW METRIC 1: Enrollment-to-Hired Conversion Rate
    const enrolledWithDateEntered = filteredData.filter(r => r.Enrolled && r['Date Entered']).length;
    const enrollmentToHiredRate = enrolled > 0 ? ((enrolledWithDateEntered / enrolled) * 100).toFixed(1) : 0;

    // NEW METRIC 2: State Quality Ranking (Enrollment Rate × Retention Rate)
    const stateQuality = Object.keys(stateBreakdown).map(state => {
      const stateApps = filteredData.filter(r => r['Certification state'] === state);
      const stateEnrolled = stateApps.filter(r => r.Enrolled).length;
      const stateStarted = stateApps.filter(r => r['Date Entered']).length;
      const stateDropped = stateApps.filter(r => r['Dropped Date']).length;
      const stateActive = stateStarted - stateDropped;
      
      const enrollRate = stateApps.length > 0 ? (stateEnrolled / stateApps.length) * 100 : 0;
      const retentionRate = stateStarted > 0 ? (stateActive / stateStarted) * 100 : 0;
      const qualityScore = (enrollRate / 100) * (retentionRate / 100) * 100; // Combined score
      
      return {
        state,
        applications: stateApps.length,
        enrolled: stateEnrolled,
        enrollRate: enrollRate.toFixed(1),
        retentionRate: retentionRate.toFixed(1),
        qualityScore: qualityScore.toFixed(1)
      };
    }).sort((a, b) => parseFloat(b.qualityScore) - parseFloat(a.qualityScore));

    // NEW METRIC 3: Retention by Time Period (cohort analysis)
    const retentionByPeriod = {
      within30: 0,
      within60: 0,
      within90: 0,
      within180: 0,
      over180: 0
    };
    
    const startedCandidates = filteredData.filter(r => r['Date Entered']);
    startedCandidates.forEach(r => {
      const dateEntered = new Date(r['Date Entered']);
      const daysSinceStart = Math.floor((now - dateEntered) / (1000 * 60 * 60 * 24));
      const isActive = !r['Dropped Date'];
      
      if (daysSinceStart <= 30) {
        retentionByPeriod.within30++;
      } else if (daysSinceStart <= 60 && isActive) {
        retentionByPeriod.within60++;
      } else if (daysSinceStart <= 90 && isActive) {
        retentionByPeriod.within90++;
      } else if (daysSinceStart <= 180 && isActive) {
        retentionByPeriod.within180++;
      } else if (isActive) {
        retentionByPeriod.over180++;
      }
    });
    
    const totalStarted = startedCandidates.length;
    const retentionRates = {
      within30: totalStarted > 0 ? ((retentionByPeriod.within30 / totalStarted) * 100).toFixed(1) : 0,
      within60: totalStarted > 0 ? ((retentionByPeriod.within60 / totalStarted) * 100).toFixed(1) : 0,
      within90: totalStarted > 0 ? ((retentionByPeriod.within90 / totalStarted) * 100).toFixed(1) : 0,
      within180: totalStarted > 0 ? ((retentionByPeriod.within180 / totalStarted) * 100).toFixed(1) : 0,
      over180: totalStarted > 0 ? ((retentionByPeriod.over180 / totalStarted) * 100).toFixed(1) : 0
    };

    // Cohort Conversion Analysis - Last 9 months of applications
    const cohortAnalysis = [];
    for (let i = 8; i >= 0; i--) {
      const monthDate = new Date(now);
      monthDate.setMonth(monthDate.getMonth() - i);
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      
      // Get all applications from this month
      const monthApplications = filteredData.filter(r => {
        if (!r.Applied) return false;
        const appliedDate = new Date(r.Applied);
        return appliedDate.getFullYear() === year && appliedDate.getMonth() === month;
      });
      
      const totalApps = monthApplications.length;
      
      // Calculate conversions within different time windows
      const conversions = {
        within30: 0,
        within60: 0,
        within90: 0,
        within180: 0,
        over180: 0,
        notConverted: 0
      };
      
      monthApplications.forEach(app => {
        if (!app.Enrolled) {
          conversions.notConverted++;
          return;
        }
        
        const appliedDate = new Date(app.Applied);
        const enrolledDate = new Date(app.Enrolled);
        const days = Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
        
        if (days <= 30) conversions.within30++;
        else if (days <= 60) conversions.within60++;
        else if (days <= 90) conversions.within90++;
        else if (days <= 180) conversions.within180++;
        else conversions.over180++;
      });
      
      cohortAnalysis.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        totalApps,
        within30: totalApps > 0 ? ((conversions.within30 / totalApps) * 100).toFixed(1) : 0,
        within60: totalApps > 0 ? ((conversions.within60 / totalApps) * 100).toFixed(1) : 0,
        within90: totalApps > 0 ? ((conversions.within90 / totalApps) * 100).toFixed(1) : 0,
        within180: totalApps > 0 ? ((conversions.within180 / totalApps) * 100).toFixed(1) : 0,
        over180: totalApps > 0 ? ((conversions.over180 / totalApps) * 100).toFixed(1) : 0,
        notConverted: totalApps > 0 ? ((conversions.notConverted / totalApps) * 100).toFixed(1) : 0,
        conversions: conversions
      });
    }

    // Overall conversion distribution (last year only)
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const allConversions = filteredData.filter(r => {
      if (!r.Applied || !r.Enrolled) return false;
      const enrolledDate = new Date(r.Enrolled);
      return enrolledDate >= oneYearAgo;
    });
    
    const conversionDistribution = {
      within30: 0,
      within60: 0,
      within90: 0,
      within180: 0,
      over180: 0
    };
    
    allConversions.forEach(app => {
      const appliedDate = new Date(app.Applied);
      const enrolledDate = new Date(app.Enrolled);
      const days = Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
      
      if (days <= 30) conversionDistribution.within30++;
      else if (days <= 60) conversionDistribution.within60++;
      else if (days <= 90) conversionDistribution.within90++;
      else if (days <= 180) conversionDistribution.within180++;
      else conversionDistribution.over180++;
    });

    // State Performance Scores (Last 6 Months)
    // Calculate for each state: conversion rate, velocity, and overall score
    const sixMonthsCutoff = new Date(yesterday);
    sixMonthsCutoff.setMonth(sixMonthsCutoff.getMonth() - 6);
    
    const stateScores = [];
    const stateNames = [...new Set(filteredData.map(r => r['Certification state']).filter(Boolean))];
    
    stateNames.forEach(state => {
      // Filter for last 6 months only
      const stateData = filteredData.filter(r => {
        if (r['Certification state'] !== state) return false;
        if (!r.Applied) return false;
        const appliedDate = new Date(r.Applied);
        return appliedDate >= sixMonthsCutoff && appliedDate <= yesterday;
      });
      
      const applied = stateData.length;
      const enrolled = stateData.filter(r => r.Enrolled).length;
      
      // Skip states with no applications or no enrollments
      if (applied === 0 || enrolled === 0) return;
      
      // Conversion Rate
      const conversionRate = enrolled / applied;
      
      // Velocity (average days from Applied to Enrolled)
      const conversions = stateData.filter(r => r.Applied && r.Enrolled);
      let avgVelocity = 0;
      
      if (conversions.length > 0) {
        const totalDays = conversions.reduce((sum, app) => {
          const appliedDate = new Date(app.Applied);
          const enrolledDate = new Date(app.Enrolled);
          const days = Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
          return sum + (days > 0 ? days : 0);
        }, 0);
        avgVelocity = totalDays / conversions.length;
      }
      
      // Skip states with no velocity data
      if (avgVelocity === 0) return;
      
      // Scoring
      const conversionTarget = 0.50; // 50%
      const velocityTarget = 30; // 30 days
      
      // Conversion Score (uncapped)
      const conversionScore = (conversionRate / conversionTarget) * 100;
      
      // Velocity Score (square-root guardrail, uncapped)
      const velocityScore = Math.sqrt(velocityTarget / avgVelocity) * 100;
      
      // Final State Score (equal weighting)
      const stateScore = (0.5 * conversionScore) + (0.5 * velocityScore);
      
      stateScores.push({
        state,
        applied,
        enrolled,
        conversionRate: (conversionRate * 100).toFixed(1),
        avgVelocity: avgVelocity.toFixed(1),
        stateScore: stateScore.toFixed(1)
      });
    });
    
    // Sort by state score
    stateScores.sort((a, b) => parseFloat(b.stateScore) - parseFloat(a.stateScore));
    
    // Get top 3 and bottom 3
    const topStates = stateScores.slice(0, 3);
    const bottomStates = stateScores.slice(-3).reverse();

    // AGGREGATE CONVERSION SCORE TREND (Trailing 12 Months)
    // Calculate All States aggregate score for each of the past 12 months
    // NOTE: To avoid recency bias, we look at APPLICATIONS by month and track their
    // conversions within 90 days, excluding the most recent 2 months
    const conversionScoreTrend = [];
    
    // Start from 3 months ago to allow for conversion lag
    const startMonth = 2; // Skip most recent 2 months
    const totalMonths = 12;
    
    for (let i = startMonth; i < startMonth + totalMonths; i++) {
      const monthEnd = new Date(yesterday);
      monthEnd.setMonth(monthEnd.getMonth() - i);
      monthEnd.setDate(1); // Start of month
      monthEnd.setMonth(monthEnd.getMonth() + 1); // Move to next month
      monthEnd.setDate(0); // Last day of target month
      
      const monthStart = new Date(monthEnd);
      monthStart.setDate(1); // First day of month
      
      const monthLabel = monthEnd.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      // Get all applications from this month (all states combined)
      const monthApplications = filteredData.filter(r => {
        if (!r.Applied) return false;
        const appliedDate = new Date(r.Applied);
        return appliedDate >= monthStart && appliedDate <= monthEnd;
      });
      
      const applied = monthApplications.length;
      
      if (applied === 0) {
        conversionScoreTrend.unshift({
          month: monthLabel,
          score: 0,
          applied: 0,
          enrolled: 0
        });
        continue;
      }
      
      // Count enrollments that happened within 90 days of application
      const enrolled = monthApplications.filter(r => {
        if (!r.Enrolled) return false;
        const appliedDate = new Date(r.Applied);
        const enrolledDate = new Date(r.Enrolled);
        const daysDiff = Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
        return daysDiff >= 0 && daysDiff <= 90;
      }).length;
      
      // Calculate conversion rate
      const conversionRate = enrolled / applied;
      
      // Velocity calculation (only for those who converted within 90 days)
      const conversions = monthApplications.filter(r => {
        if (!r.Applied || !r.Enrolled) return false;
        const appliedDate = new Date(r.Applied);
        const enrolledDate = new Date(r.Enrolled);
        const daysDiff = Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
        return daysDiff >= 0 && daysDiff <= 90;
      });
      
      let avgVelocity = 30; // Default to target if no conversions
      
      if (conversions.length > 0) {
        const totalDays = conversions.reduce((sum, app) => {
          const appliedDate = new Date(app.Applied);
          const enrolledDate = new Date(app.Enrolled);
          const days = Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0);
        avgVelocity = totalDays / conversions.length;
      }
      
      // Calculate score (same formula as state scores)
      const conversionTarget = 0.50;
      const velocityTarget = 30;
      
      const conversionScore = (conversionRate / conversionTarget) * 100;
      const velocityScore = avgVelocity > 0 ? Math.sqrt(velocityTarget / avgVelocity) * 100 : 100;
      const aggregateScore = (0.5 * conversionScore) + (0.5 * velocityScore);
      
      conversionScoreTrend.unshift({
        month: monthLabel,
        score: parseFloat(aggregateScore.toFixed(1)),
        applied,
        enrolled
      });
    }

    // STATE MONTH-OVER-MONTH ENROLLMENT COMPARISON
    // Compare rolling 30 days vs prior 30 days for each state
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const sixtyDaysAgo = new Date(today);
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    
    // Get all unique states from raw data (not filtered)
    const allStateNames = [...new Set(rawData.map(r => r['Certification state']).filter(Boolean))];
    
    const stateMoMComparison = [];
    
    allStateNames.forEach(state => {
      const stateData = rawData.filter(r => r['Certification state'] === state);
      
      // Current period: last 30 days enrollments
      const currentPeriodEnrollments = stateData.filter(r => {
        if (!r.Enrolled) return false;
        const enrolledDate = new Date(r.Enrolled);
        return enrolledDate >= thirtyDaysAgo && enrolledDate < today;
      }).length;
      
      // Prior period: 30-60 days ago enrollments
      const priorPeriodEnrollments = stateData.filter(r => {
        if (!r.Enrolled) return false;
        const enrolledDate = new Date(r.Enrolled);
        return enrolledDate >= sixtyDaysAgo && enrolledDate < thirtyDaysAgo;
      }).length;
      
      // Calculate MoM growth
      let momGrowth = 0;
      let momGrowthRaw = 0;
      if (priorPeriodEnrollments > 0) {
        momGrowthRaw = ((currentPeriodEnrollments - priorPeriodEnrollments) / priorPeriodEnrollments) * 100;
        momGrowth = momGrowthRaw.toFixed(1);
      } else if (currentPeriodEnrollments > 0) {
        momGrowthRaw = 100; // New enrollments from zero
        momGrowth = '100.0';
      }
      
      // Only include states with at least some activity
      if (currentPeriodEnrollments > 0 || priorPeriodEnrollments > 0) {
        stateMoMComparison.push({
          state,
          currentPeriod: currentPeriodEnrollments,
          priorPeriod: priorPeriodEnrollments,
          change: currentPeriodEnrollments - priorPeriodEnrollments,
          momGrowth: parseFloat(momGrowth),
          momGrowthRaw
        });
      }
    });
    
    // Sort by MoM growth
    stateMoMComparison.sort((a, b) => b.momGrowthRaw - a.momGrowthRaw);
    
    // Get top and bottom performers
    const topMoMStates = stateMoMComparison.slice(0, 3);
    const bottomMoMStates = stateMoMComparison.filter(s => s.momGrowthRaw < 0).slice(-3).reverse();
    
    // If not enough negative growth states, get lowest positive ones
    const actualBottomMoM = bottomMoMStates.length > 0 
      ? bottomMoMStates 
      : stateMoMComparison.slice(-3).reverse();

    // APPLICATIONS AGE FUNNEL
    // Count applications by how long they've been in the system without enrolling
    // Limited to applications from the last 6 months
    const applicationsAgeFunnel = {
      under30: 0,
      days31to60: 0,
      days61to90: 0,
      over90: 0
    };

    filteredData.forEach(r => {
      if (!r.Applied) return;
      if (r.Enrolled) return; // Only count non-enrolled applications
      
      const appliedDate = new Date(r.Applied);
      
      // Only include applications from last 6 months
      if (appliedDate < sixMonthsCutoff || appliedDate > yesterday) return;
      
      const daysSinceApplied = Math.floor((yesterday - appliedDate) / (1000 * 60 * 60 * 24));
      
      if (daysSinceApplied < 30) {
        applicationsAgeFunnel.under30++;
      } else if (daysSinceApplied <= 60) {
        applicationsAgeFunnel.days31to60++;
      } else if (daysSinceApplied <= 90) {
        applicationsAgeFunnel.days61to90++;
      } else {
        applicationsAgeFunnel.over90++;
      }
    });

    // STATE PERFORMANCE MATRIX (4 Quadrants)
    // Calculate conversion rate and median velocity for each state
    // Also calculate prior period for motion trails
    const stateMatrixData = [];
    
    // Prior period: the 6-month window ending 6 months ago (i.e. months 7-12 ago)
    const priorPeriodEnd = new Date(sixMonthsCutoff);
    const priorPeriodStart = new Date(sixMonthsCutoff);
    priorPeriodStart.setMonth(priorPeriodStart.getMonth() - 6);
    
    stateNames.forEach(state => {
      // Current period: Use 6-month data like state scores
      const stateData = filteredData.filter(r => {
        if (r['Certification state'] !== state) return false;
        if (!r.Applied) return false;
        const appliedDate = new Date(r.Applied);
        return appliedDate >= sixMonthsCutoff && appliedDate <= yesterday;
      });
      
      const applied = stateData.length;
      const enrolled = stateData.filter(r => r.Enrolled).length;
      
      // Need at least 10 applications for meaningful data
      if (applied < 10) return;
      
      const conversionRate = (enrolled / applied) * 100;
      
      // Calculate median velocity
      const velocities = stateData
        .filter(r => r.Applied && r.Enrolled)
        .map(r => {
          const appliedDate = new Date(r.Applied);
          const enrolledDate = new Date(r.Enrolled);
          return Math.floor((enrolledDate - appliedDate) / (1000 * 60 * 60 * 24));
        })
        .filter(d => d > 0)
        .sort((a, b) => a - b);
      
      let medianVelocity = 30; // Default
      if (velocities.length > 0) {
        const mid = Math.floor(velocities.length / 2);
        medianVelocity = velocities.length % 2 === 0
          ? (velocities[mid - 1] + velocities[mid]) / 2
          : velocities[mid];
      }
      
      // Prior period calculation (6 months ago: the 6-month window before current)
      const priorFullData = filteredData.filter(r => {
        if (r['Certification state'] !== state) return false;
        if (!r.Applied) return false;
        const appliedDate = new Date(r.Applied);
        return appliedDate >= priorPeriodStart && appliedDate < priorPeriodEnd;
      });
      
      const priorApplied = priorFullData.length;
      const priorEnrolled = priorFullData.filter(r => r.Enrolled).length;
      const priorConversionRate = priorApplied >= 10 ? (priorEnrolled / priorApplied) * 100 : null;
      
      const priorVelocities = priorFullData
        .filter(r => r.Applied && r.Enrolled)
        .map(r => {
          const aDate = new Date(r.Applied);
          const eDate = new Date(r.Enrolled);
          return Math.floor((eDate - aDate) / (1000 * 60 * 60 * 24));
        })
        .filter(d => d > 0)
        .sort((a, b) => a - b);
      
      let priorMedianVelocity = null;
      if (priorVelocities.length > 0) {
        const mid = Math.floor(priorVelocities.length / 2);
        priorMedianVelocity = priorVelocities.length % 2 === 0
          ? (priorVelocities[mid - 1] + priorVelocities[mid]) / 2
          : priorVelocities[mid];
      }
      
      // Determine quadrant
      let quadrant = '';
      if (conversionRate >= 50 && medianVelocity <= 30) {
        quadrant = 'Scale';
      } else if (conversionRate >= 50 && medianVelocity > 30) {
        quadrant = 'Operational upside';
      } else if (conversionRate < 50 && medianVelocity <= 30) {
        quadrant = 'Lead quality issue';
      } else {
        quadrant = 'Structural risk';
      }
      
      stateMatrixData.push({
        state,
        conversionRate: parseFloat(conversionRate.toFixed(1)),
        medianVelocity: parseFloat(medianVelocity.toFixed(1)),
        applied,
        enrolled,
        quadrant,
        prevConversionRate: priorConversionRate !== null ? parseFloat(priorConversionRate.toFixed(1)) : null,
        prevMedianVelocity: priorMedianVelocity !== null ? parseFloat(priorMedianVelocity.toFixed(1)) : null
      });
    });

    // ===== Evaluation Health Metrics =====
    // 1. Accepted but not enrolled (past 2 years) — count and average age
    const twoYearsAgoEval = new Date(now);
    twoYearsAgoEval.setFullYear(twoYearsAgoEval.getFullYear() - 2);
    const acceptedNotEnrolled = filteredData.filter(r => {
      if (!r.Accepted || r.Enrolled) return false;
      const acceptedDate = new Date(r.Accepted);
      return acceptedDate >= twoYearsAgoEval;
    });
    const evalAcceptedNotEnrolledCount = acceptedNotEnrolled.length;
    let evalAcceptedNotEnrolledAvgAge = 0;
    if (acceptedNotEnrolled.length > 0) {
      const ages = acceptedNotEnrolled.map(r => {
        const acceptedDate = new Date(r.Accepted);
        return Math.floor((now - acceptedDate) / (1000 * 60 * 60 * 24));
      }).filter(d => d >= 0 && d < 3650); // sanity check
      evalAcceptedNotEnrolledAvgAge = ages.length > 0 ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
    }

    // 2. Evaluations completed WTD and MTD (from Evaluation column)
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const evalCompletedWTD = filteredData.filter(r => {
      if (!r.Evaluation) return false;
      const evalDate = new Date(r.Evaluation);
      return evalDate >= startOfWeek && evalDate <= now;
    }).length;
    
    const evalCompletedMTD = filteredData.filter(r => {
      if (!r.Evaluation) return false;
      const evalDate = new Date(r.Evaluation);
      return evalDate >= startOfMonth && evalDate <= now;
    }).length;

    // ===== Forecast Readiness / Pipeline Gap Analysis =====
    // 1. Historical conversion rate (Applied → Enrolled) over last 6 months
    const sixMonthCutoff = new Date(now);
    sixMonthCutoff.setMonth(sixMonthCutoff.getMonth() - 6);
    const matureApps = filteredData.filter(r => {
      if (!r.Applied) return false;
      const d = new Date(r.Applied);
      return d >= sixMonthCutoff && d < startOfMonth; // exclude current month (not yet mature)
    });
    const matureEnrolled = matureApps.filter(r => r.Enrolled).length;
    const historicalConversionRate = matureApps.length > 0 
      ? matureEnrolled / matureApps.length 
      : 0;

    // Stage-level conversion rates
    const matureEvaluated = matureApps.filter(r => r.Evaluation).length;
    const matureAccepted = matureApps.filter(r => r.Accepted).length;
    const applyToEvalRate = matureApps.length > 0 ? matureEvaluated / matureApps.length : 0;
    const evalToAcceptRate = matureEvaluated > 0 ? matureAccepted / matureEvaluated : 0;
    const acceptToEnrollRate = matureAccepted > 0 ? matureEnrolled / matureAccepted : 0;

    // Prior period stage rates (shifted 1 month: 7mo ago through end of 2mo ago)
    // This creates a rolling MoM comparison where each month the window slides
    const priorStart = new Date(now);
    priorStart.setMonth(priorStart.getMonth() - 7);
    const priorEnd = new Date(now.getFullYear(), now.getMonth() - 1, 1); // start of last month = end of 2mo ago
    const priorApps = filteredData.filter(r => {
      if (!r.Applied) return false;
      const d = new Date(r.Applied);
      return d >= priorStart && d < priorEnd;
    });
    const priorEvaluated = priorApps.filter(r => r.Evaluation).length;
    const priorAccepted = priorApps.filter(r => r.Accepted).length;
    const priorEnrolled = priorApps.filter(r => r.Enrolled).length;
    const priorApplyToEvalRate = priorApps.length > 0 ? priorEvaluated / priorApps.length : 0;
    const priorEvalToAcceptRate = priorEvaluated > 0 ? priorAccepted / priorEvaluated : 0;
    const priorAcceptToEnrollRate = priorAccepted > 0 ? priorEnrolled / priorAccepted : 0;
    const priorOverallRate = priorApps.length > 0 ? priorEnrolled / priorApps.length : 0;

    // Median velocity: Applied → Enrolled (days)
    const enrollVelocities = matureApps
      .filter(r => r.Enrolled)
      .map(r => Math.floor((new Date(r.Enrolled) - new Date(r.Applied)) / (1000 * 60 * 60 * 24)))
      .filter(d => d > 0 && d < 365)
      .sort((a, b) => a - b);
    const medianEnrollVelocity = enrollVelocities.length > 0 
      ? enrollVelocities[Math.floor(enrollVelocities.length / 2)] 
      : 45;

    // Stage-specific median velocities (days remaining to enrollment from each stage)
    const evalToEnrollVelocities = matureApps
      .filter(r => r.Evaluation && r.Enrolled)
      .map(r => Math.floor((new Date(r.Enrolled) - new Date(r.Evaluation)) / (1000 * 60 * 60 * 24)))
      .filter(d => d > 0 && d < 365)
      .sort((a, b) => a - b);
    const medianEvalToEnroll = evalToEnrollVelocities.length > 0
      ? evalToEnrollVelocities[Math.floor(evalToEnrollVelocities.length / 2)]
      : 30;

    const acceptToEnrollVelocities = matureApps
      .filter(r => r.Accepted && r.Enrolled)
      .map(r => Math.floor((new Date(r.Enrolled) - new Date(r.Accepted)) / (1000 * 60 * 60 * 24)))
      .filter(d => d > 0 && d < 365)
      .sort((a, b) => a - b);
    const medianAcceptToEnroll = acceptToEnrollVelocities.length > 0
      ? acceptToEnrollVelocities[Math.floor(acceptToEnrollVelocities.length / 2)]
      : 14;

    const projectionWindowDays = 90; // 3 months

    // 2. Current pipeline inventory (only viable candidates — applied within last 180 days)
    const pipelineCutoff = new Date(now);
    pipelineCutoff.setDate(pipelineCutoff.getDate() - 180);
    
    // Pipeline with velocity filter: only count candidates who can realistically convert within 90 days
    const pipelineAppliedOnlyList = filteredData.filter(r => {
      if (!r.Applied || r.Evaluation || r.Enrolled) return false;
      return new Date(r.Applied) >= pipelineCutoff;
    });
    const pipelineEvaluatedList = filteredData.filter(r => {
      if (!r.Evaluation || r.Accepted || r.Enrolled) return false;
      return new Date(r.Applied || r.Evaluation) >= pipelineCutoff;
    });
    const pipelineAcceptedList = filteredData.filter(r => {
      if (!r.Accepted || r.Enrolled) return false;
      return new Date(r.Applied || r.Accepted) >= pipelineCutoff;
    });

    const pipelineAppliedOnly = pipelineAppliedOnlyList.length;
    const pipelineEvaluated = pipelineEvaluatedList.length;
    const pipelineAccepted = pipelineAcceptedList.length;

    // Velocity-filtered: how many days has each candidate already been at their current stage?
    // Only count those with enough time remaining to convert within 90 days
    const velocityFilteredAccepted = pipelineAcceptedList.filter(r => {
      const daysAtStage = Math.floor((now - new Date(r.Accepted)) / (1000 * 60 * 60 * 24));
      // They've been accepted for daysAtStage days. Median accept→enroll is medianAcceptToEnroll.
      // Estimated days remaining = max(medianAcceptToEnroll - daysAtStage, 0)
      const daysRemaining = Math.max(medianAcceptToEnroll - daysAtStage, 0);
      return daysRemaining <= projectionWindowDays;
    }).length;

    const velocityFilteredEvaluated = pipelineEvaluatedList.filter(r => {
      const daysAtStage = Math.floor((now - new Date(r.Evaluation)) / (1000 * 60 * 60 * 24));
      const daysRemaining = Math.max(medianEvalToEnroll - daysAtStage, 0);
      return daysRemaining <= projectionWindowDays;
    }).length;

    const velocityFilteredApplied = pipelineAppliedOnlyList.filter(r => {
      const daysAtStage = Math.floor((now - new Date(r.Applied)) / (1000 * 60 * 60 * 24));
      const daysRemaining = Math.max(medianEnrollVelocity - daysAtStage, 0);
      return daysRemaining <= projectionWindowDays;
    }).length;

    // 3. Project enrollments from existing pipeline (velocity-adjusted)
    const projectedFromAccepted = Math.round(velocityFilteredAccepted * acceptToEnrollRate);
    const projectedFromEvaluated = Math.round(velocityFilteredEvaluated * evalToAcceptRate * acceptToEnrollRate);
    const projectedFromApplied = Math.round(velocityFilteredApplied * applyToEvalRate * evalToAcceptRate * acceptToEnrollRate);
    const projectedFromExistingPipeline = projectedFromAccepted + projectedFromEvaluated + projectedFromApplied;

    // 4. Get remaining enrollment forecast AND application forecast for next 3 months
    let forecastNext3Months = 0;
    let forecastNext3ByState = {};
    let forecastAppsNext3Months = 0;
    let forecastAppsByMonth = {}; // month number → total forecast apps
    let forecastAppsCurrentMonth = 0; // current month's forecast app volume
    if (forecastData && forecastData.length > 0) {
      const headerKeys = Object.keys(forecastData[0]);
      const futureMonthCols = {};
      const currentMonthCols = [];
      
      headerKeys.forEach(colKey => {
        if (typeof colKey === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(colKey.trim())) {
          const parts = colKey.trim().split('/');
          const month = parseInt(parts[0]);
          const day = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          const colDate = new Date(year, month - 1, day);
          // Next 3 months from today
          const threeMonthsOut = new Date(now);
          threeMonthsOut.setMonth(threeMonthsOut.getMonth() + 3);
          if (colDate > now && colDate <= threeMonthsOut && year === now.getFullYear()) {
            if (!futureMonthCols[month]) futureMonthCols[month] = [];
            futureMonthCols[month].push(colKey);
          }
          // Current month columns (for baseline)
          if (year === now.getFullYear() && month === now.getMonth() + 1) {
            currentMonthCols.push(colKey);
          }
        }
      });

      let fCat = null;
      for (let i = 0; i < forecastData.length; i++) {
        const row = forecastData[i];
        const categoryCol = row['Category'] || '';
        const stateCol = (row['State'] || '').trim();
        
        if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('application')) {
          fCat = 'Applications';
        } else if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('enrollment')) {
          fCat = 'Enrollments';
        } else if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('hire')) {
          fCat = 'Hires';
        }
        
        if (!fCat || fCat === 'Hires' || !stateCol) continue;
        const sl = stateCol.toLowerCase();
        if (sl === 'total' || sl === 'totals' || sl.includes('grand') || sl === 'state' || sl === 'category') continue;
        
        const includeState = stateFilter === 'All States' || stateCol === stateFilter || sl === stateFilter.toLowerCase();
        if (!includeState) continue;
        
        if (fCat === 'Enrollments') {
          let stateTotal = 0;
          Object.values(futureMonthCols).flat().forEach(colKey => {
            stateTotal += parseFloat(row[colKey]) || 0;
          });
          forecastNext3Months += stateTotal;
          if (stateTotal > 0) {
            forecastNext3ByState[stateCol] = (forecastNext3ByState[stateCol] || 0) + stateTotal;
          }
        } else if (fCat === 'Applications') {
          // Total future apps
          Object.values(futureMonthCols).flat().forEach(colKey => {
            forecastAppsNext3Months += parseFloat(row[colKey]) || 0;
          });
          // Per-month future apps
          Object.entries(futureMonthCols).forEach(([month, cols]) => {
            cols.forEach(colKey => {
              forecastAppsByMonth[month] = (forecastAppsByMonth[month] || 0) + (parseFloat(row[colKey]) || 0);
            });
          });
          // Current month apps
          currentMonthCols.forEach(colKey => {
            forecastAppsCurrentMonth += parseFloat(row[colKey]) || 0;
          });
        }
      }
    }
    forecastNext3Months = Math.round(forecastNext3Months);
    forecastAppsNext3Months = Math.round(forecastAppsNext3Months);
    forecastAppsCurrentMonth = Math.round(forecastAppsCurrentMonth);

    // 4b. Project enrollments from forecasted future applications (velocity-adjusted)
    // Apps arriving in month 1 have ~75-90 days to convert — high probability
    // Apps arriving in month 2 have ~45-60 days — moderate probability  
    // Apps arriving in month 3 have ~15-30 days — lower probability if velocity is slow
    // Weight each month's apps by probability of converting within the remaining window
    let projectedFromFutureApps = 0;
    if (forecastData && forecastData.length > 0) {
      const headerKeys = Object.keys(forecastData[0]);
      
      // Group forecast app columns by month
      const appColsByMonth = {};
      headerKeys.forEach(colKey => {
        if (typeof colKey === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(colKey.trim())) {
          const parts = colKey.trim().split('/');
          const month = parseInt(parts[0]);
          const day = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          const colDate = new Date(year, month - 1, day);
          const threeMonthsOut = new Date(now);
          threeMonthsOut.setMonth(threeMonthsOut.getMonth() + 3);
          if (colDate > now && colDate <= threeMonthsOut && year === now.getFullYear()) {
            // Calculate days remaining from this date to end of window
            const daysRemaining = Math.floor((threeMonthsOut - colDate) / (1000 * 60 * 60 * 24));
            if (!appColsByMonth[colKey]) appColsByMonth[colKey] = daysRemaining;
          }
        }
      });

      let fCatApps = null;
      for (let i = 0; i < forecastData.length; i++) {
        const row = forecastData[i];
        const categoryCol = row['Category'] || '';
        const stateCol = (row['State'] || '').trim();
        
        if (typeof categoryCol === 'string' && categoryCol.toLowerCase().includes('application')) {
          fCatApps = 'Applications';
        } else if (typeof categoryCol === 'string' && (categoryCol.toLowerCase().includes('enrollment') || categoryCol.toLowerCase().includes('hire'))) {
          fCatApps = null;
        }
        
        if (fCatApps !== 'Applications' || !stateCol) continue;
        const sl = stateCol.toLowerCase();
        if (sl === 'total' || sl === 'totals' || sl.includes('grand') || sl === 'state' || sl === 'category') continue;
        
        const includeState = stateFilter === 'All States' || stateCol === stateFilter || sl === stateFilter.toLowerCase();
        if (!includeState) continue;
        
        Object.entries(appColsByMonth).forEach(([colKey, daysRemaining]) => {
          const dailyApps = parseFloat(row[colKey]) || 0;
          // Only count apps that have enough days remaining to convert (based on median velocity)
          if (daysRemaining >= medianEnrollVelocity) {
            // Full conversion probability
            projectedFromFutureApps += dailyApps * historicalConversionRate;
          } else if (daysRemaining >= medianEnrollVelocity * 0.5) {
            // Partial probability — scale linearly
            const velocityFactor = daysRemaining / medianEnrollVelocity;
            projectedFromFutureApps += dailyApps * historicalConversionRate * velocityFactor;
          }
          // Apps with less than half the needed velocity time are excluded
        });
      }
    }
    projectedFromFutureApps = Math.round(projectedFromFutureApps);
    const totalProjectedEnrollments = projectedFromExistingPipeline + projectedFromFutureApps;

    // 4c. Run-rate projection (growth-adjusted based on forecast application trajectory)
    // Base: actual recent enrollment pace
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const enrollmentsLastMonth = filteredData.filter(r => {
      if (!r.Enrolled) return false;
      const d = new Date(r.Enrolled);
      return d >= lastMonthStart && d <= lastMonthEnd;
    }).length;

    const currentMonthEnrollments = filteredData.filter(r => {
      if (!r.Enrolled) return false;
      const d = new Date(r.Enrolled);
      return d >= startOfMonth && d <= now;
    }).length;
    const daysIntoCurrentMonth = Math.max(now.getDate(), 1);
    const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentMonthPace = Math.round(currentMonthEnrollments / daysIntoCurrentMonth * daysInCurrentMonth);

    // Weighted baseline (kept for display context)
    const baseMonthlyRate = Math.round(enrollmentsLastMonth * 0.4 + currentMonthPace * 0.6);

    // Adjusted Conversion Rate: apps that applied 14-90 days ago
    // Old enough to have matured, recent enough to reflect current trends
    const adjStart = new Date(now);
    adjStart.setDate(adjStart.getDate() - 90);
    const adjEnd = new Date(now);
    adjEnd.setDate(adjEnd.getDate() - 14);
    const adjCohort = filteredData.filter(r => {
      if (!r.Applied) return false;
      const d = new Date(r.Applied);
      return d >= adjStart && d <= adjEnd;
    });
    const adjCohortCount = adjCohort.length;
    const adjCohortEnrolled = adjCohort.filter(r => r.Enrolled).length;
    const adjustedConversionRate = adjCohortCount > 0 ? (adjCohortEnrolled / adjCohortCount) : 0;

    // Rolling 14-90 day cohort conversion trend — monthly snapshots
    // Last 5 months use end-of-month, current month uses today
    const cohortTrend = [];
    for (let i = 5; i >= 0; i--) {
      let snapshotDate;
      if (i === 0) {
        snapshotDate = new Date(now);
      } else {
        // End of (current month - i): year, month - i + 1, day 0 = last day of month - i
        snapshotDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      }
      const tStart = new Date(snapshotDate);
      tStart.setDate(tStart.getDate() - 90);
      const tEnd = new Date(snapshotDate);
      tEnd.setDate(tEnd.getDate() - 14);

      const tCohort = filteredData.filter(r => {
        if (!r.Applied) return false;
        const d = new Date(r.Applied);
        return d >= tStart && d <= tEnd;
      });
      const tApps = tCohort.length;
      const tEnrolled = tCohort.filter(r => {
        if (!r.Enrolled) return false;
        // Only count enrollments that happened by the snapshot date
        return new Date(r.Enrolled) <= snapshotDate;
      }).length;
      const tRate = tApps > 0 ? +(tEnrolled / tApps * 100).toFixed(1) : 0;

      cohortTrend.push({
        label: snapshotDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        rate: tRate,
        apps: tApps,
        enrolled: tEnrolled,
      });
    }

    // YoY comparison for the same 14-90 day window
    const adjStartLY = new Date(adjStart);
    adjStartLY.setFullYear(adjStartLY.getFullYear() - 1);
    const adjEndLY = new Date(adjEnd);
    adjEndLY.setFullYear(adjEndLY.getFullYear() - 1);
    const adjCohortLY = filteredData.filter(r => {
      if (!r.Applied) return false;
      const d = new Date(r.Applied);
      return d >= adjStartLY && d <= adjEndLY;
    });
    const adjCohortCountLY = adjCohortLY.length;
    // Only count enrollments within 90 days of application (apples-to-apples with current window)
    const adjCohortEnrolledLY = adjCohortLY.filter(r => {
      if (!r.Enrolled || !r.Applied) return false;
      const applied = new Date(r.Applied);
      const enrolled = new Date(r.Enrolled);
      const days = Math.floor((enrolled - applied) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 90;
    }).length;
    const adjustedConversionRateLY = adjCohortCountLY > 0 ? (adjCohortEnrolledLY / adjCohortCountLY) : 0;
    const adjRateYoYChange = adjustedConversionRateLY > 0
      ? +((adjustedConversionRate - adjustedConversionRateLY) / adjustedConversionRateLY * 100).toFixed(1)
      : null;

    // Adjusted Pace: forecast enrollment targets scaled by the ratio of adjusted vs historical rate
    // If historical is 30% but adjusted is 25%, we're converting at 83% of expected pace
    // Apply that ratio to the forecast enrollment targets
    const paceRatio = historicalConversionRate > 0 ? (adjustedConversionRate / historicalConversionRate) : 1;
    
    // Also compute simple: existing pipeline × adjusted rate + future apps × adjusted rate
    // Simplest approach: scale the forecast target by the pace ratio
    const runRateProjection3Mo = Math.round(forecastNext3Months * paceRatio);
    const avgMonthlyRunRate = Math.round(runRateProjection3Mo / 3);

    // 5. Gap analysis
    const forecastGap = forecastNext3Months - totalProjectedEnrollments;
    const gapIsPositive = forecastGap > 0; // true = we're short
    const runRateGap = forecastNext3Months - runRateProjection3Mo;
    const runRateGapIsPositive = runRateGap > 0;

    // Prescription A: Additional apps needed at current conversion
    const additionalAppsNeeded = gapIsPositive && historicalConversionRate > 0 
      ? Math.ceil(forecastGap / historicalConversionRate) 
      : 0;

    // Prescription B: Required conversion rate improvement
    // Current rate produces totalProjectedEnrollments. We need forecastNext3Months.
    // Scale the current rate proportionally to close the gap.
    const requiredConversionRate = gapIsPositive && totalProjectedEnrollments > 0
      ? (historicalConversionRate * 100) * (forecastNext3Months / totalProjectedEnrollments)
      : historicalConversionRate * 100;

    // 6. At-risk states (only when viewing All States)
    let atRiskStates = [];
    if (stateFilter === 'All States' && forecastData && forecastData.length > 0) {
      const allStates = [...new Set(filteredData.map(r => r['Certification state']).filter(Boolean))];
      
      atRiskStates = allStates.map(state => {
        const stateData = filteredData.filter(r => r['Certification state'] === state);
        const stateMatureApps = stateData.filter(r => {
          if (!r.Applied) return false;
          const d = new Date(r.Applied);
          return d >= sixMonthCutoff && d < startOfMonth;
        });
        const stateMatureEnrolled = stateMatureApps.filter(r => r.Enrolled).length;
        const stateConv = stateMatureApps.length > 0 ? stateMatureEnrolled / stateMatureApps.length : 0;
        
        const statePipeAccepted = stateData.filter(r => r.Accepted && !r.Enrolled && new Date(r.Applied || r.Accepted) >= pipelineCutoff).length;
        const statePipeEvaluated = stateData.filter(r => r.Evaluation && !r.Accepted && !r.Enrolled && new Date(r.Applied || r.Evaluation) >= pipelineCutoff).length;
        const statePipeApplied = stateData.filter(r => r.Applied && !r.Evaluation && !r.Enrolled && new Date(r.Applied) >= pipelineCutoff).length;
        const stateProjected = Math.round(
          (statePipeAccepted * acceptToEnrollRate) + 
          (statePipeEvaluated * evalToAcceptRate * acceptToEnrollRate) + 
          (statePipeApplied * stateConv)
        );
        
        const stateForecast = Math.round(forecastNext3ByState[state] || 0);
        const stateGap = stateForecast - stateProjected;
        
        return {
          state,
          forecast: stateForecast,
          projected: stateProjected,
          gap: stateGap,
          conversionRate: (stateConv * 100).toFixed(1),
          pipeline: statePipeApplied + statePipeEvaluated + statePipeAccepted,
        };
      })
      .filter(s => s.forecast > 0 && s.gap > 0)
      .sort((a, b) => b.gap - a.gap)
      .slice(0, 3);
    }

    const forecastReadiness = {
      historicalConversionRate: (historicalConversionRate * 100).toFixed(1),
      stageRates: {
        applyToEval: (applyToEvalRate * 100).toFixed(1),
        evalToAccept: (evalToAcceptRate * 100).toFixed(1),
        acceptToEnroll: (acceptToEnrollRate * 100).toFixed(1),
      },
      priorStageRates: {
        applyToEval: (priorApplyToEvalRate * 100).toFixed(1),
        evalToAccept: (priorEvalToAcceptRate * 100).toFixed(1),
        acceptToEnroll: (priorAcceptToEnrollRate * 100).toFixed(1),
        overall: (priorOverallRate * 100).toFixed(1),
      },
      medianVelocityDays: medianEnrollVelocity,
      pipeline: {
        applied: pipelineAppliedOnly,
        evaluated: pipelineEvaluated,
        accepted: pipelineAccepted,
        total: pipelineAppliedOnly + pipelineEvaluated + pipelineAccepted,
      },
      projectedEnrollments: totalProjectedEnrollments,
      projectedFromPipeline: projectedFromExistingPipeline,
      projectedFromFutureApps: projectedFromFutureApps,
      forecastAppsNext3Mo: forecastAppsNext3Months,
      forecastTarget: forecastNext3Months,
      requiredRateToMeetForecast: totalProjectedEnrollments > 0 
        ? ((historicalConversionRate * 100) * (forecastNext3Months / totalProjectedEnrollments)).toFixed(1)
        : '0',
      gap: forecastGap,
      onTrack: !gapIsPositive,
      prescriptionA: {
        additionalAppsNeeded,
      },
      prescriptionB: {
        currentConversion: (historicalConversionRate * 100).toFixed(1),
        requiredConversion: requiredConversionRate.toFixed(1),
      },
      runRate: {
        adjustedRate: (adjustedConversionRate * 100).toFixed(1),
        adjCohortApps: adjCohortCount,
        adjCohortEnrolled: adjCohortEnrolled,
        paceRatio: (paceRatio * 100).toFixed(1),
        projection3Mo: runRateProjection3Mo,
        gap: runRateGap,
        onTrack: !runRateGapIsPositive,
        trend: cohortTrend,
        priorYearRate: (adjustedConversionRateLY * 100).toFixed(1),
        priorYearApps: adjCohortCountLY,
        priorYearEnrolled: adjCohortEnrolledLY,
        yoyChange: adjRateYoYChange,
      },
      atRiskStates,
    };

    return {
      totalApplications,
      enrolled,
      dateEntered,
      dropped,
      enrollmentRate: parseFloat(enrollmentRate),
      completionRate: parseFloat(completionRate),
      retentionRate: parseFloat(retentionRate),
      last6MonthsApps: last6MonthsApps.length,
      last6MonthsEnrolled,
      last2YearsEnrolled: last2YearsEnrolled.length,
      last2YearsCompleted,
      avgEnrollmentTime,
      popularTimeSlot,
      popularTimeCount,
      timeOfDayBreakdown,
      stateBreakdown,
      semesterBreakdown,
      semesterTrends,
      semesterComparison,
      monthlyTrend,
      monthlyEnrollmentTrend,
      currentSemester,
      currentRank,
      totalSemesters: semesterTrends.length,
      // MTD metrics
      applicationsMTD,
      applicationsYoYGrowth: parseFloat(applicationsYoYGrowth),
      applicationsMTDLastYear: applicationsLastYearSameMonth,
      forecastMTD: Math.round(forecastAppsToUse),
      forecastVariance: parseFloat(forecastAppsVariance),
      appSparkline,
      // Current funnel snapshot totals (state-filtered)
      currentApps,
      currentAccepted,
      currentEnrolled,
      // Current Hired mirrors Currently Placed Teachers (Spring + Fall 2026 FE cohorts) so the
      // Hires card shows one placement number, not a status snapshot that excludes older applicants.
      currentHired: currentlyPlacedTeachers,
      acceptancesMTD,
      acceptancesYoYGrowth: parseFloat(acceptancesYoYGrowth),
      acceptancesMTDLastYear: acceptancesLastYearSameMonth,
      acceptSparkline,
      enrollmentsMTD,
      enrollmentsYoYGrowth: parseFloat(enrollmentsYoYGrowth),
      enrollmentsMTDLastYear: enrollmentsLastYearSameMonth,
      forecastEnrollmentsMTD: Math.round(forecastEnrollToUse),
      forecastEnrollmentsVariance: parseFloat(forecastEnrollVariance),
      enrollSparkline,
      hireSparkline: hireSparklineDownsampled,
      // YTD metrics
      applicationsYTD,
      applicationsYTDGrowth: parseFloat(applicationsYTDGrowth),
      applicationsYTDLastYear: applicationsLastYearYTD,
      forecastYTD: Math.round(forecastAppsYTDToUse),
      forecastYTDVariance: parseFloat(forecastAppsYTDVariance),
      acceptancesYTD,
      acceptancesYTDGrowth: parseFloat(acceptancesYTDGrowth),
      acceptancesYTDLastYear: acceptancesLastYearYTD,
      enrollmentsYTD,
      enrollmentsYTDGrowth: parseFloat(enrollmentsYTDGrowth),
      enrollmentsYTDLastYear: enrollmentsLastYearYTD,
      forecastEnrollmentsYTD: Math.round(forecastEnrollYTDToUse),
      forecastEnrollmentsYTDVariance: parseFloat(forecastEnrollYTDVariance),
      // Spring Hires comparison
      spring2026Hires,
      spring2025Hires,
      springHiresGrowth: parseFloat(springHiresGrowth),
      fall2026Hires,
      fall2025Hires,
      fallHiresGrowth: parseFloat(fallHiresGrowth),
      currentlyPlacedTeachers,
      priorYearPlacedTeachers,
      placedTeachersGrowth: parseFloat(placedTeachersGrowth),
      // Cohort analysis
      cohortAnalysis,
      conversionDistribution,
      monthlyTrendMeta,
      // Neat facts
      appPacingRank,
      appPacingTotal: allYears.length,
      enrollmentPacingRank,
      enrollmentPacingTotal: allYears.length,
      topState,
      // New metrics
      enrollmentToHiredRate: parseFloat(enrollmentToHiredRate),
      stateQuality,
      retentionRates,
      // State performance scores
      topStates,
      bottomStates,
      conversionScoreTrend,
      // State MoM enrollment comparison
      topMoMStates,
      bottomMoMStates: actualBottomMoM,
      stateMoMComparison,
      // Applications age funnel
      applicationsAgeFunnel,
      // State performance matrix
      stateMatrixData,
      // Evaluation Health metrics
      evalAcceptedNotEnrolledCount,
      evalAcceptedNotEnrolledAvgAge,
      evalCompletedWTD,
      evalCompletedMTD,
      // Forecast readiness
      forecastReadiness,
    };
  };

  // Keep a ref to selectedState so the interval callback always has the current value
  const selectedStateRef = React.useRef(selectedState);
  React.useEffect(() => { selectedStateRef.current = selectedState; }, [selectedState]);

  const fetchData = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      // Fetch all data sources in parallel
      console.log('Fetching all data sources in parallel...');
      const startTime = Date.now();

      const [sfResult, forecastResult, evalResult, feHiresResult, addOnResult] = await Promise.allSettled([
        // Salesforce (primary) with Google Sheets fallback
        (async () => {
          try {
            console.log('Fetching data from Salesforce...');
            const sfResponse = await fetch(SALESFORCE_URL);
            if (!sfResponse.ok) throw new Error(`Salesforce HTTP error: ${sfResponse.status}`);
            const csvText = await sfResponse.text();
            console.log('Salesforce CSV length:', csvText.length);
            return { csvText, source: 'salesforce' };
          } catch (sfError) {
            console.warn('Salesforce fetch failed, falling back to Google Sheets:', sfError.message);
            const response = await fetch(SHEET_URL);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const csvText = await response.text();
            console.log('Google Sheets CSV length:', csvText.length);
            return { csvText, source: 'google-sheets' };
          }
        })(),
        // Forecast
        (async () => {
          const forecastResponse = await fetch(FORECAST_URL);
          if (!forecastResponse.ok) throw new Error('Forecast fetch failed');
          const forecastText = await forecastResponse.text();
          return parseCSV(forecastText);
        })(),
        // Evaluations
        (async () => {
          const evalResponse = await fetch(EVALUATION_URL);
          if (!evalResponse.ok) throw new Error('Evaluation fetch failed');
          const evalText = await evalResponse.text();
          return parseCSV(evalText);
        })(),
        // Field Experience hire cohorts (Fall/Spring counts by Date_Entered__c, per state)
        (async () => {
          const feResponse = await fetch(FE_HIRES_URL);
          if (!feResponse.ok) throw new Error(`FE hires HTTP error: ${feResponse.status}`);
          return feResponse.json();
        })(),
        // Louisiana Add-On track registrations (its own program, not in the main feed)
        (async () => {
          const addOnResponse = await fetch(LA_ADDON_URL);
          if (!addOnResponse.ok) throw new Error(`LA Add-On HTTP error: ${addOnResponse.status}`);
          return addOnResponse.json();
        })(),
      ]);

      console.log(`All fetches completed in ${((Date.now() - startTime) / 1000).toFixed(1)}s`);

      // Process main data (required)
      if (sfResult.status === 'rejected') {
        throw sfResult.reason;
      }
      const parsedData = parseCSV(sfResult.value.csvText);
      console.log(`Parsed ${parsedData.length} rows from ${sfResult.value.source}`);

      // Process forecast (optional)
      let parsedForecast = [];
      if (forecastResult.status === 'fulfilled') {
        parsedForecast = forecastResult.value;
        console.log('Parsed forecast rows:', parsedForecast.length);
      } else {
        console.warn('Forecast data unavailable:', forecastResult.reason?.message);
      }

      // Process evaluations (optional)
      let evalCount = 0;
      if (evalResult.status === 'fulfilled') {
        evalCount = evalResult.value.length;
        setEvaluationData(evalResult.value);
        console.log('Currently in evaluations:', evalCount);
      } else {
        console.warn('Evaluation data unavailable:', evalResult.reason?.message);
      }

      // Field Experience hire cohorts (optional)
      let feHiresData = null;
      if (feHiresResult.status === 'fulfilled' && !feHiresResult.value?.error) {
        feHiresData = feHiresResult.value;
        setFeHires(feHiresData);
        console.log('FE hires loaded:', feHiresData.totals);
      } else {
        console.warn('FE hires unavailable:', feHiresResult.reason?.message || feHiresResult.value?.error);
      }

      // Louisiana Add-On program (optional)
      let addOn = null;
      if (addOnResult.status === 'fulfilled' && !addOnResult.value?.error) {
        addOn = addOnResult.value;
        setAddOnData(addOn);
        console.log('LA Add-On loaded:', addOn.totals);
      } else {
        console.warn('LA Add-On unavailable:', addOnResult.reason?.message || addOnResult.value?.error);
      }

      setRawData(parsedData);
      setForecastData(parsedForecast);

      // Use ref to get current selectedState (avoids stale closure in setInterval)
      const currentState = selectedStateRef.current;
      const calculatedMetrics = calculateMetrics(parsedData, currentState, parsedForecast);
      calculatedMetrics.currentlyInEvaluations = evalCount;
      applyFeHires(calculatedMetrics, currentState, feHiresData);
      applyAddOns(calculatedMetrics, currentState, addOn, parsedData);
      console.log('Calculated metrics:', calculatedMetrics);

      setMetrics(calculatedMetrics);
      setLastUpdate(new Date());

      // Generate AI summary with loaded data (non-blocking)
      generateAISummary(parsedData, calculatedMetrics);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error loading data: ' + error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(true);
    const interval = setInterval(() => fetchData(false), 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track page view when dashboard data loads
  useEffect(() => {
    if (metrics && authUser?.email) {
      trackActivity('page_view', { state: selectedState });
    }
    // Only fire on initial load, not on every metrics change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!metrics, authUser?.email]);

  // Fetch AI summary from stored global summary (GET only)
  // Generate AI summary from loaded data
  const generateAISummary = async (rawData, metrics) => {
    try {
      setNarrativeLoading(true);
      
      // Build snapshot
      const snapshot = buildKPISnapshot(rawData, metrics);
      
      // Volume guardrails
      if (snapshot.apps_last_30d < 300 || snapshot.enroll_last_30d < 100 || snapshot.matured_cohort_apps < 200) {
        setAiNarrative({ 
          summary: 'Insufficient volume for a stable summary.',
          generatedAt: new Date().toISOString(),
          isFallback: true 
        });
        setNarrativeLoading(false);
        return;
      }

      // Call AI endpoint with snapshot
      const response = await fetch('/.netlify/functions/kpi-summary-regenerate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': 'PUBLIC_GENERATE' // We'll update the function to allow this
        },
        body: JSON.stringify(snapshot)
      });

      if (response.ok) {
        const data = await response.json();
        setAiNarrative({
          summary: data.summary,
          generatedAt: data.generated_at,
          isFallback: false
        });
      } else {
        setAiNarrative({ summary: "Summary generation failed.", isFallback: true });
      }
    } catch (error) {
      console.error('Error generating AI summary:', error);
      setAiNarrative({ summary: "Summary unavailable.", isFallback: true });
    } finally {
      setNarrativeLoading(false);
    }
  };

  // Build KPI snapshot for AI - LIMITED TO LAST 12 MONTHS
  const buildKPISnapshot = (rawData, metrics) => {
    const now = new Date();
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    
    // Filter to last 12 months only
    const last12MonthsData = rawData.filter(r => {
      if (!r.Applied) return false;
      const appDate = new Date(r.Applied);
      return appDate >= twelveMonthsAgo && appDate <= now;
    });
    
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const sixtyDaysAgo = new Date(now);
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    const oneDayAgo = new Date(now);
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    const last30d = last12MonthsData.filter(r => r.Applied && new Date(r.Applied) >= thirtyDaysAgo);
    const prev30d = last12MonthsData.filter(r => {
      if (!r.Applied) return false;
      const date = new Date(r.Applied);
      return date >= sixtyDaysAgo && date < thirtyDaysAgo;
    });

    const apps_last_30d = last30d.length;
    const apps_prev_30d = prev30d.length;
    const apps_pct_change = apps_prev_30d > 0 ? ((apps_last_30d - apps_prev_30d) / apps_prev_30d * 100).toFixed(1) : 0;
    const enroll_last_30d = last30d.filter(r => r.Enrolled).length;
    const enroll_prev_30d = prev30d.filter(r => r.Enrolled).length;
    const enroll_pct_change = enroll_prev_30d > 0 ? ((enroll_last_30d - enroll_prev_30d) / enroll_prev_30d * 100).toFixed(1) : 0;

    const stateApps = {}, stateEnroll = {};
    last30d.forEach(r => {
      const state = r['Certification state'];
      if (!state) return;
      stateApps[state] = (stateApps[state] || 0) + 1;
      if (r.Enrolled) stateEnroll[state] = (stateEnroll[state] || 0) + 1;
    });

    const top_states_apps = Object.entries(stateApps).sort((a,b) => b[1]-a[1]).slice(0,3).map(([state, count]) => ({state, count}));
    const top_states_enroll = Object.entries(stateEnroll).sort((a,b) => b[1]-a[1]).slice(0,3).map(([state, count]) => ({state, count}));
    const top_state_share_apps = apps_last_30d > 0 && top_states_apps[0] ? ((top_states_apps[0].count / apps_last_30d) * 100).toFixed(1) : null;

    const maturedCohort = last12MonthsData.filter(r => r.Applied && new Date(r.Applied) >= sixtyDaysAgo && new Date(r.Applied) < thirtyDaysAgo);
    const matured_cohort_apps = maturedCohort.length;
    const matured_enrolled = maturedCohort.filter(r => r.Enrolled).length;
    const matured_conv_30d = matured_cohort_apps > 0 ? ((matured_enrolled / matured_cohort_apps) * 100).toFixed(1) : 0;

    const ninetyDaysAgo = new Date(now); ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const priorMaturedCohort = last12MonthsData.filter(r => r.Applied && new Date(r.Applied) >= ninetyDaysAgo && new Date(r.Applied) < sixtyDaysAgo);
    const prior_matured_enrolled = priorMaturedCohort.filter(r => r.Enrolled).length;
    const prior_matured_conv_30d = priorMaturedCohort.length > 0 ? ((prior_matured_enrolled / priorMaturedCohort.length) * 100).toFixed(1) : null;

    const maturedWithEnrollment = maturedCohort.filter(r => r.Enrolled && r.Applied);
    const maturedDays = maturedWithEnrollment.map(r => Math.max(0, Math.floor((new Date(r.Enrolled) - new Date(r.Applied)) / 86400000))).sort((a,b) => a-b);
    const matured_median_days_to_enroll = maturedDays.length > 0 ? maturedDays[Math.floor(maturedDays.length/2)] : null;

    const priorMaturedWithEnrollment = priorMaturedCohort.filter(r => r.Enrolled && r.Applied);
    const priorMaturedDays = priorMaturedWithEnrollment.map(r => Math.max(0, Math.floor((new Date(r.Enrolled) - new Date(r.Applied)) / 86400000))).sort((a,b) => a-b);
    const prior_matured_median_days_to_enroll = priorMaturedDays.length > 0 ? priorMaturedDays[Math.floor(priorMaturedDays.length/2)] : null;

    const last24h = last12MonthsData.filter(r => r.Applied && new Date(r.Applied) >= oneDayAgo);
    const notable_24h_movement = last24h.length >= 10;
    let notable_24h_driver_state = null, notable_24h_impact = null, notable_24h_reason = null;
    
    if (notable_24h_movement) {
      const state24h = {};
      last24h.forEach(r => { if (r['Certification state']) state24h[r['Certification state']] = (state24h[r['Certification state']] || 0) + 1; });
      const topState24h = Object.entries(state24h).sort((a,b) => b[1]-a[1])[0];
      if (topState24h && topState24h[1] >= 5) {
        notable_24h_driver_state = topState24h[0];
        notable_24h_impact = `${topState24h[1]} applications`;
        notable_24h_reason = 'high activity';
      }
    }

    return {
      as_of_date: now.toISOString().split('T')[0],
      apps_last_30d, apps_prev_30d, apps_pct_change: parseFloat(apps_pct_change),
      enroll_last_30d, enroll_prev_30d, enroll_pct_change: parseFloat(enroll_pct_change),
      top_states_apps, top_states_enroll, top_state_share_apps: top_state_share_apps ? parseFloat(top_state_share_apps) : null,
      matured_cohort_label: '30-60 days ago', matured_cohort_apps,
      matured_conv_30d: parseFloat(matured_conv_30d),
      prior_matured_conv_30d: prior_matured_conv_30d ? parseFloat(prior_matured_conv_30d) : null,
      matured_median_days_to_enroll, prior_matured_median_days_to_enroll,
      enroll_mtd: metrics?.enrollmentsMTD || null,
      mtd_enroll_target: metrics?.forecastEnrollmentsMTD || null,
      mtd_pacing_pct: metrics?.forecastEnrollmentsVariance || null,
      notable_24h_movement, notable_24h_driver_state, notable_24h_reason, notable_24h_impact,
      // Forecast Readiness
      forecast_readiness: metrics?.forecastReadiness ? {
        forecast_target_3mo: metrics.forecastReadiness.forecastTarget,
        pipeline_projection: metrics.forecastReadiness.projectedEnrollments,
        projected_from_pipeline: metrics.forecastReadiness.projectedFromPipeline,
        projected_from_future_apps: metrics.forecastReadiness.projectedFromFutureApps,
        pipeline_on_track: metrics.forecastReadiness.onTrack,
        gap: metrics.forecastReadiness.gap,
        conversion_rate_6mo: metrics.forecastReadiness.historicalConversionRate,
        realtime_rate: metrics.forecastReadiness.runRate?.adjustedRate,
        realtime_pace_on_track: metrics.forecastReadiness.runRate?.onTrack,
        realtime_projection_3mo: metrics.forecastReadiness.runRate?.projection3Mo,
        pace_ratio: metrics.forecastReadiness.runRate?.paceRatio,
        active_pipeline: metrics.forecastReadiness.pipeline?.total,
        stage_rates: metrics.forecastReadiness.stageRates,
        prior_stage_rates: metrics.forecastReadiness.priorStageRates,
        at_risk_states: metrics.forecastReadiness.atRiskStates?.slice(0, 3),
      } : null
    };
  };

  // Fetch AI-generated narrative
  const fetchNarrative = async () => {
    try {
      setNarrativeLoading(true);
      
      // Create abort controller with 30 second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // Call the working function directly
      const response = await fetch('/.netlify/functions/generate-narrative', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Narrative loaded:', data);
        setAiNarrative(data);
      } else {
        console.error('Narrative fetch failed:', response.status);
        setAiNarrative({
          narrative: "AI insights will be generated daily at 6 AM CT. The first generation may take up to 30 seconds.",
          isFallback: true
        });
      }
    } catch (error) {
      console.error('Error fetching narrative:', error);
      if (error.name === 'AbortError') {
        console.log('Narrative fetch timed out after 30s');
      }
      setAiNarrative({
        narrative: "AI insights are being generated. This may take up to 30 seconds on first load. Please refresh in a moment.",
        isFallback: true
      });
    } finally {
      setNarrativeLoading(false);
    }
  };

  // Recalculate metrics when state filter (or FE hire data) changes
  useEffect(() => {
    if (rawData.length > 0) {
      const calculatedMetrics = calculateMetrics(rawData, selectedState, forecastData);
      calculatedMetrics.currentlyInEvaluations = evaluationData.length;
      applyFeHires(calculatedMetrics, selectedState, feHires);
      applyAddOns(calculatedMetrics, selectedState, addOnData, rawData);
      console.log('Recalculated metrics for state:', selectedState);
      setMetrics(calculatedMetrics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, feHires, addOnData]);

  // Fetch course duration data when tab is activated
  const fetchDurationData = async () => {
    if (durationData) return;
    try {
      setDurationLoading(true);
      setDurationError(null);
      const response = await fetch('/.netlify/functions/course-duration');
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      const data = await response.json();
      setDurationData(data);
    } catch (err) {
      console.error('Duration fetch failed:', err);
      setDurationError(err.message);
    } finally {
      setDurationLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'duration') {
      fetchDurationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const generateNarrative = () => {
    if (!metrics) return '';

    const { currentSemester, currentRank, totalSemesters, enrollmentRate, completionRate, totalApplications, enrolled } = metrics;

    if (!currentSemester) {
      return `We've processed ${totalApplications} total applications with an enrollment rate of ${enrollmentRate}%. Of those enrolled, ${completionRate}% have successfully entered the program.`;
    }

    const semesterName = currentSemester.semester.replace('*', '');
    return `${semesterName} shows ${currentSemester.students} students enrolled, ranking #${currentRank} of ${totalSemesters} semesters all-time. Our current enrollment conversion rate stands at ${enrollmentRate}%, with ${enrolled} students enrolled from ${totalApplications} total applications. The program completion rate for enrolled students is ${completionRate}%, demonstrating strong student commitment and program quality.`;
  };

  const handleAccessSubmit = () => {
    if (accessCode.toLowerCase() === 'gomeangreen') {
      setAccessGranted(true);
      setAccessError(false);
      const guestUser = { email: 'guest@accesscode', name: 'Access Code User', picture: null };
      setAuthUser(guestUser);
      logActivity('guest@accesscode', 'login_access_code', {});
    } else {
      setAccessError(true);
      setAccessCode('');
    }
  };

  if (!accessGranted || loading) {
    return (
      <div className="loading-container">
        <div className="loading-inner">
          {/* Butterfly animation - always playing on loop */}
          <ButterflyLoader 
            animate={true}
            sizePx={220}
            durationMs={2600}
            staggerMs={160}
            floatAfterAssemble={true}
            style={{ marginBottom: '0rem' }}
          />
          
          {/* Company Logo */}
          <img
            src={`${process.env.PUBLIC_URL}/iteach-only.png`}
            alt="iTeach"
            className="loading-logo"
            style={{ marginBottom: '0.5rem' }}
          />
          
          {!accessGranted ? (
            <div className="access-gate">
              {GOOGLE_CLIENT_ID && (
                <>
                  <div id="google-signin-btn" ref={(el) => { if (el) renderGoogleButton('google-signin-btn'); }} />
                  {authLoading && <p className="loading-tagline" style={{ fontSize: '0.85rem' }}>Verifying access...</p>}
                  {authError && <p className="access-error-text">{authError}</p>}
                  <div className="access-divider">
                    <span className="access-divider-line"></span>
                    <span className="access-divider-text">or</span>
                    <span className="access-divider-line"></span>
                  </div>
                </>
              )}
              <input
                type="password"
                className={`access-input${accessError ? ' access-input-error' : ''}`}
                placeholder="Enter access code"
                value={accessCode}
                onChange={(e) => { setAccessCode(e.target.value); setAccessError(false); }}
                onKeyDown={(e) => e.key === 'Enter' && handleAccessSubmit()}
                autoFocus={!GOOGLE_CLIENT_ID}
              />
              {accessError && <p className="access-error-text">Invalid access code</p>}
              <button className="access-button" onClick={handleAccessSubmit}>Access</button>
            </div>
          ) : (
            <p className="loading-tagline">Gathering data...</p>
          )}
        </div>
        <div style={{ position: 'fixed', bottom: '1rem', right: '1.5rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontWeight: '400' }}>
          Version 2.1.9
        </div>
      </div>
    );
  }

  const stateData = metrics ? Object.entries(metrics.stateBreakdown).map(([state, count]) => ({
    name: state,
    value: count
  })) : [];

  // Get unique states from raw data - filter to actual US states only
  const US_STATES = new Set([
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming', 'District of Columbia'
  ]);
  const availableStates = ['All States', ...new Set(
    rawData.map(r => r['Certification state']).filter(s => s && US_STATES.has(s)).sort()
  )];

  const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444'];

  // Enhanced summary text formatting
  const enhanceSummaryText = (summary) => {
    if (!summary) return '';
    
    // Bold all numbers (with commas, percentages, etc.) - NO color change, NO size change
    let enhanced = summary.replace(/(\d{1,3}(,\d{3})*(\.\d+)?%?)/g, '<strong class="number-highlight">$1</strong>');
    
    // Add structure indicators for key phrases
    enhanced = enhanced.replace(/(Last \d+ days:|Notable:|However,|Additionally,)/gi, '<span class="key-phrase">$1</span>');
    
    // Highlight action items if present
    enhanced = enhanced.replace(/(Action:|Focus:|Monitor:|Watch:)/gi, '<strong class="action-highlight">$1</strong>');
    
    return enhanced;
  };

  // Generate focus directive based on current metrics
  const generateFocusDirective = () => {
    if (!metrics || !rawData) return null;
    
    const appsGrowth = metrics.applicationsYoYGrowth || 0;
    const enrollGrowth = metrics.enrollmentsYoYGrowth || 0;
    const appsForecast = metrics.forecastVariance || 0;
    const enrollForecast = metrics.forecastEnrollmentsVariance || 0;
    
    const directives = [];
    
    // Enrollment issues
    if (enrollForecast < -10) {
      directives.push('Consider increasing enrollment outreach activity');
    } else if (enrollGrowth < -5) {
      directives.push('Review enrollment conversion patterns');
    }
    
    // Application issues
    if (appsForecast < -10) {
      directives.push('Boost pipeline generation activities');
    } else if (appsGrowth < -5) {
      directives.push('Increase marketing and recruitment efforts');
    }
    
    // Funnel tension
    if (appsGrowth > 5 && enrollGrowth < -5) {
      directives.push('Investigate gap between applications and enrollments');
    }
    
    // State concentration risk - use LAST 12 MONTHS data only
    const now = new Date();
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    
    // Filter to last 12 months
    const last12MonthsData = rawData.filter(r => {
      if (!r.Applied) return false;
      const appDate = new Date(r.Applied);
      return appDate >= twelveMonthsAgo && appDate <= now;
    });
    
    // Calculate state breakdown for last 12 months
    const stateApps12M = {};
    last12MonthsData.forEach(r => {
      const state = r['Certification state'];
      if (state) {
        stateApps12M[state] = (stateApps12M[state] || 0) + 1;
      }
    });
    
    const total12M = last12MonthsData.length;
    const topStates12M = Object.entries(stateApps12M)
      .map(([state, count]) => ({
        state,
        count,
        percentage: total12M > 0 ? (count / total12M) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
    
    // Only flag if a single state is >30% of volume
    if (topStates12M.length > 0 && topStates12M[0].percentage > 30) {
      directives.push(`Monitor ${topStates12M[0].state} concentration (${topStates12M[0].percentage.toFixed(0)}% of volume)`);
    }
    
    // If everything is good
    if (directives.length === 0) {
      if (appsGrowth > 10 && enrollGrowth > 10) {
        directives.push('Maintain current momentum, ensure capacity to handle growth');
      } else {
        directives.push('Continue monitoring key metrics, maintain current strategies');
      }
    }
    
    // Return up to 3 directives
    return directives.slice(0, 3).join('; ');
  };

  // Generate focus directive for Applications Age card
  const generateApplicationsAgeFocus = () => {
    if (!metrics?.applicationsAgeFunnel) return null;
    
    const { under30, days31to60, days61to90, over90 } = metrics.applicationsAgeFunnel;
    const total = (under30 || 0) + (days31to60 || 0) + (days61to90 || 0) + (over90 || 0);
    
    if (total === 0) return null;
    
    const over90Pct = ((over90 || 0) / total) * 100;
    const days61to90Pct = ((days61to90 || 0) / total) * 100;
    const stalePct = over90Pct + days61to90Pct;
    
    // High risk: >40% of apps are 60+ days old
    if (stalePct > 40) {
      return `${stalePct.toFixed(0)}% of applications are 60+ days old — outreach to aging pipeline may help improve conversion`;
    }
    
    // Medium risk: >25% are 90+ days old
    if (over90Pct > 25) {
      return `${over90Pct.toFixed(0)}% of applications are 90+ days old - risk of abandonment is high`;
    }
    
    // Medium risk: >30% are 60+ days old
    if (stalePct > 30) {
      return `${stalePct.toFixed(0)}% of applications are 60+ days old — ongoing conversion efforts on older applications could help`;
    }
    
    // Positive: Most apps are fresh
    if ((under30 || 0) / total > 0.6) {
      return `Pipeline is fresh with ${((under30 || 0) / total * 100).toFixed(0)}% of applications under 30 days old`;
    }
    
    return null;
  };

  // Generate focus directive for Performance Summary
  const generateForecastFocus = () => {
    if (!metrics) return null;
    const fr = metrics.forecastReadiness;
    if (!fr || fr.forecastTarget === 0) return null;
    
    // Check if run rate diverges significantly from pipeline model
    const runRateDiverges = fr.runRate && !fr.runRate.onTrack && fr.onTrack;
    const bothShort = fr.runRate && !fr.runRate.onTrack && !fr.onTrack;
    
    if (runRateDiverges) {
      return `Pipeline model shows on track, but real-time conversion rate (${fr.runRate.adjustedRate}%) is below 6mo rate (${fr.historicalConversionRate}%). At current pace, projecting ${fr.runRate.projection3Mo.toLocaleString()} vs ${fr.forecastTarget.toLocaleString()} target — monitor closely.`;
    }
    
    if (fr.onTrack && !runRateDiverges) {
      return `Pipeline is on track to meet the next 3-month forecast. Projected ${fr.projectedEnrollments.toLocaleString()} enrollments against a target of ${fr.forecastTarget.toLocaleString()}.`;
    }
    
    if (bothShort) {
      return `Both pipeline model (${fr.projectedEnrollments.toLocaleString()}) and actual pace (${fr.runRate.projection3Mo.toLocaleString()}) are below the ${fr.forecastTarget.toLocaleString()} target. Gap of ${fr.gap.toLocaleString()} — may benefit from additional focus.`;
    }
    
    const stages = [
      { label: 'Applied→Evaluated', cur: parseFloat(fr.stageRates.applyToEval), prev: parseFloat(fr.priorStageRates.applyToEval) },
      { label: 'Evaluated→Accepted', cur: parseFloat(fr.stageRates.evalToAccept), prev: parseFloat(fr.priorStageRates.evalToAccept) },
      { label: 'Accepted→Enrolled', cur: parseFloat(fr.stageRates.acceptToEnroll), prev: parseFloat(fr.priorStageRates.acceptToEnroll) },
    ];
    const declining = stages.filter(s => s.cur < s.prev - 1);
    
    if (declining.length > 0) {
      const worst = declining.sort((a, b) => (a.cur - a.prev) - (b.cur - b.prev))[0];
      return `Gap of ${fr.gap.toLocaleString()} enrollments vs forecast. ${worst.label} conversion dropped ${(worst.prev - worst.cur).toFixed(1)}pp — prioritize this stage to close the gap.`;
    }
    
    return `Gap of ${fr.gap.toLocaleString()} enrollments vs 3-month forecast. At current conversion (${fr.historicalConversionRate}%), ${fr.prescriptionA.additionalAppsNeeded.toLocaleString()} additional applications are needed.`;
  };

  const generatePerformanceFocus = () => {
    if (!metrics) return null;
    
    const appsGrowth = metrics.applicationsYoYGrowth || 0;
    const enrollGrowth = metrics.enrollmentsYoYGrowth || 0;
    const appsForecast = metrics.forecastVariance || 0;
    const enrollForecast = metrics.forecastEnrollmentsVariance || 0;
    
    // CRITICAL: Apps growing but enrollments declining (misalignment)
    if (appsGrowth > 5 && enrollGrowth < -5) {
      return `Applications up ${appsGrowth.toFixed(0)}% while enrollments are down ${Math.abs(enrollGrowth).toFixed(0)}% — worth reviewing conversion funnel`;
    }
    
    // HIGH RISK: Both declining
    if (appsGrowth < -10 && enrollGrowth < -10) {
      return `Both applications and enrollments trending down — consider reviewing pipeline and outreach strategies`;
    }
    
    // MEDIUM RISK: Enrollments declining while apps flat/positive
    if (enrollGrowth < -10 && appsGrowth > -5) {
      return `Enrollments down ${Math.abs(enrollGrowth).toFixed(0)}% with stable applications — conversion rates may be a factor`;
    }
    
    // MEDIUM RISK: Applications declining while enrolls flat/positive
    if (appsGrowth < -10 && enrollGrowth > -5) {
      return `Applications down ${Math.abs(appsGrowth).toFixed(0)}% — pipeline generation could help maintain enrollment levels`;
    }
    
    // WARNING: Behind forecast significantly
    if (enrollForecast < -15) {
      return `Enrollments currently ${Math.abs(enrollForecast).toFixed(0)}% below forecast — additional outreach may help close the gap`;
    }
    
    // POSITIVE: Both growing strongly
    if (appsGrowth > 10 && enrollGrowth > 10) {
      return `Strong growth in both applications (+${appsGrowth.toFixed(0)}%) and enrollments (+${enrollGrowth.toFixed(0)}%) - maintain momentum`;
    }
    
    // POSITIVE: Aligned growth
    if (appsGrowth > 0 && enrollGrowth > 0 && Math.abs(appsGrowth - enrollGrowth) < 10) {
      return `Applications and enrollments growing in alignment - healthy funnel performance`;
    }
    
    return null;
  };

  // PDF Export Function
  const exportPDF = () => {
    // Load logo image first, then generate PDF
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.onload = () => {
      // Convert logo to base64 via canvas
      const canvas = document.createElement('canvas');
      canvas.width = logoImg.naturalWidth;
      canvas.height = logoImg.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(logoImg, 0, 0);
      const logoBase64 = canvas.toDataURL('image/png');
      
      generatePDF(logoBase64);
    };
    logoImg.onerror = () => {
      // Generate without logo if it fails to load
      generatePDF(null);
    };
    logoImg.src = `${process.env.PUBLIC_URL}/iteach-logo.png`;
  };

  const generatePDF = (logoBase64) => {
    const doc = new jsPDF('p', 'mm', 'letter');
    const pageW = 215.9;
    const pageH = 279.4;
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = margin;

    // Colors
    const teal = [0, 190, 168];
    const darkBg = [30, 27, 75];
    const green = [75, 212, 141];
    const red = [255, 107, 107];
    const gray = [120, 120, 140];
    const white = [255, 255, 255];

    const sign = (v) => (v >= 0 ? '+' : '') + v;
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const monthName = now.toLocaleDateString('en-US', { month: 'long' });

    // --- HEADER ---
    doc.setFillColor(...darkBg);
    doc.rect(0, 0, pageW, 22, 'F');

    // Logo
    if (logoBase64) {
      // Logo aspect ratio ~3.44:1 (1808x526), fit in header
      const logoH = 10;
      const logoW = logoH * (1808 / 526);
      doc.addImage(logoBase64, 'PNG', margin, 6, logoW, logoH);
    } else {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...white);
      doc.text('iTeach', margin, 14);
    }

    // Date and filter right-aligned
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(200, 200, 220);
    doc.text(`Generated: ${dateStr}`, pageW - margin, 10, { align: 'right' });
    doc.setFontSize(8);
    doc.text(`Filter: ${selectedState}`, pageW - margin, 16, { align: 'right' });

    y = 28;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 60);
    doc.text('Enrollment Pipeline Report', margin, y);
    y += 3;

    // Accent line
    doc.setDrawColor(...teal);
    doc.setLineWidth(0.8);
    doc.line(margin, y, margin + 60, y);
    y += 6;

    // --- DYNAMIC SUMMARY ---
    const appsGrowth = metrics?.applicationsYoYGrowth || 0;
    const enrollGrowth = metrics?.enrollmentsYoYGrowth || 0;
    const appsForecast = metrics?.forecastVariance || 0;
    const enrollForecast = metrics?.forecastEnrollmentsVariance || 0;
    const appRank = metrics?.appPacingRank || null;
    const enrollRank = metrics?.enrollmentPacingRank || null;
    const appTotal = metrics?.appPacingTotal || 0;
    const enrollTotal = metrics?.enrollmentPacingTotal || 0;
    const hiresGrowth = metrics?.springHiresGrowth || 0;
    const placedTeachers = metrics?.currentlyPlacedTeachers || 0;

    // Build summary sentences
    let summaryParts = [];

    // Opening: month momentum
    const bothGrowing = appsGrowth > 0 && enrollGrowth > 0;
    const bothDecline = appsGrowth < 0 && enrollGrowth < 0;
    if (bothGrowing && appsGrowth > 15 && enrollGrowth > 15) {
      summaryParts.push(`${monthName} is off to a strong start with Applications (${sign(appsGrowth)}% YoY) and Enrollments (${sign(enrollGrowth)}% YoY) both showing robust growth.`);
    } else if (bothGrowing) {
      const stronger = appsGrowth > enrollGrowth ? 'Applications' : 'Enrollments';
      const weaker = appsGrowth > enrollGrowth ? 'Enrollments' : 'Applications';
      const strongerVal = appsGrowth > enrollGrowth ? appsGrowth : enrollGrowth;
      const weakerVal = appsGrowth > enrollGrowth ? enrollGrowth : appsGrowth;
      summaryParts.push(`${monthName} is off to a good start with ${stronger} leading at ${sign(strongerVal)}% YoY, and ${weaker} not far behind at ${sign(weakerVal)}%.`);
    } else if (bothDecline) {
      summaryParts.push(`${monthName} is showing softness with Applications (${sign(appsGrowth)}%) and Enrollments (${sign(enrollGrowth)}%) both declining year-over-year.`);
    } else {
      const upMetric = appsGrowth >= 0 ? 'Applications' : 'Enrollments';
      const upVal = appsGrowth >= 0 ? appsGrowth : enrollGrowth;
      const downMetric = appsGrowth >= 0 ? 'Enrollments' : 'Applications';
      const downVal = appsGrowth >= 0 ? enrollGrowth : appsGrowth;
      summaryParts.push(`${monthName} shows a mixed picture: ${upMetric} are up ${sign(upVal)}% YoY while ${downMetric} are at ${sign(downVal)}%.`);
    }

    // Pacing rank
    if (appRank && enrollRank) {
      if (appRank === 1 && enrollRank === 1) {
        summaryParts.push(`Both are on pace to be the best ${monthName} on record (Rank #1 of ${appTotal} years in Applications and Enrollments).`);
      } else if (appRank === 1) {
        summaryParts.push(`Applications are on pace for the best ${monthName} ever (Rank #1 of ${appTotal}), while Enrollments rank #${enrollRank} of ${enrollTotal}.`);
      } else if (enrollRank === 1) {
        summaryParts.push(`Enrollments are on pace for the best ${monthName} ever (Rank #1 of ${enrollTotal}), with Applications at #${appRank} of ${appTotal}.`);
      } else if (appRank <= 3 && enrollRank <= 3) {
        summaryParts.push(`Pacing is strong with Applications ranked #${appRank} and Enrollments #${enrollRank} among all ${monthName}s on record.`);
      } else {
        summaryParts.push(`Current pacing puts Applications at #${appRank} of ${appTotal} and Enrollments at #${enrollRank} of ${enrollTotal} among all ${monthName}s.`);
      }
    }

    // Forecast
    const bothBehind = appsForecast < 0 && enrollForecast < 0;
    const bothAhead = appsForecast >= 0 && enrollForecast >= 0;
    if (bothBehind) {
      if (appsForecast < -10 || enrollForecast < -10) {
        summaryParts.push(`Despite YoY growth, both Applications (${sign(appsForecast)}%) and Enrollments (${sign(enrollForecast)}%) are trailing forecast, signaling a need for increased focus in upcoming weeks.`);
      } else {
        summaryParts.push(`Both metrics are slightly behind forecast (Applications ${sign(appsForecast)}%, Enrollments ${sign(enrollForecast)}%) — worth monitoring but not yet a concern.`);
      }
    } else if (bothAhead) {
      summaryParts.push(`Both Applications (${sign(appsForecast)}%) and Enrollments (${sign(enrollForecast)}%) are ahead of forecast — strong execution by the team.`);
    } else {
      const behind = appsForecast < 0 ? 'Applications' : 'Enrollments';
      const behindVal = appsForecast < 0 ? appsForecast : enrollForecast;
      summaryParts.push(`${behind} are ${sign(behindVal)}% vs forecast and may need additional attention.`);
    }

    // Hires callout
    if (hiresGrowth > 0) {
      summaryParts.push(`Spring hires are up ${sign(hiresGrowth)}% with ${placedTeachers.toLocaleString()} teachers currently placed for the 2025-26 school year.`);
    } else if (hiresGrowth < 0) {
      summaryParts.push(`Spring hires are down ${sign(hiresGrowth)}% year-over-year with ${placedTeachers.toLocaleString()} teachers currently placed.`);
    }

    const summaryText = summaryParts.join(' ');

    // Render summary with heading
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 60);
    doc.text('Executive Summary', margin, y);
    y += 4;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 70);
    
    doc.setFillColor(245, 247, 255);
    const summaryLines = doc.splitTextToSize(summaryText, contentW - 6);
    const summaryH = summaryLines.length * 4.2 + 5;
    doc.roundedRect(margin, y, contentW, summaryH, 2, 2, 'F');
    
    let sy = y + 4.5;
    summaryLines.forEach(line => {
      doc.text(line, margin + 3, sy);
      sy += 4.2;
    });
    y += summaryH + 4;

    // --- KPI SECTION: 3 columns ---
    const colW = contentW / 3;
    const kpiStartY = y;

    const drawKpiColumn = (title, data, colIndex, ytdLabel, mtdLabel) => {
      const x = margin + colIndex * colW;
      let cy = kpiStartY;

      doc.setFillColor(...teal);
      doc.rect(x, cy, colW - 4, 6, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...white);
      doc.text(title, x + (colW - 4) / 2, cy + 4.2, { align: 'center' });
      cy += 9;

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 100);
      doc.text(mtdLabel || 'MONTH TO DATE', x + 1, cy);
      cy += 4;

      data.mtd.forEach(item => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 100, 120);
        doc.text(item.label, x + 1, cy);
        const valColor = item.isGrowth ? (item.value >= 0 ? green : red) : [40, 40, 60];
        doc.setTextColor(...valColor);
        doc.setFont('helvetica', 'bold');
        doc.text(item.display, x + colW - 6, cy, { align: 'right' });
        cy += 3.8;
      });

      cy += 1.5;

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 100);
      doc.setFontSize(7.5);
      doc.text(ytdLabel || 'YEAR TO DATE', x + 1, cy);
      cy += 4;

      data.ytd.forEach(item => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 100, 120);
        doc.text(item.label, x + 1, cy);
        const valColor = item.isGrowth ? (item.value >= 0 ? green : red) : [40, 40, 60];
        doc.setTextColor(...valColor);
        doc.setFont('helvetica', 'bold');
        doc.text(item.display, x + colW - 6, cy, { align: 'right' });
        cy += 3.8;
      });

      return cy;
    };

    const appsData = {
      mtd: [
        { label: 'Actual', display: (metrics?.applicationsMTD || 0).toLocaleString(), value: 0, isGrowth: false },
        { label: 'YoY Growth', display: `${sign(metrics?.applicationsYoYGrowth || 0)}% (${(metrics?.applicationsMTDLastYear || 0).toLocaleString()})`, value: metrics?.applicationsYoYGrowth || 0, isGrowth: true },
        { label: 'vs Forecast', display: `${sign(metrics?.forecastVariance || 0)}% (${(metrics?.forecastMTD || 0).toLocaleString()})`, value: metrics?.forecastVariance || 0, isGrowth: true },
      ],
      ytd: [
        { label: 'Actual', display: (metrics?.applicationsYTD || 0).toLocaleString(), value: 0, isGrowth: false },
        { label: 'YoY Growth', display: `${sign(metrics?.applicationsYTDGrowth || 0)}% (${(metrics?.applicationsYTDLastYear || 0).toLocaleString()})`, value: metrics?.applicationsYTDGrowth || 0, isGrowth: true },
        { label: 'vs Forecast', display: `${sign(metrics?.forecastYTDVariance || 0)}% (${(metrics?.forecastYTD || 0).toLocaleString()})`, value: metrics?.forecastYTDVariance || 0, isGrowth: true },
      ]
    };

    const enrollData = {
      mtd: [
        { label: 'Actual', display: (metrics?.enrollmentsMTD || 0).toLocaleString(), value: 0, isGrowth: false },
        { label: 'YoY Growth', display: `${sign(metrics?.enrollmentsYoYGrowth || 0)}% (${(metrics?.enrollmentsMTDLastYear || 0).toLocaleString()})`, value: metrics?.enrollmentsYoYGrowth || 0, isGrowth: true },
        { label: 'vs Forecast', display: `${sign(metrics?.forecastEnrollmentsVariance || 0)}% (${(metrics?.forecastEnrollmentsMTD || 0).toLocaleString()})`, value: metrics?.forecastEnrollmentsVariance || 0, isGrowth: true },
      ],
      ytd: [
        { label: 'Actual', display: (metrics?.enrollmentsYTD || 0).toLocaleString(), value: 0, isGrowth: false },
        { label: 'YoY Growth', display: `${sign(metrics?.enrollmentsYTDGrowth || 0)}% (${(metrics?.enrollmentsYTDLastYear || 0).toLocaleString()})`, value: metrics?.enrollmentsYTDGrowth || 0, isGrowth: true },
        { label: 'vs Forecast', display: `${sign(metrics?.forecastEnrollmentsYTDVariance || 0)}% (${(metrics?.forecastEnrollmentsYTD || 0).toLocaleString()})`, value: metrics?.forecastEnrollmentsYTDVariance || 0, isGrowth: true },
      ]
    };

    const hiresData = {
      mtd: [
        { label: 'Fall 2026', display: (metrics?.fall2026Hires || 0).toLocaleString(), value: 0, isGrowth: false },
        { label: 'YoY Growth', display: `${sign(metrics?.fallHiresGrowth || 0)}%`, value: metrics?.fallHiresGrowth || 0, isGrowth: true },
        { label: 'Fall 2025', display: (metrics?.fall2025Hires || 0).toLocaleString(), value: 0, isGrowth: false },
      ],
      ytd: [
        { label: 'Placed Teachers', display: (metrics?.currentlyPlacedTeachers || 0).toLocaleString(), value: 0, isGrowth: false },
        { label: 'YoY Growth', display: `${sign(metrics?.placedTeachersGrowth || 0)}%`, value: metrics?.placedTeachersGrowth || 0, isGrowth: true },
        { label: 'Last Year', display: (metrics?.priorYearPlacedTeachers || 0).toLocaleString(), value: 0, isGrowth: false },
      ]
    };

    const y1 = drawKpiColumn('Applications', appsData, 0);
    const y2 = drawKpiColumn('Enrollments', enrollData, 1);
    const y3 = drawKpiColumn('Hires', hiresData, 2, 'TOTAL TEACHING', 'CURRENTLY');
    y = Math.max(y1, y2, y3) + 5;

    // --- STATE PERFORMANCE TABLE ---
    doc.setDrawColor(200, 200, 210);
    doc.setLineWidth(0.3);
    doc.line(margin, y, margin + contentW, y);
    y += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 60);
    doc.text('State Performance (MTD)', margin, y);
    y += 5;

    // Calculate per-state MTD data with YoY
    const pdfNow = new Date();
    const pdfYesterday = new Date(pdfNow);
    pdfYesterday.setDate(pdfYesterday.getDate() - 1);
    const pdfYear = pdfYesterday.getFullYear();
    const pdfMonth = pdfYesterday.getMonth();

    // Same day last year
    const pdfLastYearDate = new Date(pdfYesterday);
    pdfLastYearDate.setFullYear(pdfLastYearDate.getFullYear() - 1);

    const states = [...new Set(rawData.map(r => r['Certification state']).filter(Boolean))].sort();
    
    const stateTableData = states.map(state => {
      const stateRows = rawData.filter(r => r['Certification state'] === state);
      
      // MTD Applications this year
      const appsMTD = stateRows.filter(r => {
        if (!r.Applied) return false;
        const d = new Date(r.Applied);
        return d.getFullYear() === pdfYear && d.getMonth() === pdfMonth && d <= pdfYesterday;
      }).length;
      
      // MTD Applications last year (same month, up to same day)
      const appsMTDLY = stateRows.filter(r => {
        if (!r.Applied) return false;
        const d = new Date(r.Applied);
        return d.getFullYear() === pdfYear - 1 && d.getMonth() === pdfMonth && d <= pdfLastYearDate;
      }).length;
      
      // MTD Enrollments this year
      const enrollMTD = stateRows.filter(r => {
        if (!r.Enrolled) return false;
        const d = new Date(r.Enrolled);
        return d.getFullYear() === pdfYear && d.getMonth() === pdfMonth && d <= pdfYesterday;
      }).length;
      
      // MTD Enrollments last year
      const enrollMTDLY = stateRows.filter(r => {
        if (!r.Enrolled) return false;
        const d = new Date(r.Enrolled);
        return d.getFullYear() === pdfYear - 1 && d.getMonth() === pdfMonth && d <= pdfLastYearDate;
      }).length;
      
      // MTD hires this year — Date Entered in current month
      const hiresTY = stateRows.filter(r => {
        if (!r['FE Semester'] || !r['Date Entered']) return false;
        if (!r['FE Semester'].includes('Spring 2026')) return false;
        const dateEntered = new Date(r['Date Entered']);
        return dateEntered.getFullYear() === pdfYear && dateEntered.getMonth() === pdfMonth && dateEntered <= pdfYesterday;
      }).length;

      // MTD hires last year — same month last year
      const hiresLY = stateRows.filter(r => {
        if (!r['FE Semester'] || !r['Date Entered']) return false;
        if (!r['FE Semester'].includes('Spring 2025')) return false;
        const dateEntered = new Date(r['Date Entered']);
        return dateEntered.getFullYear() === pdfYear - 1 && dateEntered.getMonth() === pdfMonth && dateEntered <= pdfLastYearDate;
      }).length;
      
      const appsGrowthPct = appsMTDLY > 0 ? (((appsMTD - appsMTDLY) / appsMTDLY) * 100).toFixed(1) : null;
      const enrollGrowthPct = enrollMTDLY > 0 ? (((enrollMTD - enrollMTDLY) / enrollMTDLY) * 100).toFixed(1) : null;
      const hiresGrowthPct = hiresLY > 0 ? (((hiresTY - hiresLY) / hiresLY) * 100).toFixed(1) : null;
      
      return { state, appsMTD, appsGrowthPct, enrollMTD, enrollGrowthPct, hiresTY, hiresGrowthPct };
    }).filter(s => s.appsMTD > 0 || s.enrollMTD > 0 || s.hiresTY > 0);

    // Draw table
    const tableColWidths = [32, 20, 28, 20, 28, 20, 28]; // state, apps, growth, enroll, growth, hires, growth
    const tableHeaders = ['State', 'Apps', 'Growth', 'Enroll', 'Growth', 'Hires', 'Growth'];
    
    // Header row
    doc.setFillColor(30, 27, 75);
    doc.rect(margin, y, contentW, 5.5, 'F');
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);
    
    let tx = margin;
    tableHeaders.forEach((header, i) => {
      const align = i === 0 ? 'left' : 'center';
      const xPos = i === 0 ? tx + 2 : tx + tableColWidths[i] / 2;
      doc.text(header, xPos, y + 3.8, { align });
      tx += tableColWidths[i];
    });
    y += 6;

    // Data rows
    stateTableData.forEach((row, idx) => {
      // Alternating row background
      if (idx % 2 === 0) {
        doc.setFillColor(248, 249, 255);
        doc.rect(margin, y - 0.5, contentW, 5, 'F');
      }
      
      doc.setFontSize(6.5);
      let rx = margin;
      
      // State name
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 60);
      doc.text(getStateAbbrev(row.state), rx + 2, y + 3);
      rx += tableColWidths[0];
      
      // Apps MTD
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 60);
      doc.text(row.appsMTD.toString(), rx + tableColWidths[1] / 2, y + 3, { align: 'center' });
      rx += tableColWidths[1];
      
      // Apps Growth
      if (row.appsGrowthPct !== null) {
        const gv = parseFloat(row.appsGrowthPct);
        doc.setTextColor(...(gv >= 0 ? green : red));
        doc.setFont('helvetica', 'bold');
        doc.text(`${gv >= 0 ? '+' : ''}${row.appsGrowthPct}%`, rx + tableColWidths[2] / 2, y + 3, { align: 'center' });
      } else {
        doc.setTextColor(...gray);
        doc.text('—', rx + tableColWidths[2] / 2, y + 3, { align: 'center' });
      }
      rx += tableColWidths[2];
      
      // Enrollments MTD
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 60);
      doc.text(row.enrollMTD.toString(), rx + tableColWidths[3] / 2, y + 3, { align: 'center' });
      rx += tableColWidths[3];
      
      // Enrollments Growth
      if (row.enrollGrowthPct !== null) {
        const gv = parseFloat(row.enrollGrowthPct);
        doc.setTextColor(...(gv >= 0 ? green : red));
        doc.setFont('helvetica', 'bold');
        doc.text(`${gv >= 0 ? '+' : ''}${row.enrollGrowthPct}%`, rx + tableColWidths[4] / 2, y + 3, { align: 'center' });
      } else {
        doc.setTextColor(...gray);
        doc.text('—', rx + tableColWidths[4] / 2, y + 3, { align: 'center' });
      }
      rx += tableColWidths[4];
      
      // Hires
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 60);
      doc.text(row.hiresTY.toString(), rx + tableColWidths[5] / 2, y + 3, { align: 'center' });
      rx += tableColWidths[5];
      
      // Hires Growth
      if (row.hiresGrowthPct !== null) {
        const gv = parseFloat(row.hiresGrowthPct);
        doc.setTextColor(...(gv >= 0 ? green : red));
        doc.setFont('helvetica', 'bold');
        doc.text(`${gv >= 0 ? '+' : ''}${row.hiresGrowthPct}%`, rx + tableColWidths[6] / 2, y + 3, { align: 'center' });
      } else {
        doc.setTextColor(...gray);
        doc.text('—', rx + tableColWidths[6] / 2, y + 3, { align: 'center' });
      }
      
      y += 5;
    });

    // Totals row
    const totalApps = stateTableData.reduce((s, r) => s + r.appsMTD, 0);
    const totalEnroll = stateTableData.reduce((s, r) => s + r.enrollMTD, 0);
    const totalHires = stateTableData.reduce((s, r) => s + r.hiresTY, 0);
    
    // Calculate overall growth from raw totals
    const totalAppsLY = stateTableData.reduce((s, r) => {
      if (r.appsGrowthPct === null || r.appsMTD === 0) return s;
      return s + Math.round(r.appsMTD / (1 + parseFloat(r.appsGrowthPct) / 100));
    }, 0);
    const totalEnrollLY = stateTableData.reduce((s, r) => {
      if (r.enrollGrowthPct === null || r.enrollMTD === 0) return s;
      return s + Math.round(r.enrollMTD / (1 + parseFloat(r.enrollGrowthPct) / 100));
    }, 0);
    const totalHiresLY = stateTableData.reduce((s, r) => {
      if (r.hiresGrowthPct === null || r.hiresTY === 0) return s;
      return s + Math.round(r.hiresTY / (1 + parseFloat(r.hiresGrowthPct) / 100));
    }, 0);
    
    const totalAppsGrowth = totalAppsLY > 0 ? (((totalApps - totalAppsLY) / totalAppsLY) * 100).toFixed(1) : null;
    const totalEnrollGrowth = totalEnrollLY > 0 ? (((totalEnroll - totalEnrollLY) / totalEnrollLY) * 100).toFixed(1) : null;
    const totalHiresGrowth = totalHiresLY > 0 ? (((totalHires - totalHiresLY) / totalHiresLY) * 100).toFixed(1) : null;

    // Draw top border for totals
    doc.setDrawColor(80, 80, 100);
    doc.setLineWidth(0.4);
    doc.line(margin, y - 0.5, margin + contentW, y - 0.5);

    // Totals row background
    doc.setFillColor(235, 237, 250);
    doc.rect(margin, y - 0.5, contentW, 5, 'F');

    doc.setFontSize(6.5);
    let trx = margin;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 50);
    doc.text('TOTAL', trx + 2, y + 3);
    trx += tableColWidths[0];

    doc.text(totalApps.toString(), trx + tableColWidths[1] / 2, y + 3, { align: 'center' });
    trx += tableColWidths[1];
    if (totalAppsGrowth !== null) {
      const gv = parseFloat(totalAppsGrowth);
      doc.setTextColor(...(gv >= 0 ? green : red));
      doc.text(`${gv >= 0 ? '+' : ''}${totalAppsGrowth}%`, trx + tableColWidths[2] / 2, y + 3, { align: 'center' });
    } else { doc.setTextColor(...gray); doc.text('—', trx + tableColWidths[2] / 2, y + 3, { align: 'center' }); }
    trx += tableColWidths[2];

    doc.setTextColor(30, 30, 50);
    doc.text(totalEnroll.toString(), trx + tableColWidths[3] / 2, y + 3, { align: 'center' });
    trx += tableColWidths[3];
    if (totalEnrollGrowth !== null) {
      const gv = parseFloat(totalEnrollGrowth);
      doc.setTextColor(...(gv >= 0 ? green : red));
      doc.text(`${gv >= 0 ? '+' : ''}${totalEnrollGrowth}%`, trx + tableColWidths[4] / 2, y + 3, { align: 'center' });
    } else { doc.setTextColor(...gray); doc.text('—', trx + tableColWidths[4] / 2, y + 3, { align: 'center' }); }
    trx += tableColWidths[4];

    doc.setTextColor(30, 30, 50);
    doc.text(totalHires.toString(), trx + tableColWidths[5] / 2, y + 3, { align: 'center' });
    trx += tableColWidths[5];
    if (totalHiresGrowth !== null) {
      const gv = parseFloat(totalHiresGrowth);
      doc.setTextColor(...(gv >= 0 ? green : red));
      doc.text(`${gv >= 0 ? '+' : ''}${totalHiresGrowth}%`, trx + tableColWidths[6] / 2, y + 3, { align: 'center' });
    } else { doc.setTextColor(...gray); doc.text('—', trx + tableColWidths[6] / 2, y + 3, { align: 'center' }); }
    y += 6;

    // --- STATE PERFORMANCE TABLE (YTD) ---
    // Always start on a new page — MTD table typically fills page 1
    doc.addPage();
    y = margin;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 60);
    doc.text('State Performance (YTD)', margin, y);
    y += 5;

    // Calculate per-state YTD data with YoY
    const pdfYtdStart = new Date(pdfYear, 0, 1); // Jan 1 current year
    const pdfYtdStartLY = new Date(pdfYear - 1, 0, 1); // Jan 1 last year

    const stateTableDataYTD = states.map(state => {
      const stateRows = rawData.filter(r => r['Certification state'] === state);

      const appsYTD = stateRows.filter(r => {
        if (!r.Applied) return false;
        const d = new Date(r.Applied);
        return d.getFullYear() === pdfYear && d >= pdfYtdStart && d <= pdfYesterday;
      }).length;

      const appsYTDLY = stateRows.filter(r => {
        if (!r.Applied) return false;
        const d = new Date(r.Applied);
        return d.getFullYear() === pdfYear - 1 && d >= pdfYtdStartLY && d <= pdfLastYearDate;
      }).length;

      const enrollYTD = stateRows.filter(r => {
        if (!r.Enrolled) return false;
        const d = new Date(r.Enrolled);
        return d.getFullYear() === pdfYear && d >= pdfYtdStart && d <= pdfYesterday;
      }).length;

      const enrollYTDLY = stateRows.filter(r => {
        if (!r.Enrolled) return false;
        const d = new Date(r.Enrolled);
        return d.getFullYear() === pdfYear - 1 && d >= pdfYtdStartLY && d <= pdfLastYearDate;
      }).length;

      const hiresYTD = stateRows.filter(r => {
        if (!r['FE Semester'] || !r['Date Entered']) return false;
        if (!r['FE Semester'].includes('Spring 2026')) return false;
        const dateEntered = new Date(r['Date Entered']);
        return dateEntered.getFullYear() === pdfYear && dateEntered >= pdfYtdStart && dateEntered <= pdfYesterday;
      }).length;

      const hiresYTDLY = stateRows.filter(r => {
        if (!r['FE Semester'] || !r['Date Entered']) return false;
        if (!r['FE Semester'].includes('Spring 2025')) return false;
        const dateEntered = new Date(r['Date Entered']);
        return dateEntered.getFullYear() === pdfYear - 1 && dateEntered >= pdfYtdStartLY && dateEntered <= pdfLastYearDate;
      }).length;

      const appsGrowthPct = appsYTDLY > 0 ? (((appsYTD - appsYTDLY) / appsYTDLY) * 100).toFixed(1) : null;
      const enrollGrowthPct = enrollYTDLY > 0 ? (((enrollYTD - enrollYTDLY) / enrollYTDLY) * 100).toFixed(1) : null;
      const hiresGrowthPct = hiresYTDLY > 0 ? (((hiresYTD - hiresYTDLY) / hiresYTDLY) * 100).toFixed(1) : null;

      return { state, appsMTD: appsYTD, appsGrowthPct, enrollMTD: enrollYTD, enrollGrowthPct, hiresTY: hiresYTD, hiresGrowthPct };
    }).filter(s => s.appsMTD > 0 || s.enrollMTD > 0 || s.hiresTY > 0);

    console.log(`YTD table: ${stateTableDataYTD.length} states, y=${y}, page=${doc.internal.getCurrentPageInfo().pageNumber}`);

    // YTD Header row
    doc.setFillColor(30, 27, 75);
    doc.rect(margin, y, contentW, 5.5, 'F');
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);

    tx = margin;
    tableHeaders.forEach((header, i) => {
      const xPos = i === 0 ? tx + 2 : tx + tableColWidths[i] / 2;
      doc.text(header, xPos, y + 3.8, { align: i === 0 ? 'left' : 'center' });
      tx += tableColWidths[i];
    });
    y += 6;

    // YTD Data rows
    stateTableDataYTD.forEach((row, idx) => {
      if (y > pageH - 15) {
        doc.addPage();
        y = margin;
      }
      if (idx % 2 === 0) {
        doc.setFillColor(248, 249, 255);
        doc.rect(margin, y - 0.5, contentW, 5, 'F');
      }

      doc.setFontSize(6.5);
      let rx = margin;

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 60);
      doc.text(getStateAbbrev(row.state), rx + 2, y + 3);
      rx += tableColWidths[0];

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 60);
      doc.text(row.appsMTD.toString(), rx + tableColWidths[1] / 2, y + 3, { align: 'center' });
      rx += tableColWidths[1];

      if (row.appsGrowthPct !== null) {
        const gv = parseFloat(row.appsGrowthPct);
        doc.setTextColor(...(gv >= 0 ? green : red));
        doc.setFont('helvetica', 'bold');
        doc.text(`${gv >= 0 ? '+' : ''}${row.appsGrowthPct}%`, rx + tableColWidths[2] / 2, y + 3, { align: 'center' });
      } else {
        doc.setTextColor(...gray);
        doc.text('—', rx + tableColWidths[2] / 2, y + 3, { align: 'center' });
      }
      rx += tableColWidths[2];

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 60);
      doc.text(row.enrollMTD.toString(), rx + tableColWidths[3] / 2, y + 3, { align: 'center' });
      rx += tableColWidths[3];

      if (row.enrollGrowthPct !== null) {
        const gv = parseFloat(row.enrollGrowthPct);
        doc.setTextColor(...(gv >= 0 ? green : red));
        doc.setFont('helvetica', 'bold');
        doc.text(`${gv >= 0 ? '+' : ''}${row.enrollGrowthPct}%`, rx + tableColWidths[4] / 2, y + 3, { align: 'center' });
      } else {
        doc.setTextColor(...gray);
        doc.text('—', rx + tableColWidths[4] / 2, y + 3, { align: 'center' });
      }
      rx += tableColWidths[4];

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 60);
      doc.text(row.hiresTY.toString(), rx + tableColWidths[5] / 2, y + 3, { align: 'center' });
      rx += tableColWidths[5];

      if (row.hiresGrowthPct !== null) {
        const gv = parseFloat(row.hiresGrowthPct);
        doc.setTextColor(...(gv >= 0 ? green : red));
        doc.setFont('helvetica', 'bold');
        doc.text(`${gv >= 0 ? '+' : ''}${row.hiresGrowthPct}%`, rx + tableColWidths[6] / 2, y + 3, { align: 'center' });
      } else {
        doc.setTextColor(...gray);
        doc.text('—', rx + tableColWidths[6] / 2, y + 3, { align: 'center' });
      }

      y += 5;
    });

    // YTD Totals row
    const totalAppsYTD = stateTableDataYTD.reduce((s, r) => s + r.appsMTD, 0);
    const totalEnrollYTD = stateTableDataYTD.reduce((s, r) => s + r.enrollMTD, 0);
    const totalHiresYTD = stateTableDataYTD.reduce((s, r) => s + r.hiresTY, 0);

    const totalAppsYTDLY = stateTableDataYTD.reduce((s, r) => {
      if (r.appsGrowthPct === null || r.appsMTD === 0) return s;
      return s + Math.round(r.appsMTD / (1 + parseFloat(r.appsGrowthPct) / 100));
    }, 0);
    const totalEnrollYTDLY = stateTableDataYTD.reduce((s, r) => {
      if (r.enrollGrowthPct === null || r.enrollMTD === 0) return s;
      return s + Math.round(r.enrollMTD / (1 + parseFloat(r.enrollGrowthPct) / 100));
    }, 0);
    const totalHiresYTDLY = stateTableDataYTD.reduce((s, r) => {
      if (r.hiresGrowthPct === null || r.hiresTY === 0) return s;
      return s + Math.round(r.hiresTY / (1 + parseFloat(r.hiresGrowthPct) / 100));
    }, 0);

    const totalAppsYTDGrowth = totalAppsYTDLY > 0 ? (((totalAppsYTD - totalAppsYTDLY) / totalAppsYTDLY) * 100).toFixed(1) : null;
    const totalEnrollYTDGrowth = totalEnrollYTDLY > 0 ? (((totalEnrollYTD - totalEnrollYTDLY) / totalEnrollYTDLY) * 100).toFixed(1) : null;
    const totalHiresYTDGrowth = totalHiresYTDLY > 0 ? (((totalHiresYTD - totalHiresYTDLY) / totalHiresYTDLY) * 100).toFixed(1) : null;

    doc.setDrawColor(80, 80, 100);
    doc.setLineWidth(0.4);
    doc.line(margin, y - 0.5, margin + contentW, y - 0.5);
    doc.setFillColor(235, 237, 250);
    doc.rect(margin, y - 0.5, contentW, 5, 'F');

    doc.setFontSize(6.5);
    trx = margin;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 50);
    doc.text('TOTAL', trx + 2, y + 3);
    trx += tableColWidths[0];

    doc.text(totalAppsYTD.toString(), trx + tableColWidths[1] / 2, y + 3, { align: 'center' });
    trx += tableColWidths[1];
    if (totalAppsYTDGrowth !== null) {
      const gv = parseFloat(totalAppsYTDGrowth);
      doc.setTextColor(...(gv >= 0 ? green : red));
      doc.text(`${gv >= 0 ? '+' : ''}${totalAppsYTDGrowth}%`, trx + tableColWidths[2] / 2, y + 3, { align: 'center' });
    } else { doc.setTextColor(...gray); doc.text('—', trx + tableColWidths[2] / 2, y + 3, { align: 'center' }); }
    trx += tableColWidths[2];

    doc.setTextColor(30, 30, 50);
    doc.text(totalEnrollYTD.toString(), trx + tableColWidths[3] / 2, y + 3, { align: 'center' });
    trx += tableColWidths[3];
    if (totalEnrollYTDGrowth !== null) {
      const gv = parseFloat(totalEnrollYTDGrowth);
      doc.setTextColor(...(gv >= 0 ? green : red));
      doc.text(`${gv >= 0 ? '+' : ''}${totalEnrollYTDGrowth}%`, trx + tableColWidths[4] / 2, y + 3, { align: 'center' });
    } else { doc.setTextColor(...gray); doc.text('—', trx + tableColWidths[4] / 2, y + 3, { align: 'center' }); }
    trx += tableColWidths[4];

    doc.setTextColor(30, 30, 50);
    doc.text(totalHiresYTD.toString(), trx + tableColWidths[5] / 2, y + 3, { align: 'center' });
    trx += tableColWidths[5];
    if (totalHiresYTDGrowth !== null) {
      const gv = parseFloat(totalHiresYTDGrowth);
      doc.setTextColor(...(gv >= 0 ? green : red));
      doc.text(`${gv >= 0 ? '+' : ''}${totalHiresYTDGrowth}%`, trx + tableColWidths[6] / 2, y + 3, { align: 'center' });
    } else { doc.setTextColor(...gray); doc.text('—', trx + tableColWidths[6] / 2, y + 3, { align: 'center' }); }
    y += 6;

    // --- REUSABLE TREND CHART FUNCTION ---
    const drawTrendChart = (title, data, meta, startY) => {
      // Check if we need a new page
      if (startY > pageH - 80) {
        doc.addPage();
        startY = margin;
      }

      doc.setDrawColor(200, 200, 210);
      doc.setLineWidth(0.3);
      doc.line(margin, startY, margin + contentW, startY);
      startY += 5;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 60);
      doc.text(title, margin, startY);
      startY += 6;

      const chartX = margin + 12;
      const chartY = startY;
      const chartW = contentW - 16;
      const availableH = pageH - 12 - startY - 18;
      const chartH = Math.min(availableH, 50);

      // Find max value
      let maxVal = 0;
      data.forEach(d => {
        [meta.year1Label, meta.year2Label, meta.year3Label].forEach(key => {
          if (d[key] !== null && d[key] !== undefined && d[key] > maxVal) maxVal = d[key];
        });
      });
      maxVal = Math.ceil(maxVal / 100) * 100;
      if (maxVal === 0) maxVal = 100;

      // Chart background
      doc.setFillColor(252, 252, 255);
      doc.rect(chartX, chartY, chartW, chartH, 'F');

      // Y-axis gridlines
      const yTicks = 5;
      for (let i = 0; i <= yTicks; i++) {
        const tickY = chartY + chartH - (i / yTicks) * chartH;
        const tickVal = Math.round((i / yTicks) * maxVal);
        doc.setDrawColor(230, 230, 240);
        doc.setLineWidth(0.2);
        doc.line(chartX, tickY, chartX + chartW, tickY);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 140);
        doc.text(tickVal.toLocaleString(), chartX - 2, tickY + 1.5, { align: 'right' });
      }

      // X-axis labels
      const monthLabels = data.map(d => d.month);
      const stepX = chartW / (monthLabels.length - 1);
      monthLabels.forEach((label, i) => {
        const lx = chartX + i * stepX;
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 140);
        doc.text(label, lx, chartY + chartH + 4, { align: 'center' });
      });

      // Plot line helper
      const plotLine = (yearKey, color, lineWidth, dashed) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(lineWidth);
        let lastX = null, lastY = null;
        data.forEach((d, i) => {
          const val = d[yearKey];
          if (val === null || val === undefined) return;
          const px = chartX + i * stepX;
          const py = chartY + chartH - (val / maxVal) * chartH;
          if (lastX !== null) {
            if (dashed) {
              const segments = 6;
              const dx = (px - lastX) / segments;
              const dy = (py - lastY) / segments;
              for (let s = 0; s < segments; s += 2) {
                doc.line(lastX + s * dx, lastY + s * dy, lastX + (s + 1) * dx, lastY + (s + 1) * dy);
              }
            } else {
              doc.line(lastX, lastY, px, py);
            }
          }
          lastX = px;
          lastY = py;
        });
      };

      plotLine(meta.year1Label, [180, 180, 200], 0.4, true);
      plotLine(meta.year2Label, [2, 111, 191], 0.6, false);
      plotLine(meta.year3Label, [0, 190, 168], 1.2, false);

      // Dots on current year
      data.forEach((d, i) => {
        const val = d[meta.year3Label];
        if (val === null || val === undefined) return;
        const px = chartX + i * stepX;
        const py = chartY + chartH - (val / maxVal) * chartH;
        doc.setFillColor(0, 190, 168);
        doc.circle(px, py, 1, 'F');
      });

      // Legend
      const legendY = chartY + chartH + 6;
      const legendItems = [
        { label: meta.year1Label, color: [180, 180, 200], dashed: true },
        { label: meta.year2Label, color: [2, 111, 191], dashed: false },
        { label: `${meta.year3Label} (current)`, color: [0, 190, 168], dashed: false },
      ];
      const legendTotalW = legendItems.length * 40;
      let legendX = chartX + (chartW - legendTotalW) / 2;
      legendItems.forEach(item => {
        doc.setDrawColor(...item.color);
        doc.setLineWidth(item.dashed ? 0.5 : 1);
        if (item.dashed) {
          doc.line(legendX, legendY, legendX + 3, legendY);
          doc.line(legendX + 5, legendY, legendX + 8, legendY);
          doc.line(legendX + 10, legendY, legendX + 12, legendY);
        } else {
          doc.line(legendX, legendY, legendX + 12, legendY);
        }
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 80);
        doc.text(item.label, legendX + 14, legendY + 1);
        legendX += 45;
      });

      return legendY + 6;
    };

    // Draw Application Trend — always on a new page after YTD table
    const trendData = metrics?.monthlyTrend || [];
    const trendMeta = metrics?.monthlyTrendMeta || {};
    if (trendData.length > 0) {
      doc.addPage();
      y = margin;
      y = drawTrendChart('Application Trend (3-Year Comparison)', trendData, trendMeta, y);
    }

    // --- FOOTER (on each page) ---
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(160, 160, 180);
      doc.text('Confidential — iteach Enrollment Pipeline Report', margin, pageH - 8);
      doc.text(`Version 2.1.9 | ${dateStr}`, pageW - margin, pageH - 8, { align: 'right' });
    }

    const fileName = `iTeach_Pipeline_Report_${now.toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
  };

  // Render individual dashboard card by ID
  const renderCard = (cardId) => {
    switch (cardId) {
      case 'ai-summary':
        return (
          <>
        <div className="ai-narrative-card-enhanced">
          <div className="ai-narrative-header">
            <div className="ai-header-left">
              <span className="ai-badge-enhanced">📊 Executive Summary</span>
              <span className="ai-status-indicator" style={{
                color: aiNarrative?.isFallback ? '#F59E0B' : '#10B981'
              }}>
                {aiNarrative?.isFallback ? '⚠️' : '✓'}
              </span>
            </div>
            {aiNarrative?.generatedAt && !aiNarrative?.isFallback && (
              <span className="ai-timestamp">
                Updated: {new Date(aiNarrative.generatedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </span>
            )}
          </div>
          <div className="ai-narrative-content-enhanced">
            {narrativeLoading ? (
              <div className="ai-narrative-loading">
                <div className="loading-spinner"></div>
                <span>Analyzing metrics and generating insights...</span>
              </div>
            ) : aiNarrative ? (
              (() => {
                const parts = (aiNarrative.summary || '').split('|||');
                const lead = parts[0]?.trim();
                const detail = parts[1]?.trim();
                return (
                  <div className="ai-summary-expandable">
                    <div className="ai-summary-text" dangerouslySetInnerHTML={{
                      __html: enhanceSummaryText(lead)
                    }}></div>
                    {detail && (
                      <>
                        <button 
                          className="ai-read-more-btn"
                          onClick={() => {
                            const el = document.querySelector('.ai-summary-detail');
                            if (el) el.classList.toggle('ai-detail-visible');
                            const btn = document.querySelector('.ai-read-more-btn');
                            if (btn) btn.textContent = el?.classList.contains('ai-detail-visible') ? '← less' : '... read more';
                          }}
                        >... read more</button>
                        <div className="ai-summary-detail" dangerouslySetInnerHTML={{
                          __html: enhanceSummaryText(detail)
                        }}></div>
                      </>
                    )}
                    {generateFocusDirective() && (
                      <div className="focus-directive">
                        <strong className="focus-label">Needed Focus:</strong> {generateFocusDirective()}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <p>Summary will appear here shortly.</p>
            )}
          </div>
        </div>
          </>
        );
      case 'kpi-cards':
        return (
          <>
        {/* Performance Summary - Moved to top */}
        <div className="performance-summary-card">
          <h2 className="section-title">
            <Calendar size={20} />
            <span>Performance Summary (Through Yesterday)</span>
          </h2>

          {/* Needed Focus for Performance Summary */}
          {generatePerformanceFocus() && (
            <div className="card-focus-directive" style={{ marginBottom: '1rem' }}>
              <strong className="focus-label">Needed Focus:</strong> {generatePerformanceFocus()}
            </div>
          )}

          <div className="performance-grid">
            {/* Applications Column */}
            <div className="performance-column-card">
              <h3 className="performance-column-title">Applications</h3>

              <div className="performance-column-content">
                <div className="mtd-section">
                  <h4 className="mtd-title">Month to Date</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Actual:</span>
                      <span className="mtd-value">{metrics?.applicationsMTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.applicationsYoYGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.applicationsYoYGrowth >= 0 ? '+' : ''}{metrics?.applicationsYoYGrowth || 0}%
                      </span>
                    </div>
                  </div>
                  <div className="mtd-stats" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Forecast:</span>
                      <span className="mtd-value" style={{ fontStyle: 'italic', fontSize: '0.95em' }}>{metrics?.forecastMTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">vs Forecast:</span>
                      <span className={`mtd-value ${metrics?.forecastVariance >= 0 ? 'growth-positive' : 'growth-negative'}`} style={{ fontStyle: 'italic', fontSize: '0.95em' }}>
                        {metrics?.forecastVariance >= 0 ? '+' : ''}{metrics?.forecastVariance || 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mtd-section">
                  <h4 className="mtd-title">Year to Date</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Actual:</span>
                      <span className="mtd-value">{metrics?.applicationsYTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.applicationsYTDGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.applicationsYTDGrowth >= 0 ? '+' : ''}{metrics?.applicationsYTDGrowth || 0}%
                      </span>
                    </div>
                  </div>
                  <div className="mtd-stats" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Forecast:</span>
                      <span className="mtd-value" style={{ fontStyle: 'italic', fontSize: '0.95em' }}>{metrics?.forecastYTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">vs Forecast:</span>
                      <span className={`mtd-value ${metrics?.forecastYTDVariance >= 0 ? 'growth-positive' : 'growth-negative'}`} style={{ fontStyle: 'italic', fontSize: '0.95em' }}>
                        {metrics?.forecastYTDVariance >= 0 ? '+' : ''}{metrics?.forecastYTDVariance || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="current-status-anchor">
                <span className="current-status-label">Current Apps</span>
                <span className="current-status-value">{metrics?.currentApps?.toLocaleString() || 0}</span>
              </div>
            </div>

            {/* Accepted Column */}
            <div className="performance-column-card">
              <h3 className="performance-column-title">Accepted</h3>

              <div className="performance-column-content">
                <div className="mtd-section">
                  <h4 className="mtd-title">Month to Date</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Actual:</span>
                      <span className="mtd-value">{metrics?.acceptancesMTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.acceptancesYoYGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.acceptancesYoYGrowth >= 0 ? '+' : ''}{metrics?.acceptancesYoYGrowth || 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mtd-section">
                  <h4 className="mtd-title">Year to Date</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Actual:</span>
                      <span className="mtd-value">{metrics?.acceptancesYTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.acceptancesYTDGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.acceptancesYTDGrowth >= 0 ? '+' : ''}{metrics?.acceptancesYTDGrowth || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="current-status-anchor">
                <span className="current-status-label">Current Accepted</span>
                <span className="current-status-value">{metrics?.currentAccepted?.toLocaleString() || 0}</span>
              </div>
            </div>

            {/* Enrollments Column */}
            <div className="performance-column-card">
              <h3 className="performance-column-title">Enrollments</h3>

              <div className="performance-column-content">
                <div className="mtd-section">
                  <h4 className="mtd-title">Month to Date</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Actual:</span>
                      <span className="mtd-value">{metrics?.enrollmentsMTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.enrollmentsYoYGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.enrollmentsYoYGrowth >= 0 ? '+' : ''}{metrics?.enrollmentsYoYGrowth || 0}%
                      </span>
                    </div>
                  </div>
                  <div className="mtd-stats" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Forecast:</span>
                      <span className="mtd-value" style={{ fontStyle: 'italic', fontSize: '0.95em' }}>{metrics?.forecastEnrollmentsMTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">vs Forecast:</span>
                      <span className={`mtd-value ${metrics?.forecastEnrollmentsVariance >= 0 ? 'growth-positive' : 'growth-negative'}`} style={{ fontStyle: 'italic', fontSize: '0.95em' }}>
                        {metrics?.forecastEnrollmentsVariance >= 0 ? '+' : ''}{metrics?.forecastEnrollmentsVariance || 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mtd-section">
                  <h4 className="mtd-title">Year to Date</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Actual:</span>
                      <span className="mtd-value">{metrics?.enrollmentsYTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.enrollmentsYTDGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.enrollmentsYTDGrowth >= 0 ? '+' : ''}{metrics?.enrollmentsYTDGrowth || 0}%
                      </span>
                    </div>
                  </div>
                  <div className="mtd-stats" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Forecast:</span>
                      <span className="mtd-value" style={{ fontStyle: 'italic', fontSize: '0.95em' }}>{metrics?.forecastEnrollmentsYTD?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">vs Forecast:</span>
                      <span className={`mtd-value ${metrics?.forecastEnrollmentsYTDVariance >= 0 ? 'growth-positive' : 'growth-negative'}`} style={{ fontStyle: 'italic', fontSize: '0.95em' }}>
                        {metrics?.forecastEnrollmentsYTDVariance >= 0 ? '+' : ''}{metrics?.forecastEnrollmentsYTDVariance || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="current-status-anchor">
                <span className="current-status-label">Current Enrolled</span>
                <span className="current-status-value">{metrics?.currentEnrolled?.toLocaleString() || 0}</span>
              </div>
            </div>

            {/* Hires Column */}
            <div className="performance-column-card">
              <h3 className="performance-column-title">Hires</h3>

              <div className="performance-column-content">
                <div className="mtd-section">
                  <h4 className="mtd-title">Fall 2026</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Total:</span>
                      <span className="mtd-value">{metrics?.fall2026Hires?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.fallHiresGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.fallHiresGrowth >= 0 ? '+' : ''}{metrics?.fallHiresGrowth || 0}%
                      </span>
                    </div>
                  </div>
                  <div className="mtd-stats" style={{ marginTop: '0.35rem', paddingTop: '0.35rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Fall 2025:</span>
                      <span className="mtd-value" style={{ opacity: 0.6 }}>{metrics?.fall2025Hires?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="mtd-section">
                  <h4 className="mtd-title">Currently Placed Teachers</h4>
                  <div className="mtd-stats">
                    <div className="mtd-stat-item" style={{ justifyContent: 'flex-end', textAlign: 'right' }}>
                      <span className="mtd-value" style={{ fontSize: '2rem', color: '#CFFB5E' }}>{metrics?.currentlyPlacedTeachers?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item" style={{ justifyContent: 'flex-end', textAlign: 'right' }}>
                      <span className="mtd-label" style={{ fontStyle: 'italic', fontSize: '0.75rem', opacity: 0.7 }}>Spring + Fall 2026</span>
                    </div>
                  </div>
                  <div className="mtd-stats" style={{ marginTop: '0.35rem', paddingTop: '0.35rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">Last year:</span>
                      <span className="mtd-value" style={{ opacity: 0.6 }}>{metrics?.priorYearPlacedTeachers?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mtd-stat-item">
                      <span className="mtd-label">YoY Growth:</span>
                      <span className={`mtd-value ${metrics?.placedTeachersGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                        {metrics?.placedTeachersGrowth >= 0 ? '+' : ''}{metrics?.placedTeachersGrowth || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="current-status-anchor">
                <span className="current-status-label">Current Hired</span>
                <span className="current-status-value">{metrics?.currentHired?.toLocaleString() || 0}</span>
              </div>
            </div>
          </div>

          <div className="narrative-footer">
            <div className="footer-text">
              Last updated: {lastUpdate.toLocaleString()}
            </div>
          </div>
        </div>
          </>
        );
      case 'la-addon': {
        // Louisiana-only breakout — the Add-On is a second LA track, folded into the
        // enrolled totals above but worth seeing on its own while the program ramps.
        const ao = metrics?.addOn;
        if (selectedState !== 'Louisiana' || !ao) return null;
        const t = ao.totals || {};
        const tiles = [
          { label: 'Registered', value: t.candidates || 0, color: '#E0E7FF', hint: 'All Add-On candidates' },
          { label: 'Applied', value: t.applied || 0, color: '#00BEA8', hint: 'Awaiting acceptance' },
          { label: 'Accepted', value: t.accepted || 0, color: '#FFA500', hint: 'Not yet enrolled' },
          { label: 'Enrolled', value: t.everEnrolled || 0, color: '#CFFB5E', hint: `${t.enrolled || 0} currently enrolled` },
        ];
        if (t.completed > 0) {
          tiles.push({ label: 'Completed', value: t.completed, color: '#4BD48D', hint: 'Finished the track' });
        }
        return (
          <div className="chart-card chart-full" id="la-addon">
            <h3 className="chart-title">Louisiana Add-On Program (as of {ao.asOf})</h3>
            <div className="addon-stat-grid">
              {tiles.map(tile => (
                <div className="addon-stat-card" key={tile.label}>
                  <span className="addon-stat-label">{tile.label}</span>
                  <span className="addon-stat-value" style={{ color: tile.color }}>{tile.value.toLocaleString()}</span>
                  <span className="addon-stat-hint">{tile.hint}</span>
                </div>
              ))}
            </div>

            {ao.monthly?.length > 0 && (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={ao.monthly} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="month" stroke="#E0E7FF" fontSize={12} />
                  <YAxis stroke="#E0E7FF" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                    labelStyle={{ color: '#E0E7FF' }}
                  />
                  <Legend />
                  <Bar dataKey="registered" name="Registered" fill="#00BEA8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="enrolled" name="Enrolled" fill="#CFFB5E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}

            <div className="cohort-note">
              <strong>Note:</strong> Counted from Add-On track registrations in Salesforce, not the
              Contact admission record — that's how returning teachers picking up an endorsement show
              up, since they never re-apply. All {(t.everEnrolled || 0).toLocaleString()} enrolled Add-On
              candidates are included in Louisiana's enrolled totals above
              {ao.addedEnrolled > 0
                ? `, ${ao.addedEnrolled.toLocaleString()} of which the main application feed misses and this card supplies.`
                : '; the main application feed already carries all of them.'}
            </div>
          </div>
        );
      }
      case 'highlights':
        return (
          <>
        {/* Highlights Section */}
        <div className="eval-health-section">
          <h2 className="section-title">
            <span>Pacing &amp; Highlights</span>
          </h2>
        </div>
        <div className="highlights-grid">
          <div className="highlight-card">
            <div className="neat-fact-icon">📈</div>
            <div className="neat-fact-content">
              <h4 className="neat-fact-title">Application Pacing</h4>
              <div className="neat-fact-value">
                #{metrics?.appPacingRank || 'N/A'} <span className="neat-fact-unit">of {metrics?.appPacingTotal || 0} years</span>
              </div>
              <div className="neat-fact-description">
                Ranked by apps received MTD vs all previous years
              </div>
            </div>
          </div>

          <div className="highlight-card">
            <div className="neat-fact-icon">🎓</div>
            <div className="neat-fact-content">
              <h4 className="neat-fact-title">Enrollment Pacing</h4>
              <div className="neat-fact-value">
                #{metrics?.enrollmentPacingRank || 'N/A'} <span className="neat-fact-unit">of {metrics?.enrollmentPacingTotal || 0} years</span>
              </div>
              <div className="neat-fact-description">
                Ranked by enrollments MTD vs all previous years
              </div>
            </div>
          </div>

          <div className="highlight-card">
            <div className="neat-fact-icon">🕐</div>
            <div className="neat-fact-content">
              <h4 className="neat-fact-title">Popular Time (6mo)</h4>
              <div className="neat-fact-value">
                {metrics?.popularTimeSlot || 'N/A'}
              </div>
              <div className="neat-fact-description">
                {(metrics?.popularTimeCount || 0).toLocaleString()} applications
              </div>
            </div>
          </div>
        </div>
          </>
        );
      case 'app-age':
        return (
          <>
        {/* Applications Age Funnel - Stacked Bar */}
        <div className="chart-card chart-full" style={{ marginTop: '2rem' }} id="applications-age">
          <h3 className="chart-title">Applications Age Distribution (Last 6 Months, Non-Enrolled)</h3>

          {/* Needed Focus for Applications Age */}
          {generateApplicationsAgeFocus() && (
            <div className="card-focus-directive">
              <strong className="focus-label">Needed Focus:</strong> {generateApplicationsAgeFocus()}
            </div>
          )}

          {/* Single stacked bar */}
          <div style={{ marginTop: '1.5rem' }}>
            {(() => {
              const total = (metrics?.applicationsAgeFunnel?.under30 || 0) +
                           (metrics?.applicationsAgeFunnel?.days31to60 || 0) +
                           (metrics?.applicationsAgeFunnel?.days61to90 || 0) +
                           (metrics?.applicationsAgeFunnel?.over90 || 0);

              const stages = [
                { label: '<30 days', count: metrics?.applicationsAgeFunnel?.under30 || 0, color: '#4BD48D' },
                { label: '31-60 days', count: metrics?.applicationsAgeFunnel?.days31to60 || 0, color: '#00BEA8' },
                { label: '61-90 days', count: metrics?.applicationsAgeFunnel?.days61to90 || 0, color: '#FFA500' },
                { label: '90+ days', count: metrics?.applicationsAgeFunnel?.over90 || 0, color: '#FF6B6B' }
              ];

              return (
                <>
                  <div style={{
                    display: 'flex',
                    width: '100%',
                    height: '60px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    {stages.map((stage, idx) => {
                      const percentage = total > 0 ? (stage.count / total) * 100 : 0;

                      return percentage > 0 ? (
                        <div
                          key={idx}
                          title={`${stage.label}: ${stage.count} (${percentage.toFixed(1)}%)`}
                          style={{
                            width: `${percentage}%`,
                            background: stage.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          {percentage > 8 && (
                            <div style={{
                              color: 'white',
                              fontWeight: '600',
                              fontSize: '0.9rem',
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                            }}>
                              {percentage.toFixed(0)}%
                            </div>
                          )}
                        </div>
                      ) : null;
                    })}
                  </div>

                  {/* Legend */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '2rem',
                    marginTop: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    {stages.map((stage, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: stage.color,
                          borderRadius: '3px'
                        }}></div>
                        <span style={{ fontSize: '0.875rem' }}>
                          {stage.label}: {stage.count} ({total > 0 ? ((stage.count / total) * 100).toFixed(1) : 0}%)
                        </span>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.6)',
                    marginTop: '0.75rem',
                    textAlign: 'center'
                  }}>
                    Total: {total.toLocaleString()} non-enrolled applications
                  </div>
                </>
              );
            })()}
          </div>
        </div>
          </>
        );
      case 'cohort':
        return (
          <>
        {/* Cohort Conversion Analysis Card */}
        <div className="cohort-analysis-card" id="conversion-speed">
          <h2 className="section-title">
            <TrendingUp size={20} />
            <span>Conversion Speed Analysis</span>
          </h2>

          {/* Needed Focus for Conversion Speed */}
          {(() => {
            const dist = metrics?.conversionDistribution || {};
            const totalApps = (dist.within30 || 0) + (dist.within60 || 0) + (dist.within90 || 0) + (dist.within180 || 0) + (dist.over180 || 0);
            if (totalApps === 0) return null;
            const slowPct = Math.round(((dist.within180 || 0) + (dist.over180 || 0)) / totalApps * 100);
            const fastPct = Math.round((dist.within30 || 0) / totalApps * 100);
            const medianDays = metrics?.medianConversionDays || metrics?.avgEnrollmentTime || 0;

            let focusText = null;
            if (slowPct > 40) {
              focusText = `${slowPct}% of conversions take 90+ days — streamline enrollment process to reduce cycle time.`;
            } else if (medianDays > 60) {
              focusText = `Median conversion time is ${medianDays} days — target reducing below 45 days through faster follow-up.`;
            } else if (fastPct > 50) {
              focusText = `Strong velocity with ${fastPct}% converting within 30 days — maintain current outreach cadence.`;
            } else if (slowPct > 25) {
              focusText = `${slowPct}% of conversions are 90+ days — investigate bottlenecks in the enrollment pipeline.`;
            }

            if (!focusText) return null;
            return (
              <div className="card-focus-directive" style={{ marginBottom: '1rem' }}>
                <strong className="focus-label">Needed Focus:</strong> {focusText}
              </div>
            );
          })()}

          <div className="cohort-grid">
            {/* Overall Distribution */}
            <div className="cohort-distribution">
              <h3 className="cohort-subtitle">Conversion Distribution (Last Year)</h3>
              <div className="distribution-bars">
                {(() => {
                  const dist = metrics?.conversionDistribution || {};
                  const totalApps = (dist.within30 || 0) + (dist.within60 || 0) + (dist.within90 || 0) + (dist.within180 || 0) + (dist.over180 || 0);

                  return (
                    <>
                      <div className="distribution-item">
                        <div className="distribution-label">
                          <span className="label-text">0-30 days</span>
                          <span className="label-count">
                            {dist.within30 || 0} apps
                          </span>
                        </div>
                        <div className="distribution-bar-container">
                          <div
                            className="distribution-bar bar-green"
                            style={{
                              width: `${totalApps > 0 ? (dist.within30 / totalApps) * 100 : 0}%`,
                              backgroundColor: '#4BD48D'
                            }}
                          ></div>
                        </div>
                      </div>

                      <div className="distribution-item">
                        <div className="distribution-label">
                          <span className="label-text">31-60 days</span>
                          <span className="label-count">
                            {dist.within60 || 0} apps
                          </span>
                        </div>
                        <div className="distribution-bar-container">
                          <div
                            className="distribution-bar bar-blue"
                            style={{
                              width: `${totalApps > 0 ? (dist.within60 / totalApps) * 100 : 0}%`,
                              backgroundColor: '#00BEA8'
                            }}
                          ></div>
                        </div>
                      </div>

                      <div className="distribution-item">
                        <div className="distribution-label">
                          <span className="label-text">61-90 days</span>
                          <span className="label-count">
                            {dist.within90 || 0} apps
                          </span>
                        </div>
                        <div className="distribution-bar-container">
                          <div
                            className="distribution-bar bar-purple"
                            style={{
                              width: `${totalApps > 0 ? (dist.within90 / totalApps) * 100 : 0}%`,
                              backgroundColor: '#026FBF'
                            }}
                          ></div>
                        </div>
                      </div>

                      <div className="distribution-item">
                        <div className="distribution-label">
                          <span className="label-text">91-180 days</span>
                          <span className="label-count">
                            {dist.within180 || 0} apps
                          </span>
                        </div>
                        <div className="distribution-bar-container">
                          <div
                            className="distribution-bar bar-yellow"
                            style={{
                              width: `${totalApps > 0 ? (dist.within180 / totalApps) * 100 : 0}%`,
                              backgroundColor: '#026ABD'
                            }}
                          ></div>
                        </div>
                      </div>

                      <div className="distribution-item">
                        <div className="distribution-label">
                          <span className="label-text">180+ days</span>
                          <span className="label-count">
                            {dist.over180 || 0} apps
                          </span>
                        </div>
                        <div className="distribution-bar-container">
                          <div
                            className="distribution-bar bar-red"
                            style={{
                              width: `${totalApps > 0 ? (dist.over180 / totalApps) * 100 : 0}%`,
                              backgroundColor: '#001997'
                            }}
                          ></div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Cohort Trend Chart */}
            <div className="cohort-chart">
              <h3 className="cohort-subtitle">Monthly Cohort Performance (Last 9 Months)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={metrics?.cohortAnalysis || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="month"
                    stroke="#E0E7FF"
                    style={{ fontSize: '0.75rem' }}
                  />
                  <YAxis
                    stroke="#E0E7FF"
                    label={{ value: '% of Applications', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }}
                    style={{ fontSize: '0.875rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 27, 75, 0.95)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#E0E7FF' }}
                    formatter={(value) => `${value}%`}
                  />
                  <Legend />
                  <Bar dataKey="within30" stackId="a" fill="#4BD48D" name="0-30 days" />
                  <Bar dataKey="within60" stackId="a" fill="#00BEA8" name="31-60 days" />
                  <Bar dataKey="within90" stackId="a" fill="#026FBF" name="61-90 days" />
                  <Bar dataKey="within180" stackId="a" fill="#026ABD" name="91-180 days" />
                  <Bar dataKey="over180" stackId="a" fill="#001997" name="180+ days" />
                </BarChart>
              </ResponsiveContainer>
              <div className="cohort-note">
                <strong>Note:</strong> Shows % of applications from each month that converted within each timeframe.
                Newer cohorts may have lower totals as they haven't had time to convert yet.
              </div>
            </div>
          </div>
        </div>
          </>
        );
      case 'app-trend':
        return (
          <>
        {/* Application Trend - 3 Year Comparison */}
        <div className="chart-card chart-full">
          <h3 className="chart-title">Application Trend by Month (3 Year Comparison)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics?.monthlyTrend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="month" stroke="#E0E7FF" />
              <YAxis stroke="#E0E7FF" label={{ value: 'Applications', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                labelStyle={{ color: '#E0E7FF' }}
                formatter={(value) => value.toLocaleString()}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.year1Label}
                stroke="#4BD48D"
                strokeWidth={3}
                name={metrics?.monthlyTrendMeta?.year1Label || '2023'}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.year2Label}
                stroke="#00BEA8"
                strokeWidth={3}
                name={metrics?.monthlyTrendMeta?.year2Label || '2024'}
              />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.year3Label}
                stroke="#CFFB5E"
                strokeWidth={4}
                name={metrics?.monthlyTrendMeta?.year3Label || '2026'}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.forecastLabel}
                stroke="#CFFB5E"
                strokeWidth={3}
                strokeDasharray="8 4"
                name={metrics?.monthlyTrendMeta?.forecastLabel || '2026 Forecast'}
                connectNulls={false}
                dot={false}
                opacity={0.7}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="cohort-note">
            <strong>Note:</strong> Shows each calendar month (Jan-Dec) compared across the last 3 years. {metrics?.monthlyTrendMeta?.year3Label} solid line shows actuals through the current month — dashed line shows forecast for remaining months.
          </div>
        </div>
          </>
        );
      case 'enroll-trend':
        return (
          <>
        {/* Enrollment Trend - 3 Year Comparison */}
        <div className="chart-card chart-full">
          <h3 className="chart-title">Enrollment Trend by Month (3 Year Comparison)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics?.monthlyEnrollmentTrend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="month" stroke="#E0E7FF" />
              <YAxis stroke="#E0E7FF" label={{ value: 'Enrollments', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                labelStyle={{ color: '#E0E7FF' }}
                formatter={(value) => value.toLocaleString()}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.year1Label}
                stroke="#4BD48D"
                strokeWidth={3}
                name={metrics?.monthlyTrendMeta?.year1Label || '2023'}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.year2Label}
                stroke="#00BEA8"
                strokeWidth={3}
                name={metrics?.monthlyTrendMeta?.year2Label || '2024'}
              />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.year3Label}
                stroke="#CFFB5E"
                strokeWidth={4}
                name={metrics?.monthlyTrendMeta?.year3Label || '2026'}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey={metrics?.monthlyTrendMeta?.forecastLabel}
                stroke="#CFFB5E"
                strokeWidth={3}
                strokeDasharray="8 4"
                name={metrics?.monthlyTrendMeta?.forecastLabel || '2026 Forecast'}
                connectNulls={false}
                dot={false}
                opacity={0.7}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="cohort-note">
            <strong>Note:</strong> Shows each calendar month (Jan-Dec) compared across the last 3 years. {metrics?.monthlyTrendMeta?.year3Label} solid line shows actuals through the current month — dashed line shows forecast for remaining months.
          </div>
        </div>
          </>
        );
      case 'fall-fe-state':
        return (
          <div className="chart-card chart-full">
            <h3 className="chart-title">Fall Field Experiences by State — 2026 vs 2025 (as of {new Date().toLocaleDateString()})</h3>
            {(() => {
              const rows = Object.entries(feHires?.states || {})
                .map(([state, c]) => ({ state, 'Fall 2026': c.fall2026 || 0, 'Fall 2025': c.fall2025 || 0 }))
                .filter(r => r.state !== 'Unknown' && (r['Fall 2026'] > 0 || r['Fall 2025'] > 0))
                .sort((a, b) => b['Fall 2026'] - a['Fall 2026']);
              if (rows.length === 0) {
                return <div style={{ color: 'rgba(199,210,254,0.6)', padding: '1rem' }}>No Field Experience data available yet.</div>;
              }
              return (
                <ResponsiveContainer width="100%" height={Math.max(320, rows.length * 34)}>
                  <BarChart data={rows} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis type="number" stroke="#E0E7FF" />
                    <YAxis type="category" dataKey="state" stroke="#E0E7FF" width={130} fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                      labelStyle={{ color: '#E0E7FF' }}
                      formatter={(value) => value.toLocaleString()}
                    />
                    <Legend />
                    <Bar dataKey="Fall 2026" fill="#CFFB5E" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="Fall 2025" fill="#00BEA8" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
            <div className="cohort-note">
              <strong>Note:</strong> Field Experiences received/processed (Date Entered) through today for Fall 2026 vs. through the same date last year for Fall 2025, by candidate certification state. Shows all states regardless of the State filter above.
            </div>
          </div>
        );
      case 'state-matrix':
        return (
          <>
        {/* State Performance Matrix */}
        <div className="chart-card chart-full" style={{ marginTop: '2rem' }} id="state-matrix">
          <h3 className="chart-title">State Performance Matrix (Last 6 Months)</h3>
          <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem' }}>
            States plotted by conversion rate (X-axis) and median velocity (Y-axis). Faint trails show movement over the last 6 months.
          </div>

          {(() => {
            try {
            const data = metrics?.stateMatrixData || [];
            if (data.length === 0) return <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '2rem' }}>No state data available</div>;

            // Calculate dynamic domain with padding
            const allConvRates = data.map(d => d.conversionRate);
            const allVelocities = data.map(d => d.medianVelocity);
            // Include previous positions in domain calc
            data.forEach(d => {
              if (d.prevConversionRate !== null) allConvRates.push(d.prevConversionRate);
              if (d.prevMedianVelocity !== null) allVelocities.push(d.prevMedianVelocity);
            });

            const minConv = Math.min(...allConvRates);
            const maxConv = Math.max(...allConvRates);
            const minVel = Math.min(...allVelocities);
            const maxVel = Math.max(...allVelocities);

            // Add 10% padding on each side, but always include target lines (50%, 30 days)
            const padX = Math.max((maxConv - minConv) * 0.12, 5);
            const padY = Math.max((maxVel - minVel) * 0.12, 3);

            const domainXMin = Math.max(0, Math.min(minConv - padX, 45));
            const domainXMax = Math.min(100, Math.max(maxConv + padX, 55));
            const domainYMin = Math.max(0, Math.min(minVel - padY, 25));
            const domainYMax = Math.max(maxVel + padY, 35);

            // Chart dimensions
            const chartW = 900;
            const chartH = 400;
            const margin = { top: 20, right: 30, bottom: 50, left: 60 };
            const plotW = chartW - margin.left - margin.right;
            const plotH = chartH - margin.top - margin.bottom;

            // Scale functions (with NaN safeguards)
            const scaleX = (val) => {
              const range = domainXMax - domainXMin;
              if (range <= 0) return margin.left + plotW / 2;
              return margin.left + ((val - domainXMin) / range) * plotW;
            };
            const scaleY = (val) => {
              const range = domainYMax - domainYMin;
              if (range <= 0) return margin.top + plotH / 2;
              return margin.top + ((val - domainYMin) / range) * plotH;
            };

            // Generate tick values (with safeguards against infinite loops)
            const xTicks = [];
            const xRange = domainXMax - domainXMin;
            const xStep = xRange > 0 ? Math.max(Math.ceil(xRange / 6 / 5) * 5, 5) : 10;
            for (let v = Math.ceil(domainXMin / xStep) * xStep; v <= domainXMax && xTicks.length < 20; v += xStep) xTicks.push(v);

            const yTicks = [];
            const yRange = domainYMax - domainYMin;
            const yStep = yRange > 0 ? Math.max(Math.ceil(yRange / 5 / 5) * 5, 5) : 10;
            for (let v = Math.ceil(domainYMin / yStep) * yStep; v <= domainYMax && yTicks.length < 20; v += yStep) yTicks.push(v);

            // Color for quadrant
            const getColor = (q) => {
              if (q === 'Scale') return '#4BD48D';
              if (q === 'Operational upside') return '#CFFB5E';
              if (q === 'Lead quality issue') return '#FFA500';
              return '#FF6B6B';
            };

            // Target line positions
            const targetXPos = scaleX(50);
            const targetYPos = scaleY(30);
            const showTargetX = domainXMin <= 50 && domainXMax >= 50;
            const showTargetY = domainYMin <= 30 && domainYMax >= 30;

            return (
              <div style={{ width: '100%', overflowX: 'auto', position: 'relative' }}
                onMouseLeave={() => setMatrixHover(null)}
              >
                <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', height: 'auto', minHeight: '400px' }}>
                  <defs>
                    {/* Motion blur filter */}
                    <filter id="motionBlur" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
                    </filter>
                    {/* Glow for current dots */}
                    <filter id="dotGlow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Background grid */}
                  {xTicks.map(v => (
                    <line key={`gx-${v}`} x1={scaleX(v)} x2={scaleX(v)} y1={margin.top} y2={margin.top + plotH} stroke="rgba(255,255,255,0.06)" />
                  ))}
                  {yTicks.map(v => (
                    <line key={`gy-${v}`} x1={margin.left} x2={margin.left + plotW} y1={scaleY(v)} y2={scaleY(v)} stroke="rgba(255,255,255,0.06)" />
                  ))}

                  {/* Quadrant background shading */}
                  {showTargetX && showTargetY && (
                    <>
                      {/* Top-right: Scale (best) */}
                      <rect x={targetXPos} y={margin.top} width={margin.left + plotW - targetXPos} height={targetYPos - margin.top} fill="rgba(75, 212, 141, 0.04)" />
                      {/* Top-left: Lead quality issue */}
                      <rect x={margin.left} y={margin.top} width={targetXPos - margin.left} height={targetYPos - margin.top} fill="rgba(255, 165, 0, 0.03)" />
                      {/* Bottom-right: Operational upside */}
                      <rect x={targetXPos} y={targetYPos} width={margin.left + plotW - targetXPos} height={margin.top + plotH - targetYPos} fill="rgba(207, 251, 94, 0.03)" />
                      {/* Bottom-left: Structural risk */}
                      <rect x={margin.left} y={targetYPos} width={targetXPos - margin.left} height={margin.top + plotH - targetYPos} fill="rgba(255, 107, 107, 0.03)" />
                    </>
                  )}

                  {/* Target reference lines */}
                  {showTargetX && (
                    <>
                      <line x1={targetXPos} x2={targetXPos} y1={margin.top} y2={margin.top + plotH} stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} strokeDasharray="6 4" />
                      <text x={targetXPos + 4} y={margin.top + 14} fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="500">50% Target</text>
                    </>
                  )}
                  {showTargetY && (
                    <>
                      <line x1={margin.left} x2={margin.left + plotW} y1={targetYPos} y2={targetYPos} stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} strokeDasharray="6 4" />
                      <text x={margin.left + plotW - 75} y={targetYPos - 6} fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="500">30d Target</text>
                    </>
                  )}

                  {/* Motion trails: line from previous to current position */}
                  {data.map((d, i) => {
                    if (d.prevConversionRate === null || d.prevMedianVelocity === null) return null;
                    const x1 = scaleX(d.prevConversionRate);
                    const y1 = scaleY(d.prevMedianVelocity);
                    const x2 = scaleX(d.conversionRate);
                    const y2 = scaleY(d.medianVelocity);
                    const color = getColor(d.quadrant);
                    // Skip if barely moved
                    const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
                    if (dist < 3) return null;
                    const isHovered = matrixHover && matrixHover.data.state === d.state;
                    const isOtherHovered = matrixHover && matrixHover.data.state !== d.state;
                    return (
                      <g key={`trail-${i}`} style={{ transition: 'opacity 0.2s ease' }} opacity={isOtherHovered ? 0.08 : isHovered ? 1 : 1}>
                        {/* Faint blurred trail line */}
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={isHovered ? 4 : 3} opacity={isHovered ? 0.4 : 0.15} filter="url(#motionBlur)" />
                        {/* Sharper thin trail */}
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={isHovered ? 2.5 : 1.5} opacity={isHovered ? 0.6 : 0.3} />
                        {/* Ghost dot at previous position */}
                        <circle cx={x1} cy={y1} r={isHovered ? 6 : 4} fill={color} opacity={isHovered ? 0.35 : 0.15} />
                      </g>
                    );
                  })}

                  {/* State dots and labels */}
                  {data.map((d, i) => {
                    const cx = scaleX(d.conversionRate);
                    const cy = scaleY(d.medianVelocity);
                    const color = getColor(d.quadrant);
                    // Scale dot size by application volume
                    const r = Math.min(Math.max(Math.sqrt(d.applied) * 0.7, 5), 14);
                    const isHovered = matrixHover && matrixHover.data.state === d.state;
                    const isOtherHovered = matrixHover && matrixHover.data.state !== d.state;
                    return (
                      <g key={`dot-${i}`}
                        onMouseEnter={(e) => {
                          const svgRect = e.currentTarget.closest('svg').getBoundingClientRect();
                          const pctX = (cx / chartW);
                          const pctY = (cy / chartH);
                          setMatrixHover({
                            data: d,
                            x: pctX * svgRect.width,
                            y: pctY * svgRect.height
                          });
                        }}
                        onMouseLeave={() => setMatrixHover(null)}
                        style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                      >
                        {/* Invisible larger hit area */}
                        <circle cx={cx} cy={cy} r={Math.max(r + 6, 14)} fill="transparent" />
                        {/* Highlight ring on hover */}
                        {isHovered && (
                          <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke={color} strokeWidth={2} opacity={0.5} filter="url(#dotGlow)" />
                        )}
                        <circle
                          cx={cx} cy={cy} r={isHovered ? r + 2 : r}
                          fill={color}
                          opacity={isOtherHovered ? 0.15 : isHovered ? 1 : 0.85}
                          filter={isHovered ? 'url(#dotGlow)' : 'none'}
                          style={{ transition: 'opacity 0.2s ease, r 0.2s ease' }}
                        />
                        <text
                          x={cx}
                          y={cy - (isHovered ? r + 6 : r) - 4}
                          textAnchor="middle"
                          fill="#FFFFFF"
                          fontSize={isHovered ? '12' : '10'}
                          fontWeight="600"
                          opacity={isOtherHovered ? 0.15 : 1}
                          style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)', pointerEvents: 'none', transition: 'opacity 0.2s ease' }}
                        >
                          {getStateAbbrev(d.state)}
                        </text>
                      </g>
                    );
                  })}

                  {/* Axes */}
                  <line x1={margin.left} x2={margin.left + plotW} y1={margin.top + plotH} y2={margin.top + plotH} stroke="#E0E7FF" strokeWidth={1} />
                  <line x1={margin.left} x2={margin.left} y1={margin.top} y2={margin.top + plotH} stroke="#E0E7FF" strokeWidth={1} />

                  {/* X-axis ticks and labels */}
                  {xTicks.map(v => (
                    <g key={`xt-${v}`}>
                      <line x1={scaleX(v)} x2={scaleX(v)} y1={margin.top + plotH} y2={margin.top + plotH + 5} stroke="#E0E7FF" />
                      <text x={scaleX(v)} y={margin.top + plotH + 18} textAnchor="middle" fill="#E0E7FF" fontSize="11">{v}%</text>
                    </g>
                  ))}
                  <text x={margin.left + plotW / 2} y={chartH - 5} textAnchor="middle" fill="#E0E7FF" fontSize="12">Conversion Rate (%)</text>

                  {/* Y-axis ticks and labels */}
                  {yTicks.map(v => (
                    <g key={`yt-${v}`}>
                      <line x1={margin.left - 5} x2={margin.left} y1={scaleY(v)} y2={scaleY(v)} stroke="#E0E7FF" />
                      <text x={margin.left - 10} y={scaleY(v) + 4} textAnchor="end" fill="#E0E7FF" fontSize="11">{v}d</text>
                    </g>
                  ))}
                  <text x={15} y={margin.top + plotH / 2} textAnchor="middle" fill="#E0E7FF" fontSize="12" transform={`rotate(-90, 15, ${margin.top + plotH / 2})`}>Median Velocity (days)</text>
                </svg>

                {/* Hover tooltip - positioned well clear of the dot and trail */}
                {matrixHover && (() => {
                  // Position tooltip to the right by default, flip left if near right edge
                  const tooltipW = 190;
                  const tooltipH = 140;
                  const offsetX = 45;
                  const offsetY = -tooltipH / 2;
                  // Get container width to check if tooltip would overflow
                  const flipRight = matrixHover.x + offsetX + tooltipW > (chartW * 0.9);
                  const finalX = flipRight ? matrixHover.x - offsetX - tooltipW : matrixHover.x + offsetX;
                  const finalY = Math.max(5, Math.min(matrixHover.y + offsetY, 350));
                  return (
                    <div style={{
                      position: 'absolute',
                      left: `${finalX}px`,
                      top: `${finalY}px`,
                      backgroundColor: 'rgba(30, 27, 75, 0.95)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      pointerEvents: 'none',
                      zIndex: 10,
                      minWidth: '160px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#FFFFFF', fontSize: '0.95rem' }}>{matrixHover.data.state}</div>
                      <div style={{ fontSize: '0.85rem', color: '#FFFFFF', marginBottom: '2px' }}>Conversion: {matrixHover.data.conversionRate}%</div>
                      <div style={{ fontSize: '0.85rem', color: '#FFFFFF', marginBottom: '2px' }}>Velocity: {matrixHover.data.medianVelocity} days</div>
                      <div style={{ fontSize: '0.85rem', color: '#FFFFFF', marginBottom: '2px' }}>Applications: {matrixHover.data.applied}</div>
                      {matrixHover.data.prevConversionRate !== null && (
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                          6mo ago: {matrixHover.data.prevConversionRate}% conv, {matrixHover.data.prevMedianVelocity}d vel
                        </div>
                      )}
                      <div style={{ fontSize: '0.85rem', marginTop: '6px', fontWeight: 'bold', color: '#CFFB5E' }}>{matrixHover.data.quadrant}</div>
                    </div>
                  );
                })()}
              </div>
            );
            } catch (err) {
              console.error('State Matrix render error:', err);
              return <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '2rem' }}>Error rendering state matrix. Check console for details.</div>;
            }
          })()}

          {/* Legend */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#4BD48D', borderRadius: '50%' }}></div>
              <span style={{ fontSize: '0.875rem' }}>Scale (High conv, Fast)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#CFFB5E', borderRadius: '50%' }}></div>
              <span style={{ fontSize: '0.875rem' }}>Operational upside (High conv, Slow)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#FFA500', borderRadius: '50%' }}></div>
              <span style={{ fontSize: '0.875rem' }}>Lead quality issue (Low conv, Fast)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#FF6B6B', borderRadius: '50%' }}></div>
              <span style={{ fontSize: '0.875rem' }}>Structural risk (Low conv, Slow)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: '1 / -1' }}>
              <div style={{ width: '24px', height: '2px', background: 'linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.4))', borderRadius: '1px' }}></div>
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>Trail shows movement from 6 months ago → current</span>
            </div>
          </div>
        </div>
          </>
        );
      case 'state-mom':
        return (
          <>
        {/* State MoM Enrollment Comparison */}
        <div className="mom-comparison-card">
          <h2 className="section-title">
            <TrendingUp size={20} />
            <span>State Enrollment Trends (Rolling 30 Days vs Prior 30 Days)</span>
          </h2>

          <div className="mom-comparison-grid">
            {/* Top Performers */}
            <div className="mom-column">
              <h3 className="mom-column-title mom-positive">📈 Top Performers</h3>
              <div className="mom-state-list">
                {(metrics?.topMoMStates || []).map((state, idx) => (
                  <div key={state.state} className="mom-state-item">
                    <div className="mom-state-rank">{idx + 1}</div>
                    <div className="mom-state-info">
                      <div className="mom-state-name">{state.state}</div>
                      <div className="mom-state-details">
                        {state.currentPeriod} enrollments ({state.change >= 0 ? '+' : ''}{state.change} from prior)
                      </div>
                    </div>
                    <div className={`mom-state-growth ${state.momGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                      {state.momGrowth >= 0 ? '+' : ''}{state.momGrowth}%
                    </div>
                  </div>
                ))}
                {(!metrics?.topMoMStates || metrics.topMoMStates.length === 0) && (
                  <div className="mom-no-data">No data available</div>
                )}
              </div>
            </div>

            {/* Bottom Performers */}
            <div className="mom-column">
              <h3 className="mom-column-title mom-negative">📉 Needs Attention</h3>
              <div className="mom-state-list">
                {(metrics?.bottomMoMStates || []).map((state, idx) => (
                  <div key={state.state} className="mom-state-item">
                    <div className="mom-state-rank">{idx + 1}</div>
                    <div className="mom-state-info">
                      <div className="mom-state-name">{state.state}</div>
                      <div className="mom-state-details">
                        {state.currentPeriod} enrollments ({state.change >= 0 ? '+' : ''}{state.change} from prior)
                      </div>
                    </div>
                    <div className={`mom-state-growth ${state.momGrowth >= 0 ? 'growth-positive' : 'growth-negative'}`}>
                      {state.momGrowth >= 0 ? '+' : ''}{state.momGrowth}%
                    </div>
                  </div>
                ))}
                {(!metrics?.bottomMoMStates || metrics.bottomMoMStates.length === 0) && (
                  <div className="mom-no-data">No data available</div>
                )}
              </div>
            </div>
          </div>
        </div>

          </>
        );
      case 'hired-comparison':
        return (
          <>
        <div className="chart-card chart-full">
          <h3 className="chart-title">Hired Candidate Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metrics?.semesterComparison || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="term" stroke="#E0E7FF" angle={-45} textAnchor="end" height={80} />
              <YAxis stroke="#E0E7FF" label={{ value: 'Hired Count', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                labelStyle={{ color: '#E0E7FF' }}
                itemStyle={{ color: '#FFFFFF' }}
                formatter={(value) => [value.toLocaleString(), 'Hired Count']}
              />
              <Legend
                payload={[
                  { value: 'Spring', type: 'square', color: '#00BEA8' },
                  { value: 'Fall', type: 'square', color: '#CFFB5E' }
                ]}
              />
              <Bar dataKey="enrolled" name="Hired Count">
                {(metrics?.semesterComparison || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.termOnly === 'Spring' ? '#00BEA8' : '#CFFB5E'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
          </>
        );
      case 'eval-health':
        return (
          <>
        <div className="eval-health-section">
          <h2 className="section-title">
            <span>Evaluation Health</span>
          </h2>
          <div className="highlights-grid">
            <div className="highlight-card">
              <div className="neat-fact-content">
                <h4 className="neat-fact-title">Accepted, Not Enrolled</h4>
                <div className="neat-fact-value">
                  {(metrics?.evalAcceptedNotEnrolledCount || 0).toLocaleString()}
                </div>
                <div className="neat-fact-description">
                  Avg wait: <span style={{ color: (metrics?.evalAcceptedNotEnrolledAvgAge || 0) > 30 ? '#F59E0B' : '#10B981', fontWeight: 600 }}>
                    {metrics?.evalAcceptedNotEnrolledAvgAge || 0} days
                  </span>
                  <span style={{ opacity: 0.6 }}> (past 2 years)</span>
                </div>
              </div>
            </div>

            <div className="highlight-card">
              <div className="neat-fact-content">
                <h4 className="neat-fact-title">Evaluations Completed</h4>
                <div className="neat-fact-value">
                  {metrics?.evalCompletedWTD || 0} <span className="neat-fact-unit">WTD</span>
                  <span style={{ margin: '0 0.4rem', opacity: 0.3 }}>|</span>
                  {metrics?.evalCompletedMTD || 0} <span className="neat-fact-unit">MTD</span>
                </div>
                <div className="neat-fact-description">
                  Week-to-date and month-to-date
                </div>
              </div>
            </div>

            <div className="highlight-card">
              <div className="neat-fact-content">
                <h4 className="neat-fact-title">Currently in Evaluations</h4>
                <div className="neat-fact-value">
                  {(metrics?.currentlyInEvaluations || 0).toLocaleString()}
                </div>
                <div className="neat-fact-description">
                  Active evaluation queue
                </div>
              </div>
            </div>
          </div>
        </div>
          </>
        );
      case 'forecast-readiness':
        const fr = metrics?.forecastReadiness;
        const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '0';
        if (!fr || fr.forecastTarget === 0) return (
          <>
          <div className="forecast-readiness-wrapper">
            <h2 className="section-title"><span>Forecast Readiness</span></h2>
            <p style={{ color: '#c7d2fe', fontSize: '0.9rem', marginTop: '1rem' }}>
              Forecast data not available for the selected period.
            </p>
          </div>
          </>
        );
        const rateDelta = (cur, prev) => {
          const diff = parseFloat(cur) - parseFloat(prev);
          if (Math.abs(diff) < 0.1) return null;
          return diff;
        };
        return (
          <>
        <div className="forecast-readiness-wrapper">
          <h2 className="section-title"><span>Forecast Readiness</span></h2>
          <p className="forecast-subtitle">Next 3 months: Can your pipeline deliver? <span style={{ opacity: 0.5 }}>(pipeline = applied within 180 days)</span></p>

          {generateForecastFocus() && (
            <div className="card-focus-directive" style={{ marginBottom: '1rem' }}>
              <strong className="focus-label">Needed Focus:</strong> {generateForecastFocus()}
            </div>
          )}

          {/* Status Banner + Prescriptions combined */}
          {(() => {
            const pipelineOnTrack = fr.onTrack;
            const paceOnTrack = fr.runRate?.onTrack;
            const bothOnTrack = pipelineOnTrack && paceOnTrack;
            const pipelineOnlyOn = pipelineOnTrack && !paceOnTrack;
            const bothOff = !pipelineOnTrack && !paceOnTrack;
            // Status: green only if BOTH agree, amber if mixed, amber/red if both short
            const statusClass = bothOnTrack ? 'forecast-on-track' : (bothOff ? 'forecast-at-risk' : 'forecast-caution');
            const statusLabel = bothOnTrack ? 'On Track' : (pipelineOnlyOn ? 'Caution — Pace Lagging' : 'Gap Identified');
            const showRx = !bothOnTrack;
            return (
            <div className={`forecast-status ${statusClass}`}>
              <div className="forecast-status-top">
                <div>
                  <div className="forecast-status-label">{statusLabel}</div>
                  <div className="forecast-status-detail">
                    {bothOnTrack 
                      ? `Pipeline projects ${fmtNum(fr.projectedEnrollments)} enrollments vs ${fmtNum(fr.forecastTarget)} target`
                      : pipelineOnlyOn
                        ? `Pipeline model projects ${fmtNum(fr.projectedEnrollments)} (on track), but actual pace projects only ${fmtNum(fr.runRate.projection3Mo)} vs ${fmtNum(fr.forecastTarget)} target`
                        : `Pipeline projects ${fmtNum(fr.projectedEnrollments)}, pace projects ${fmtNum(fr.runRate.projection3Mo)} — need ${fmtNum(fr.forecastTarget)} (gap: ${fmtNum(Math.max(fr.gap, fr.runRate.gap))})`
                    }
                  </div>
                </div>
                {showRx && (
                  <div className="forecast-rx-inline">
                    <div className="forecast-rx">
                      <div className="forecast-rx-tag">Option A: Volume</div>
                      <div className="forecast-rx-value">{fmtNum(fr.prescriptionA.additionalAppsNeeded)}</div>
                      <div className="forecast-rx-label">additional apps needed</div>
                      <div className="forecast-rx-sub">At current {fr.prescriptionB.currentConversion}% rate</div>
                    </div>
                    <div className="forecast-rx-or">or</div>
                    <div className="forecast-rx">
                      <div className="forecast-rx-tag">Option B: Conversion</div>
                      <div className="forecast-rx-value">{fr.prescriptionB.requiredConversion}%</div>
                      <div className="forecast-rx-label">conversion rate needed</div>
                      <div className="forecast-rx-sub">Up from current {fr.prescriptionB.currentConversion}%</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Metrics Row */}
          <div className="forecast-metrics-row">
            <div className="forecast-metric">
              <div className="forecast-metric-value">{fmtNum(fr.forecastTarget)}</div>
              <div className="forecast-metric-label">Forecast Target</div>
              <div className="forecast-metric-sub">Next 3 months</div>
            </div>
            <div className="forecast-metric">
              <div className="forecast-metric-value" style={{ color: fr.onTrack ? '#4ADE80' : '#FCD34D' }}>{fmtNum(fr.projectedEnrollments)}</div>
              <div className="forecast-metric-label">Pipeline Model Projection</div>
              <div className="forecast-metric-sub">Current pipeline + forecasted additions</div>
            </div>
            <div className="forecast-metric">
              <div className="forecast-metric-value">{fr.historicalConversionRate}%</div>
              <div className="forecast-metric-label">6mo Conversion Rate</div>
              <div className="forecast-metric-sub">Applied → Enrolled</div>
            </div>
            <div className="forecast-metric">
              <div className="forecast-metric-value">{fmtNum(fr.pipeline.total)}</div>
              <div className="forecast-metric-label">Active Pipeline</div>
              <div className="forecast-metric-sub">{fmtNum(fr.pipeline.applied)} applied · {fmtNum(fr.pipeline.evaluated)} eval · {fmtNum(fr.pipeline.accepted)} accepted</div>
            </div>
          </div>

          {/* Pipeline vs Run Rate comparison */}
          <div className="forecast-comparison">
            <h3 className="forecast-section-title">Pipeline Model vs Real-Time Pace</h3>
            <div className="forecast-comparison-bars">
              <div className="forecast-bar-group">
                <div className="forecast-bar-label">Forecast Target</div>
                <div className="forecast-bar-track">
                  <div className="forecast-bar-fill forecast-bar-target" style={{ width: '100%' }}></div>
                </div>
                <div className="forecast-bar-value">{fmtNum(fr.forecastTarget)}</div>
              </div>
              <div className="forecast-bar-group">
                <div className="forecast-bar-label">Pipeline Model</div>
                <div className="forecast-bar-track">
                  <div className={`forecast-bar-fill ${fr.onTrack ? 'forecast-bar-good' : 'forecast-bar-warn'}`} style={{ width: `${Math.min((fr.projectedEnrollments / fr.forecastTarget) * 100, 100)}%` }}></div>
                </div>
                <div className="forecast-bar-value" style={{ color: fr.onTrack ? '#4ADE80' : '#FCD34D' }}>{fmtNum(fr.projectedEnrollments)}</div>
              </div>
              <div className="forecast-bar-group">
                <div className="forecast-bar-label">Real-Time Pace</div>
                <div className="forecast-bar-track">
                  <div className={`forecast-bar-fill ${fr.runRate.onTrack ? 'forecast-bar-good' : 'forecast-bar-danger'}`} style={{ width: `${Math.min((fr.runRate.projection3Mo / fr.forecastTarget) * 100, 100)}%` }}></div>
                </div>
                <div className="forecast-bar-value" style={{ color: fr.runRate.onTrack ? '#4ADE80' : '#F87171' }}>{fmtNum(fr.runRate.projection3Mo)}</div>
              </div>
            </div>
            <div className="forecast-comparison-detail">
              <div className="forecast-rate-compare">
                <span className="forecast-rate-chip">
                  <span style={{ color: '#c7d2fe' }}>6mo Conversion Rate:</span>{' '}
                  <strong style={{ color: '#00BEA8' }}>{fr.historicalConversionRate}%</strong>
                  <span style={{ opacity: 0.5 }}> (pipeline model uses this)</span>
                </span>
                <span className="forecast-rate-chip">
                  <span style={{ color: '#c7d2fe' }}>Real-Time Rate (14-90 day cohort):</span>{' '}
                  <strong style={{ color: parseFloat(fr.runRate.adjustedRate) >= parseFloat(fr.historicalConversionRate) ? '#4ADE80' : '#FCD34D' }}>{fr.runRate.adjustedRate}%</strong>
                  <span style={{ opacity: 0.5 }}> ({fmtNum(fr.runRate.adjCohortEnrolled)} of {fmtNum(fr.runRate.adjCohortApps)} apps)</span>
                </span>
                <span className="forecast-rate-chip">
                  <span style={{ color: '#c7d2fe' }}>Pace vs Forecast:</span>{' '}
                  <strong style={{ color: parseFloat(fr.runRate.paceRatio) >= 100 ? '#4ADE80' : '#FCD34D' }}>{fr.runRate.paceRatio}%</strong>
                  <span style={{ opacity: 0.5 }}> of expected conversion pace</span>
                </span>
                {fr.runRate.yoyChange !== null && (
                  <span className="forecast-rate-chip">
                    <span style={{ color: '#c7d2fe' }}>YoY (same window last year):</span>{' '}
                    <strong style={{ color: fr.runRate.yoyChange >= 0 ? '#4ADE80' : '#EF4444' }}>
                      {fr.runRate.yoyChange >= 0 ? '+' : ''}{fr.runRate.yoyChange}%
                    </strong>
                    <span style={{ opacity: 0.5 }}> ({fr.runRate.priorYearRate}% → {fr.runRate.adjustedRate}%)</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Real-Time Conversion Trend */}
          {fr.runRate.trend && fr.runRate.trend.length > 0 && (
            <div className="forecast-funnel" style={{ marginTop: '0.75rem' }}>
              <h3 className="forecast-section-title">
                Real-Time Conversion Trend <span style={{ fontWeight: 400, opacity: 0.5 }}>— 14-90 day cohort rate, rolling monthly</span>
              </h3>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', padding: '0.75rem 0' }}>
                {fr.runRate.trend.map((point, i) => {
                  const maxRate = Math.max(...fr.runRate.trend.map(p => p.rate), 1);
                  const barH = Math.max((point.rate / maxRate) * 120, 4);
                  const isLast = i === fr.runRate.trend.length - 1;
                  const prevRate = i > 0 ? fr.runRate.trend[i - 1].rate : null;
                  const trending = prevRate !== null ? (point.rate > prevRate ? 'up' : point.rate < prevRate ? 'down' : 'flat') : 'flat';
                  const barColor = isLast ? '#818CF8' : point.rate >= parseFloat(fr.historicalConversionRate) ? '#4ADE80' : '#FCD34D';
                  return (
                    <div key={point.label} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: isLast ? 700 : 600, color: barColor, marginBottom: '0.25rem' }}>
                        {point.rate}%
                      </div>
                      <div style={{
                        height: `${barH}px`, background: barColor, borderRadius: '4px 4px 0 0',
                        opacity: isLast ? 1 : 0.7, transition: 'height 0.3s ease',
                        border: isLast ? '1px solid rgba(129,140,248,0.5)' : 'none',
                      }} title={`${point.apps} apps → ${point.enrolled} enrolled`} />
                      <div style={{ fontSize: '0.62rem', color: 'rgba(199,210,254,0.5)', marginTop: '0.3rem' }}>{point.label}</div>
                      <div style={{ fontSize: '0.58rem', color: 'rgba(199,210,254,0.35)', marginTop: '0.1rem' }}>
                        {point.enrolled}/{point.apps}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'rgba(199,210,254,0.4)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.5rem' }}>
                <span>6mo Historical Rate: <strong style={{ color: '#c7d2fe' }}>{fr.historicalConversionRate}%</strong></span>
                <span>
                  Trend: <strong style={{ color:
                    fr.runRate.trend.length >= 2 && fr.runRate.trend[fr.runRate.trend.length - 1].rate > fr.runRate.trend[fr.runRate.trend.length - 2].rate ? '#4ADE80' :
                    fr.runRate.trend.length >= 2 && fr.runRate.trend[fr.runRate.trend.length - 1].rate < fr.runRate.trend[fr.runRate.trend.length - 2].rate ? '#EF4444' : 'rgba(199,210,254,0.6)'
                  }}>
                    {fr.runRate.trend.length >= 2 && fr.runRate.trend[fr.runRate.trend.length - 1].rate > fr.runRate.trend[fr.runRate.trend.length - 2].rate ? '↑ Improving' :
                     fr.runRate.trend.length >= 2 && fr.runRate.trend[fr.runRate.trend.length - 1].rate < fr.runRate.trend[fr.runRate.trend.length - 2].rate ? '↓ Declining' : '→ Flat'}
                  </strong>
                </span>
              </div>
            </div>
          )}

          {/* Stage Conversion Funnel with Prior Period */}
          <div className="forecast-funnel">
            <h3 className="forecast-section-title">Stage Conversion Rates <span style={{ fontWeight: 400, opacity: 0.5 }}>— last 6mo vs prior period (shifted 1mo)</span></h3>
            <div className="forecast-funnel-row">
              {[
                { label: 'Applied → Evaluated', cur: fr.stageRates.applyToEval, prev: fr.priorStageRates.applyToEval },
                { label: 'Evaluated → Accepted', cur: fr.stageRates.evalToAccept, prev: fr.priorStageRates.evalToAccept },
                { label: 'Accepted → Enrolled', cur: fr.stageRates.acceptToEnroll, prev: fr.priorStageRates.acceptToEnroll },
              ].map((stage, i) => {
                const delta = rateDelta(stage.cur, stage.prev);
                return (
                  <React.Fragment key={stage.label}>
                    {i > 0 && <div className="forecast-funnel-arrow">→</div>}
                    <div className="forecast-funnel-stage">
                      <div className="forecast-funnel-rate">{stage.cur}%</div>
                      <div className="forecast-funnel-label">{stage.label}</div>
                      <div className="forecast-funnel-prior">
                        <span style={{ opacity: 0.7 }}>was {stage.prev}%</span>
                        {delta !== null && (
                          <span style={{ color: delta > 0 ? '#4ADE80' : '#FCD34D', fontWeight: 700, marginLeft: '0.35rem', fontSize: '0.8rem' }}>
                            {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}pp
                          </span>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="forecast-velocity">Median time: Applied → Enrolled = <strong>{fr.medianVelocityDays} days</strong></div>
          </div>

          {/* At-Risk States */}
          {fr.atRiskStates && fr.atRiskStates.length > 0 && (
            <div className="forecast-at-risk-states">
              <h3 className="forecast-section-title">States Most at Risk</h3>
              <div className="forecast-states-table">
                <div className="forecast-states-header">
                  <span>State</span>
                  <span>Pipeline</span>
                  <span>Conv Rate</span>
                  <span>Projected</span>
                  <span>Target</span>
                  <span>Gap</span>
                </div>
                {fr.atRiskStates.map(s => (
                  <div key={s.state} className="forecast-states-row">
                    <span className="forecast-state-name">{s.state}</span>
                    <span>{fmtNum(s.pipeline)}</span>
                    <span>{s.conversionRate}%</span>
                    <span>{fmtNum(s.projected)}</span>
                    <span>{fmtNum(s.forecast)}</span>
                    <span className="forecast-gap-value">-{fmtNum(s.gap)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
          </>
        );
      case 'pipeline-health':
        return <PipelineHealth />;
      default:
        return null;
    }
  };


  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="header">
          <div className="header-left">
            <img src={`${process.env.PUBLIC_URL}/iteach-logo.png`} alt="iteach logo" className="header-logo" />
            <div className="header-text">
              <h1 className="title">Pipeline Report</h1>
              <p className="subtitle">High level review of key pipeline data points</p>
            </div>
          </div>
          {/* Dashboard Selector */}
          <div className="dashboard-selector-container">
            <label htmlFor="dashboard-select" className="state-filter-label">Dashboard:</label>
            <select
              id="dashboard-select"
              className="state-filter-dropdown"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              {dashboardAccess.includes('funnel') && <option value="funnel">Funnel Report</option>}
              {dashboardAccess.includes('hiring') && <option value="hiring">Hiring Forecast</option>}
              {dashboardAccess.includes('marketing') && <option value="marketing">Marketing Metrics</option>}
              {dashboardAccess.includes('duration') && <option value="duration">Program Duration</option>}
              {dashboardAccess.includes('partnerships') && <option value="partnerships">Partnerships</option>}
              {dashboardAccess.includes('email') && <option value="email">Monthly Email</option>}
              {dashboardAccess.includes('sfquery') && <option value="sfquery">SF Query</option>}
              {dashboardAccess.includes('testaudit') && <option value="testaudit">Test Entry Audit</option>}
            </select>
          </div>
          <div className="header-right">
            <div className="state-filter-container">
              <label htmlFor="state-filter" className="state-filter-label">Filter by State:</label>
              <select 
                id="state-filter"
                className="state-filter-dropdown"
                value={selectedState}
                onChange={(e) => { 
                  setSelectedState(e.target.value); 
                  trackActivity('filter_change', { state: e.target.value }); 
                }}
              >
                {availableStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <button onClick={() => { exportPDF(); trackActivity('pdf_export', { state: selectedState }); }} className="refresh-button" style={{ backgroundColor: 'rgba(0, 190, 168, 0.2)', borderColor: 'rgba(0, 190, 168, 0.4)' }}>
              <Download size={16} />
              <span>Export PDF</span>
            </button>
            <button onClick={() => { fetchData(); trackActivity('data_refresh'); }} className="refresh-button">
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
            {authUser && authUser.email !== 'guest@accesscode' && (
              <div className="user-menu">
                {isAdmin && (
                  <button onClick={() => setShowAdmin(!showAdmin)} className="admin-btn" title="Usage Analytics">
                    📊
                  </button>
                )}
                <button onClick={() => setShowSettings(!showSettings)} className="settings-button" title="Customize dashboard">
                  <Settings size={16} />
                </button>
                {authUser.picture ? (
                  <img src={authUser.picture} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                ) : (
                  <div className="user-avatar user-avatar-placeholder">
                    {authUser.name?.charAt(0) || authUser.email?.charAt(0) || '?'}
                  </div>
                )}
                <button onClick={handleLogout} className="logout-button" title="Sign out">
                  <LogOut size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="settings-panel">
            <div className="settings-header">
              <h3 className="settings-title">
                <Settings size={18} />
                Customize Dashboard
              </h3>
              <div className="settings-actions">
                <button className="settings-reset" onClick={resetCardConfig}>Reset to Default</button>
                <button className="settings-close" onClick={() => setShowSettings(false)}>✕</button>
              </div>
            </div>
            <p className="settings-hint">Drag to reorder • Toggle visibility • Changes save automatically</p>
            <div className="settings-card-list">
              {cardConfig.sort((a, b) => a.order - b.order).map((card, idx) => {
                const reg = CARD_REGISTRY.find(r => r.id === card.id);
                if (!reg) return null;
                return (
                  <div
                    key={card.id}
                    className={`settings-card-item ${dragItem === idx ? 'settings-card-dragging' : ''} ${!card.visible ? 'settings-card-hidden' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="settings-card-grip">
                      <GripVertical size={16} />
                    </div>
                    <span className="settings-card-icon">{reg.icon}</span>
                    <span className="settings-card-label">{reg.label}</span>
                    <button 
                      className="settings-card-toggle"
                      onClick={() => toggleCard(card.id)}
                      title={card.visible ? 'Hide' : 'Show'}
                    >
                      {card.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Admin Analytics Panel */}
        {showAdmin && isAdmin && (
          <AdminPanel onClose={() => setShowAdmin(false)} currentUserEmail={authUser?.email} />
        )}

        {/* Dashboard Cards — rendered in user-configured order */}
        {activeTab === 'funnel' && (
          <>
            {getOrderedCards().filter(c => c.visible).map(card => (
              <div key={card.id}>{renderCard(card.id)}</div>
            ))}
          </>
        )}

        {/* Hiring Forecast */}
        {activeTab === 'hiring' && dashboardAccess.includes('hiring') && (
          <HiringForecast candidateData={rawData} />
        )}

        {/* Marketing Metrics */}
        {activeTab === 'marketing' && dashboardAccess.includes('marketing') && (
          <>
            <SFMarketingAttribution />
            <div style={{ marginTop: '2rem' }} />
            <MarketingMetrics candidateData={rawData} />
          </>
        )}

        {/* Program Duration */}
        {activeTab === 'duration' && (
          <div className="duration-tab">
            {durationLoading && (
              <div className="duration-loading">
                <RefreshCw size={24} className="spinning" />
                <p>Loading program duration data from Salesforce...</p>
              </div>
            )}
            {durationError && (
              <div className="duration-error">
                <p>Error loading duration data: {durationError}</p>
                <button onClick={() => { setDurationData(null); fetchDurationData(); }} className="refresh-button">
                  <RefreshCw size={16} />
                  <span>Retry</span>
                </button>
              </div>
            )}
            {durationData && (() => {
              // Apply time range filter to students
              const cutoffDate = durationTimeRange === 'all' ? null : new Date();
              if (cutoffDate) {
                const months = { '6m': 6, '12m': 12, '24m': 24 }[durationTimeRange] || 0;
                cutoffDate.setMonth(cutoffDate.getMonth() - months);
              }
              const filteredStudents = cutoffDate
                ? durationData.students.filter(s => new Date(s.startDate) >= cutoffDate)
                : durationData.students;

              // Rebuild summary from filtered students
              const stateMap = {};
              for (const s of filteredStudents) {
                if (!stateMap[s.state]) stateMap[s.state] = { state: s.state, students: [] };
                stateMap[s.state].students.push(s);
              }
              const avg = (arr) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
              const median = (arr) => {
                if (arr.length === 0) return null;
                const sorted = [...arr].sort((a, b) => a - b);
                const mid = Math.floor(sorted.length / 2);
                return sorted.length % 2 !== 0 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
              };
              const filteredSummary = Object.values(stateMap).map(s => {
                const durations = s.students.map(c => c.durationDays);
                return {
                  state: s.state,
                  count: s.students.length,
                  avgDays: avg(durations),
                  medianDays: median(durations),
                  minDays: Math.min(...durations),
                  maxDays: Math.max(...durations),
                };
              }).sort((a, b) => b.count - a.count);

              const overallAvg = filteredStudents.length > 0
                ? Math.round(filteredStudents.reduce((sum, s) => sum + s.durationDays, 0) / filteredStudents.length)
                : null;

              return (
              <>
                <div className="duration-filter-bar">
                  <label className="duration-filter-label">Time Range:</label>
                  <select
                    className="duration-filter-dropdown"
                    value={durationTimeRange}
                    onChange={(e) => setDurationTimeRange(e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="6m">Last 6 Months</option>
                    <option value="12m">Last 12 Months</option>
                    <option value="24m">Last 24 Months</option>
                  </select>
                  <button
                    className="duration-export-btn"
                    onClick={() => {
                      const abbrevMapFull = {
                        'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
                        'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
                        'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
                        'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                        'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
                        'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
                        'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
                        'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                        'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
                        'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
                        'District of Columbia': 'DC'
                      };
                      const st = abbrevMapFull[selectedState] || (selectedState.length === 2 ? selectedState : 'VA');
                      window.open(`/.netlify/functions/course-progress-report?state=${st}&since=2023-09-01`, '_blank');
                    }}
                  >
                    <Download size={16} />
                    <span>Full Progress Report</span>
                  </button>
                  <button
                    className="duration-export-btn"
                    onClick={() => {
                      const stateFilter = selectedState !== 'All States';
                      const abbrevMap = {
                        'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
                        'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
                        'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
                        'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                        'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
                        'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
                        'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
                        'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                        'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
                        'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
                        'District of Columbia': 'DC'
                      };
                      const exportData = stateFilter
                        ? filteredStudents.filter(s => s.state === abbrevMap[selectedState] || s.state === selectedState)
                        : filteredStudents;
                      const csvRows = [
                        ['Name', 'State', 'District', 'Start Date', 'End Date', 'Duration (Days)', 'Duration (Months)'].join(','),
                        ...exportData.map(s => [
                          `"${s.contactName}"`,
                          s.state,
                          `"${s.district || ''}"`,
                          new Date(s.startDate).toLocaleDateString(),
                          new Date(s.endDate).toLocaleDateString(),
                          s.durationDays,
                          (s.durationDays / 30.44).toFixed(1)
                        ].join(','))
                      ];
                      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `program-duration-${stateFilter ? abbrevMap[selectedState] || selectedState : 'all'}-${durationTimeRange}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download size={16} />
                    <span>Export CSV</span>
                  </button>
                </div>

                <div className="duration-summary-cards">
                  <div className="duration-stat-card">
                    <div className="duration-stat-value">{filteredStudents.length}</div>
                    <div className="duration-stat-label">Completed Students</div>
                  </div>
                  <div className="duration-stat-card">
                    <div className="duration-stat-value">{filteredSummary.length}</div>
                    <div className="duration-stat-label">States</div>
                  </div>
                  <div className="duration-stat-card">
                    <div className="duration-stat-value">
                      {overallAvg != null ? `${overallAvg} days` : 'N/A'}
                    </div>
                    <div className="duration-stat-label">Overall Avg Duration</div>
                  </div>
                  <div className="duration-stat-card">
                    <div className="duration-stat-value">
                      {overallAvg != null ? `${(overallAvg / 30.44).toFixed(1)} mo` : 'N/A'}
                    </div>
                    <div className="duration-stat-label">Overall Avg (Months)</div>
                  </div>
                </div>

                <div className="chart-card chart-full">
                  <h3 className="chart-title">Average Program Duration by State</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={[...filteredSummary].sort((a, b) => a.avgDays - b.avgDays)}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="state" stroke="#E0E7FF" angle={-45} textAnchor="end" height={60} />
                      <YAxis stroke="#E0E7FF" label={{ value: 'Days', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                        labelStyle={{ color: '#E0E7FF' }}
                        formatter={(value, name) => {
                          if (name === 'avgDays') return [`${value} days`, 'Avg Duration'];
                          if (name === 'medianDays') return [`${value} days`, 'Median Duration'];
                          return [value, name];
                        }}
                      />
                      <Legend
                        payload={[
                          { value: 'Avg Duration', type: 'square', color: '#00BEA8' },
                          { value: 'Median Duration', type: 'square', color: '#CFFB5E' },
                        ]}
                      />
                      <Bar dataKey="avgDays" name="avgDays" fill="#00BEA8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="medianDays" name="medianDays" fill="#CFFB5E" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card chart-full">
                  <h3 className="chart-title">Program Duration by State — Detail</h3>
                  <div className="duration-table-wrapper">
                    <table className="duration-table">
                      <thead>
                        <tr>
                          <th>State</th>
                          <th>Completed</th>
                          <th>Avg Days</th>
                          <th>Median Days</th>
                          <th>Min Days</th>
                          <th>Max Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedState === 'All States'
                          ? filteredSummary
                          : filteredSummary.filter(s => {
                              const abbrevMap = {
                                'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
                                'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
                                'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
                                'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                                'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
                                'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
                                'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
                                'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                                'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
                                'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
                                'District of Columbia': 'DC'
                              };
                              return s.state === abbrevMap[selectedState] || s.state === selectedState;
                            })
                        ).map(row => (
                          <tr key={row.state}>
                            <td className="duration-state-cell">{row.state}</td>
                            <td>{row.count}</td>
                            <td>{row.avgDays} days</td>
                            <td>{row.medianDays} days</td>
                            <td>{row.minDays} days</td>
                            <td>{row.maxDays} days</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="chart-card chart-full">
                  <h3 className="chart-title">Completed Students by State</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={[...filteredSummary].sort((a, b) => b.count - a.count)}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="state" stroke="#E0E7FF" angle={-45} textAnchor="end" height={60} />
                      <YAxis stroke="#E0E7FF" label={{ value: 'Students', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                        labelStyle={{ color: '#E0E7FF' }}
                      />
                      <Bar dataKey="count" name="Completed" fill="#00BEA8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
              );
            })()}

            {/* Field Experience: Enrolled → Program Completion */}
            {durationData?.fieldExperience && durationData.fieldExperience.totalStudents > 0 && (() => {
              const fe = durationData.fieldExperience;
              return (
              <>
                <div style={{ marginTop: '2rem', borderTop: '2px solid rgba(255,255,255,0.08)', paddingTop: '1.5rem' }}>
                  <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.5rem' }}>
                    Enrolled Date → Field Experience Completion
                  </h3>
                  <p style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.8rem', margin: '0 0 1rem' }}>
                    Average time from Contact Enrolled Date to Field Experience Standard Date (program completion) — {fe.totalStudents.toLocaleString()} completed students
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
                    <div className="kpi-card" style={{ borderLeft: '3px solid #818CF8' }}>
                      <div className="kpi-value" style={{ color: '#818CF8' }}>{fe.totalStudents.toLocaleString()}</div>
                      <div className="kpi-label">Completed Students</div>
                    </div>
                    <div className="kpi-card" style={{ borderLeft: '3px solid #00BEA8' }}>
                      <div className="kpi-value" style={{ color: '#00BEA8' }}>{fe.overallAvgDays} days</div>
                      <div className="kpi-label">Average Duration</div>
                      <div className="kpi-sub">{(fe.overallAvgDays / 30.44).toFixed(1)} months</div>
                    </div>
                    <div className="kpi-card" style={{ borderLeft: '3px solid #4ADE80' }}>
                      <div className="kpi-value" style={{ color: '#4ADE80' }}>{fe.overallMedianDays} days</div>
                      <div className="kpi-label">Median Duration</div>
                      <div className="kpi-sub">{(fe.overallMedianDays / 30.44).toFixed(1)} months</div>
                    </div>
                    <div className="kpi-card" style={{ borderLeft: '3px solid #FBBF24' }}>
                      <div className="kpi-value" style={{ color: '#FBBF24' }}>{fe.summary.length}</div>
                      <div className="kpi-label">States</div>
                    </div>
                  </div>

                  <div className="duration-table-wrapper">
                    <table className="duration-table">
                      <thead>
                        <tr>
                          <th>State</th>
                          <th>Completed</th>
                          <th>Avg (days)</th>
                          <th>Avg (months)</th>
                          <th>Median</th>
                          <th>Min</th>
                          <th>Max</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fe.summary.map(row => (
                          <tr key={row.state}>
                            <td style={{ fontWeight: 600 }}>{row.state}</td>
                            <td>{row.count}</td>
                            <td>{row.avgDays}</td>
                            <td>{(row.avgDays / 30.44).toFixed(1)}</td>
                            <td>{row.medianDays}</td>
                            <td>{row.minDays}</td>
                            <td>{row.maxDays}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="duration-chart-container" style={{ marginTop: '1.25rem' }}>
                    <h4 className="duration-chart-title">Average Duration by State — Enrolled to FE Completion</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={[...fe.summary].sort((a, b) => a.avgDays - b.avgDays)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="state" stroke="#E0E7FF" />
                        <YAxis stroke="#E0E7FF" label={{ value: 'Days', angle: -90, position: 'insideLeft', fill: '#E0E7FF' }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                          labelStyle={{ color: '#E0E7FF' }}
                          formatter={(value, name) => [`${value} days (${(value / 30.44).toFixed(1)} mo)`, name]}
                        />
                        <Legend />
                        <Bar dataKey="avgDays" name="Average" fill="#818CF8" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="medianDays" name="Median" fill="#4ADE80" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
              );
            })()}
          </div>
        )}

        {/* Monthly Email — andrew only */}
        {/* Partnerships */}
        {activeTab === 'partnerships' && dashboardAccess.includes('partnerships') && (
          <Partnerships />
        )}

        {activeTab === 'email' && dashboardAccess.includes('email') && (
          <EmailGenerator />
        )}

        {/* SF Query — andrew only */}
        {activeTab === 'sfquery' && dashboardAccess.includes('sfquery') && (
          <SFQuery />
        )}

        {/* Test Entry Audit — did the score reports get read correctly? */}
        {activeTab === 'testaudit' && dashboardAccess.includes('testaudit') && (
          <TestEntryAudit />
        )}
      </div>
    </div>
  );
};

export default EnrollmentDashboard;
